import { createElement, closest, detach, Browser, isNullOrUndefined as isNOU, EventHandler, addClass, removeClass } from '@syncfusion/ej2-base';
import * as CONSTANT from './../base/constant';
import { InsertHtml } from './inserthtml';
import { removeClassWithAttr, getCorrespondingColumns, getCorrespondingIndex, getColGroup, insertColGroupWithSizes, convertPixelToPercentage, getCellIndex, getMaxCellCount, cleanupInternalElements } from '../../common/util';
import * as EVENTS from '../../common/constant';
import { CLS_TABLE_MULTI_CELL, CLS_TABLE_SEL, CLS_TABLE_SEL_END } from '../../common/constant';
import { TABLE_SELECTION_STATE_ALLOWED_ACTIONKEYS } from '../../common/config';
import { TablePasting } from './table-pasting';
/**
 * Link internal component
 *
 * @hidden
 * @private
 */
var TableCommand = /** @class */ (function () {
    /**
     * Constructor for creating the Formats plugin
     *
     * @param {IEditorModel} parent - specifies the parent element
     * @param {ITableModel} tableModel - specifies the table model instance
     * @param {IFrameSettingsModel} iframeSettings - specifies the table model instance
     * @hidden
     * @private
     */
    function TableCommand(parent, tableModel, iframeSettings) {
        var _this = this;
        this.isTableMoveActive = false;
        this.pageX = null;
        this.pageY = null;
        this.isResizeBind = true;
        this.currentColumnResize = '';
        this.resizeEndTime = 0;
        this.ensureInsideTableList = true;
        // Store dynamic args as class properties
        this.currentRowTarget = null;
        this.currentRowIndex = -1;
        this.currentColIndex = -1;
        this.currentColTable = null;
        this.currentEntireTable = null;
        this.parent = parent;
        this.tablePastingObj = new TablePasting();
        this.tableModel = tableModel;
        this.iframeSettings = iframeSettings;
        this.selectTableRowBoundFn = this.selectTableRow.bind(this);
        this.selectTableColBoundFn = this.selectTableColumn.bind(this);
        this.selectEntireTableBoundFn = this.selectEntireTable.bind(this);
        this.rowIconClickHandler = function () { return _this.selectTableRowBoundFn(_this.currentRowTarget); };
        this.colIconClickHandler = function () { return _this.selectTableColBoundFn(_this.currentColIndex, _this.currentColTable); };
        this.tableIconClickHandler = function () { return _this.selectEntireTableBoundFn(_this.currentEntireTable); };
        this.addEventListener();
    }
    /*
     * Registers all event listeners for table operations
     */
    TableCommand.prototype.addEventListener = function () {
        this.parent.observer.on(CONSTANT.TABLE, this.createTable, this);
        this.parent.observer.on(CONSTANT.INSERT_ROW, this.insertRow, this);
        this.parent.observer.on(CONSTANT.INSERT_COLUMN, this.insertColumn, this);
        this.parent.observer.on(CONSTANT.DELETEROW, this.deleteRow, this);
        this.parent.observer.on(CONSTANT.DELETECOLUMN, this.deleteColumn, this);
        this.parent.observer.on(CONSTANT.REMOVETABLE, this.removeTable, this);
        this.parent.observer.on(CONSTANT.TABLEHEADER, this.tableHeader, this);
        this.parent.observer.on(CONSTANT.TABLE_VERTICAL_ALIGN, this.tableVerticalAlign, this);
        this.parent.observer.on(CONSTANT.TABLE_MERGE, this.cellMerge, this);
        this.parent.observer.on(CONSTANT.TABLE_HORIZONTAL_SPLIT, this.horizontalSplit, this);
        this.parent.observer.on(CONSTANT.TABLE_VERTICAL_SPLIT, this.verticalSplit, this);
        this.parent.observer.on(CONSTANT.TABLE_STYLES, this.tableStyles, this);
        this.parent.observer.on(CONSTANT.TABLE_BACKGROUND_COLOR, this.setBGColor, this);
        this.parent.observer.on(CONSTANT.TABLE_MOVE, this.tableMove, this);
        this.parent.observer.on(EVENTS.INTERNAL_DESTROY, this.destroy, this);
    };
    /*
     * Removes all registered event listeners
     */
    TableCommand.prototype.removeEventListener = function () {
        this.parent.observer.off(CONSTANT.TABLE, this.createTable);
        this.parent.observer.off(CONSTANT.INSERT_ROW, this.insertRow);
        this.parent.observer.off(CONSTANT.INSERT_COLUMN, this.insertColumn);
        this.parent.observer.off(CONSTANT.DELETEROW, this.deleteRow);
        this.parent.observer.off(CONSTANT.DELETECOLUMN, this.deleteColumn);
        this.parent.observer.off(CONSTANT.REMOVETABLE, this.removeTable);
        this.parent.observer.off(CONSTANT.TABLEHEADER, this.tableHeader);
        this.parent.observer.off(CONSTANT.TABLE_VERTICAL_ALIGN, this.tableVerticalAlign);
        this.parent.observer.off(CONSTANT.TABLE_MERGE, this.cellMerge);
        this.parent.observer.off(CONSTANT.TABLE_HORIZONTAL_SPLIT, this.horizontalSplit);
        this.parent.observer.off(CONSTANT.TABLE_VERTICAL_SPLIT, this.verticalSplit);
        this.parent.observer.off(CONSTANT.TABLE_STYLES, this.tableStyles);
        this.parent.observer.off(CONSTANT.TABLE_BACKGROUND_COLOR, this.setBGColor);
        this.parent.observer.off(CONSTANT.TABLE_MOVE, this.tableMove);
        this.parent.observer.off(EVENTS.INTERNAL_DESTROY, this.destroy);
        // Browser-specific event handlers for table resizing
        if (!Browser.isDevice && this.tableModel.tableSettings.resize) {
            EventHandler.remove(this.tableModel.getEditPanel(), 'mouseover', this.resizeHelper);
            this.parent.observer.off(EVENTS.touchStart, this.resizeStart);
        }
        if (this.curTable) {
            EventHandler.remove(this.curTable, 'mouseleave', this.tableMouseLeave);
        }
        EventHandler.remove(this.tableModel.getDocument(), 'selectionchange', this.tableCellsKeyboardSelection);
    };
    /**
     * Copies the selected table cells to clipboard.
     * Creates a temporary table with only the selected cells' content.
     *
     * @param {boolean} isCut - Indicates whether the operation is a cut (true) or copy (false).
     * @returns {void} Nothing is returned
     * @public
     * @hidden
     */
    TableCommand.prototype.copy = function (isCut) {
        var copyTable = this.extractSelectedTable(this.curTable, isCut);
        if (copyTable) {
            var tableHtml = cleanupInternalElements(copyTable.outerHTML, this.tableModel.editorMode);
            return tableHtml;
        }
        return null;
    };
    /**
     * Updates the table command object with the latest table model configuration and settings
     *
     * @param {ITableModel} updatedTableMode - The updated table model with latest configuration
     * @returns {void} - This method does not return a value
     * @public
     * @hidden
     */
    TableCommand.prototype.updateTableModel = function (updatedTableMode) {
        this.tableModel = updatedTableMode;
    };
    /*
     * Extracts a cloned HTMLTableElement containing only the selected cells,
     * preserving their original row and column positions. All non-selected
     * rows and cells are removed. If `isCut` is true, the original cell content
     * is cleared by replacing it with a <br>.
     *
     * @hidden
     */
    TableCommand.prototype.extractSelectedTable = function (originalTable, isCut) {
        var selectedCells = originalTable.querySelectorAll('.e-cell-select.e-multi-cells-select');
        if (!selectedCells || selectedCells.length === 0) {
            return null;
        }
        var clonedTable = originalTable.cloneNode(true);
        var rowsWithSelection = this.buildSelectionMap(originalTable, selectedCells, isCut);
        this.cleanTableToSelection(clonedTable, rowsWithSelection);
        return clonedTable;
    };
    /* Builds a map of selected cell coordinates and clears original cell content if cut */
    TableCommand.prototype.buildSelectionMap = function (originalTable, selectedCells, isCut) {
        var selectionMap = new Map();
        for (var i = 0; i < selectedCells.length; i++) {
            var cell = selectedCells[i];
            var row = cell.parentElement;
            var rowIndex = Array.prototype.indexOf.call(originalTable.rows, row);
            var cellIndex = Array.prototype.indexOf.call(row.cells, cell);
            var rowSpan = parseInt(cell.getAttribute('rowspan') || '1', 10);
            for (var r = 0; r < rowSpan; r++) {
                var rowPosition = r + rowIndex;
                if (!selectionMap.has(rowPosition)) {
                    selectionMap.set(rowPosition, new Set());
                }
                if (r === 0) {
                    selectionMap.get(rowPosition).add(cellIndex);
                }
            }
            if (isCut) {
                var originalCell = originalTable.rows[rowIndex].cells[cellIndex];
                originalCell.innerHTML = '<br>';
            }
        }
        return selectionMap;
    };
    /* Modifies the cloned table by removing non-selected rows and cells */
    TableCommand.prototype.cleanTableToSelection = function (table, selectionMap) {
        for (var rowIndex = table.rows.length - 1; rowIndex >= 0; rowIndex--) {
            var row = table.rows[rowIndex];
            if (!selectionMap.has(rowIndex)) {
                detach(row);
                continue;
            }
            var selectedCellIndices = selectionMap.get(rowIndex);
            for (var cellIndex = row.cells.length - 1; cellIndex >= 0; cellIndex--) {
                if (!selectedCellIndices.has(cellIndex)) {
                    row.deleteCell(cellIndex);
                }
            }
        }
    };
    /* Removes the <colgroup> from the cloned table if it exists */
    TableCommand.prototype.removeColGroup = function (table) {
        var colGroup = getColGroup(table);
        if (colGroup) {
            detach(colGroup);
        }
    };
    /*
     * Creates and inserts a table based on the specified configuration.
     */
    TableCommand.prototype.createTable = function (e) {
        var table = this.createTableStructure(e);
        this.insertTableInDocument(table, e);
        this.handlePostTableInsertion(table, e);
        return table;
    };
    /*
     * Creates the table structure with rows and columns.
     */
    TableCommand.prototype.createTableStructure = function (e) {
        var table = createElement('table', { className: 'e-rte-table' });
        this.applyTableDimensions(table, e.item.width);
        var cellWidth = this.calculateCellWidth(e.item.width.width, e.item.columns);
        // Create colgroup with columns
        var colGroup = this.createInitialColgroup(e.item.columns, cellWidth);
        table.appendChild(colGroup);
        var tblBody = createElement('tbody');
        this.createRowsAndCells(tblBody, e.item.rows, e.item.columns);
        table.appendChild(tblBody);
        return table;
    };
    /*
     * Creates a colgroup element with evenly distributed columns
     */
    TableCommand.prototype.createInitialColgroup = function (columnCount, cellWidth) {
        var colGroup = createElement('colgroup');
        for (var i = 0; i < columnCount; i++) {
            var col = createElement('col');
            col.appendChild(createElement('br'));
            col.style.width = cellWidth + '%';
            colGroup.appendChild(col);
        }
        return colGroup;
    };
    /*
     * Applies width dimensions to the table.
     */
    TableCommand.prototype.applyTableDimensions = function (table, widthConfig) {
        if (!isNOU(widthConfig.width)) {
            table.style.width = this.calculateStyleValue(widthConfig.width);
        }
        if (!isNOU(widthConfig.minWidth)) {
            table.style.minWidth = this.calculateStyleValue(widthConfig.minWidth);
        }
        if (!isNOU(widthConfig.maxWidth)) {
            table.style.maxWidth = this.calculateStyleValue(widthConfig.maxWidth);
        }
    };
    /*
     * Calculates appropriate cell width based on table width and column count.
     */
    TableCommand.prototype.calculateCellWidth = function (width, columns) {
        return parseInt(width, 10) > 100 ?
            100 / columns : parseInt(width, 10) / columns;
    };
    /*
     * Creates rows and cells in the table body.
     */
    TableCommand.prototype.createRowsAndCells = function (tblBody, rowCount, columnCount) {
        for (var i = 0; i < rowCount; i++) {
            var row = createElement('tr');
            for (var j = 0; j < columnCount; j++) {
                var cell = createElement('td');
                cell.appendChild(createElement('br'));
                row.appendChild(cell);
            }
            tblBody.appendChild(row);
        }
    };
    /*
     * Inserts the table into the document.
     */
    TableCommand.prototype.insertTableInDocument = function (table, e) {
        e.item.selection.restore();
        InsertHtml.Insert(this.tableModel.getDocument(), table, this.tableModel.getEditPanel());
        e.item.selection.setSelectionText(this.tableModel.getDocument(), table.querySelector('td'), table.querySelector('td'), 0, 0);
    };
    /*
     * Handles post-insertion operations for the table.
     */
    TableCommand.prototype.handlePostTableInsertion = function (table, e) {
        this.insertElementAfterTableIfNeeded(table, e.enterAction);
        if (table.classList.contains('ignore-table')) {
            removeClassWithAttr([table], ['ignore-table']);
        }
        var offsetParent = this.getOffsetParent(table, this.tableModel.getDocument());
        if (offsetParent) {
            var isNestedTable = offsetParent && (offsetParent.nodeName === 'TD' || offsetParent.nodeName === 'TH');
            var isMultiCell = (offsetParent.classList && offsetParent.classList.contains('e-multi-cells-select')) ? true : false;
            if (isNestedTable && !isMultiCell) {
                removeClassWithAttr([offsetParent], ['e-cell-select']);
            }
        }
        table.querySelector('td').classList.add('e-cell-select');
        if (e.callBack) {
            e.callBack({
                requestType: 'Table',
                editorMode: 'HTML',
                event: e.event,
                range: this.parent.nodeSelection.getRange(this.tableModel.getDocument()),
                elements: [table]
            });
        }
    };
    /*
     * Inserts an appropriate element after the table if needed.
     */
    TableCommand.prototype.insertElementAfterTableIfNeeded = function (table, enterAction) {
        if (table.nextElementSibling === null && !table.classList.contains('ignore-table')) {
            var insertElem = void 0;
            if (enterAction === 'DIV') {
                insertElem = createElement('div');
                insertElem.appendChild(createElement('br'));
            }
            else if (enterAction === 'BR') {
                insertElem = createElement('br');
            }
            else {
                insertElem = createElement('p');
                insertElem.appendChild(createElement('br'));
            }
            this.insertAfter(insertElem, table);
        }
    };
    /*
     * Calculates CSS style value by appending appropriate units.
     * If the value is a string with a unit (px, %, auto), it returns the original value.
     * Otherwise, it appends 'px' to the value.
     */
    TableCommand.prototype.calculateStyleValue = function (value) {
        var styleValue;
        if (typeof value === 'string') {
            if (value.indexOf('px') >= 0 || value.indexOf('%') >= 0 || value.indexOf('auto') >= 0) {
                styleValue = value;
            }
            else {
                styleValue = value + 'px';
            }
        }
        else {
            styleValue = value + 'px';
        }
        return styleValue;
    };
    /*
     * Inserts a node after the specified reference node.
     * Acts as a helper method since there's no direct insertAfter method in DOM.
     */
    TableCommand.prototype.insertAfter = function (newNode, referenceNode) {
        if (!referenceNode.parentNode) {
            return;
        }
        referenceNode.parentNode.insertBefore(newNode, referenceNode.nextSibling);
    };
    /*
     * Determines the minimum and maximum row/column indexes of selected cells.
     * This method calculates the bounding box that encloses all selected cells in the table.
     */
    TableCommand.prototype.getSelectedCellMinMaxIndex = function (cellsMatrix) {
        var selectedCells = this.curTable.querySelectorAll('.e-cell-select');
        var minRowIndex = cellsMatrix.length;
        var maxRowIndex = 0;
        var minColIndex = cellsMatrix[0].length;
        var maxColIndex = 0;
        for (var i = 0; i < selectedCells.length; i++) {
            var selectedCellPosition = getCorrespondingIndex(selectedCells[i], cellsMatrix);
            var cellEndPosition = this.FindIndex(selectedCellPosition[0], selectedCellPosition[1], cellsMatrix);
            minRowIndex = Math.min(selectedCellPosition[0], minRowIndex);
            maxRowIndex = Math.max(cellEndPosition[0], maxRowIndex);
            minColIndex = Math.min(selectedCellPosition[1], minColIndex);
            maxColIndex = Math.max(cellEndPosition[1], maxColIndex);
        }
        return {
            startRow: minRowIndex,
            endRow: maxRowIndex,
            startColumn: minColIndex,
            endColumn: maxColIndex
        };
    };
    /*
     * Inserts a new row before or after the selected row in a table.
     */
    TableCommand.prototype.insertRow = function (e) {
        var isBelow = e.item.subCommand === 'InsertRowBefore' ? false : true;
        this.curTable = closest(this.parent.nodeSelection.range.startContainer.parentElement, 'table');
        var focusedCell = null;
        if (this.curTable.querySelectorAll('.e-cell-select').length === 0) {
            focusedCell = this.addRowWithoutCellSelection();
        }
        else {
            focusedCell = this.addRowWithCellSelection(e, isBelow);
        }
        this.updateSelectionAfterRowInsertion(focusedCell, e);
        this.executeCallback(e);
    };
    /*
     * Adds a new row when no cell is specifically selected.
     * Clones the last row and appends it to the table.
     */
    TableCommand.prototype.addRowWithoutCellSelection = function () {
        var lastRow = this.curTable.rows[this.curTable.rows.length - 1];
        var cloneRow = lastRow.cloneNode(true);
        cloneRow.removeAttribute('rowspan');
        this.insertAfter(cloneRow, lastRow);
        var cells = cloneRow.cells;
        return cells && cells.length > 0 ? cells[0] : null;
    };
    /*
     * Adds a new row when a cell is selected, handling rowspan adjustments.
     */
    TableCommand.prototype.addRowWithCellSelection = function (e, isBelow) {
        var allCells = getCorrespondingColumns(this.curTable);
        var minMaxIndex = this.getSelectedCellMinMaxIndex(allCells);
        var minVal = isBelow ? minMaxIndex.endRow : minMaxIndex.startRow;
        var newRow = createElement('tr');
        var isHeaderSelect = this.curTable.querySelectorAll('th.e-cell-select').length > 0;
        this.createCellsForNewRow(allCells, minVal, isBelow, isHeaderSelect, newRow);
        this.insertNewRowAtPosition(e, isBelow, isHeaderSelect, minVal, newRow);
        var cells = newRow.cells;
        return cells && cells.length > 0 && minMaxIndex.endColumn < cells.length ? cells[minMaxIndex.endColumn] : null;
    };
    /*
     * Creates cells for the new row, handling rowspan adjustments and styles.
     */
    TableCommand.prototype.createCellsForNewRow = function (allCells, minVal, isBelow, isHeaderSelect, newRow) {
        for (var i = 0; i < allCells[minVal].length; i++) {
            if (this.isCellAffectedByRowspan(allCells, minVal, i, isBelow)) {
                if (this.isFirstCellInSpan(allCells, minVal, i)) {
                    this.incrementRowspan(allCells[minVal][i]);
                }
            }
            else {
                this.createNewCellForRow(allCells, minVal, i, isHeaderSelect, isBelow, newRow);
            }
        }
    };
    /*
     * Checks if a cell position is affected by a rowspan.
     */
    TableCommand.prototype.isCellAffectedByRowspan = function (allCells, rowIndex, colIndex, isBelow) {
        return (isBelow &&
            rowIndex < allCells.length - 1 &&
            allCells[rowIndex][colIndex] === allCells[rowIndex + 1][colIndex]) ||
            (!isBelow &&
                0 < rowIndex &&
                allCells[rowIndex][colIndex] === allCells[rowIndex - 1][colIndex]);
    };
    /*
     * Checks if this cell is the first cell in a rowspan/colspan.]
     */
    TableCommand.prototype.isFirstCellInSpan = function (allCells, rowIndex, colIndex) {
        return 0 === colIndex ||
            (0 < colIndex && allCells[rowIndex][colIndex] !== allCells[rowIndex][colIndex - 1]);
    };
    /*
     * Increments the rowspan attribute of a cell.
     */
    TableCommand.prototype.incrementRowspan = function (cell) {
        var currentRowspan = parseInt(cell.getAttribute('rowspan'), 10) || 1;
        cell.setAttribute('rowspan', (currentRowspan + 1).toString());
    };
    /*
     * Creates a new cell for the new row.
     */
    TableCommand.prototype.createNewCellForRow = function (allCells, rowIndex, colIndex, isHeaderSelect, isBelow, newRow) {
        var tdElement = createElement('td');
        tdElement.appendChild(createElement('br'));
        newRow.appendChild(tdElement);
        var referenceRowIndex = this.getReferenceRowIndex(allCells, rowIndex, isHeaderSelect, isBelow);
        var styleValue = allCells[referenceRowIndex][colIndex].getAttribute('style');
        if (styleValue) {
            var updatedStyle = this.cellStyleCleanup(styleValue);
            tdElement.style.cssText = updatedStyle;
        }
    };
    /*
     * Gets the appropriate reference row index for styling.
     */
    TableCommand.prototype.getReferenceRowIndex = function (allCells, rowIndex, isHeaderSelect, isBelow) {
        if (isHeaderSelect && isBelow) {
            // If header is selected and inserting below, use first body row if available
            return (rowIndex + 1 < allCells.length) ? (rowIndex + 1) : rowIndex;
        }
        return rowIndex;
    };
    /*
     * Inserts the new row at the appropriate position in the table.
     */
    TableCommand.prototype.insertNewRowAtPosition = function (e, isBelow, isHeaderSelect, rowIndex, newRow) {
        var selectedRow;
        if (isHeaderSelect && isBelow) {
            selectedRow = this.curTable.querySelector('tbody').childNodes[0];
        }
        else {
            selectedRow = this.curTable.rows[rowIndex];
        }
        if (e.item.subCommand === 'InsertRowBefore') {
            selectedRow.parentElement.insertBefore(newRow, selectedRow);
        }
        else if (isHeaderSelect) {
            selectedRow.parentElement.insertBefore(newRow, selectedRow);
        }
        else {
            this.insertAfter(newRow, selectedRow);
        }
    };
    /*
     * Updates the selection after row insertion.
     */
    TableCommand.prototype.updateSelectionAfterRowInsertion = function (cell, e) {
        if (!cell) {
            return;
        }
        this.clearTableSelections();
        addClass([cell], CLS_TABLE_SEL);
        e.item.selection.setSelectionText(this.tableModel.getDocument(), cell, cell, 0, 0);
    };
    /*
     * Clears selection state on table cells.
     */
    TableCommand.prototype.clearTableSelections = function () {
        var selectedElements = this.tableModel.getEditPanel().querySelectorAll('.e-cell-select');
        removeClassWithAttr(selectedElements, CLS_TABLE_SEL);
        this.removeTableSelection();
    };
    /*
     * Executes the callback function if provided.
     */
    TableCommand.prototype.executeCallback = function (e) {
        if (e.callBack) {
            e.callBack({
                requestType: e.item.subCommand,
                editorMode: 'HTML',
                event: e.event,
                range: this.parent.nodeSelection.getRange(this.tableModel.getDocument()),
                elements: this.parent.nodeSelection.getSelectedNodes(this.tableModel.getDocument())
            });
        }
    };
    /*
     * Inserts a new column before or after the selected column in a table.
     */
    TableCommand.prototype.insertColumn = function (e) {
        // Locate the selected cell
        var selectedCell = e.item.selection.range.startContainer;
        if (!(selectedCell.nodeName === 'TH' || selectedCell.nodeName === 'TD')) {
            selectedCell = closest(selectedCell.parentElement, 'td,th');
        }
        var curRow = closest(selectedCell, 'tr');
        var allRows = closest(curRow, 'table').rows;
        var colIndex = Array.prototype.slice.call(curRow.querySelectorAll(':scope > td, :scope > th')).indexOf(selectedCell);
        var currentTabElm = closest(curRow, 'table');
        this.prepareTableForColumnInsertion(e, currentTabElm);
        this.insertCellsInAllRows(e, allRows, colIndex, currentTabElm);
        var currentCellIndex = e.item.subCommand === 'InsertColumnRight' ? colIndex + 1 : colIndex;
        var focusedCell = (curRow.cells[currentCellIndex]);
        this.clearTableSelections();
        addClass([focusedCell], CLS_TABLE_SEL);
        this.finalizeColumnInsertion(e, focusedCell);
    };
    /*
     * Prepares the table for column insertion by calculating and storing widths.
     */
    TableCommand.prototype.prepareTableForColumnInsertion = function (e, currentTabElm) {
        var thTdElm = currentTabElm.querySelectorAll('th,td');
        for (var i = 0; i < thTdElm.length; i++) {
            thTdElm[i].dataset.oldWidth =
                (thTdElm[i].offsetWidth / currentTabElm.offsetWidth * 100) + '%';
        }
        if (isNOU(currentTabElm.style.width) || currentTabElm.style.width === '') {
            currentTabElm.style.width = currentTabElm.offsetWidth + 'px';
        }
    };
    /*
     * Inserts new cells in all rows at the specified column index.
     */
    TableCommand.prototype.insertCellsInAllRows = function (e, allRows, colIndex, currentTabElm) {
        // Get current table to calculate proper column width
        var thTdElm = currentTabElm.querySelectorAll('th,td');
        var currentCellCount = allRows[0].querySelectorAll(':scope > td, :scope > th').length;
        var currentWidth = parseInt(e.item.width, 10) / (currentCellCount + 1);
        var previousWidth = parseInt(e.item.width, 10) / currentCellCount;
        // update column group
        var cols = this.updateColumnGroup(currentTabElm, colIndex, e.item.subCommand, currentWidth);
        //update the column
        for (var i = 0; i < allRows.length; i++) {
            var curCell = allRows[i].querySelectorAll(':scope > td, :scope > th')[colIndex];
            var colTemplate = this.createColumnCell(curCell);
            if (e.item.subCommand === 'InsertColumnLeft') {
                curCell.parentElement.insertBefore(colTemplate, curCell);
            }
            else {
                this.insertAfter(colTemplate, curCell);
            }
            delete colTemplate.dataset.oldWidth;
        }
        this.redistributeCellWidths(thTdElm, previousWidth, currentWidth, cols);
    };
    /*
     * Updates colgroup structure during column insertion
     */
    TableCommand.prototype.updateColumnGroup = function (currentTabElm, colIndex, subCommand, currentWidth) {
        insertColGroupWithSizes(currentTabElm);
        var colGroup = getColGroup(currentTabElm);
        var newCol = createElement('col');
        newCol.appendChild(createElement('br'));
        newCol.style.width = currentWidth.toFixed(4) + '%';
        var cols = colGroup.querySelectorAll('col');
        if (cols.length > 0 && colIndex < cols.length) {
            var curCol = cols[colIndex];
            if (subCommand === 'InsertColumnLeft') {
                colGroup.insertBefore(newCol, curCol);
            }
            else {
                this.insertAfter(newCol, curCol);
            }
        }
        else {
            colGroup.appendChild(newCol);
        }
        return colGroup.querySelectorAll('col');
    };
    /*
     * Creates a new cell for column insertion with proper attributes.
     */
    TableCommand.prototype.createColumnCell = function (referenceCell) {
        var colTemplate = referenceCell.cloneNode(true);
        var style = colTemplate.getAttribute('style');
        if (style) {
            var updatedStyle = this.cellStyleCleanup(style);
            colTemplate.style.cssText = updatedStyle;
        }
        colTemplate.innerHTML = '';
        colTemplate.appendChild(createElement('br'));
        colTemplate.removeAttribute('class');
        colTemplate.removeAttribute('colspan');
        colTemplate.removeAttribute('rowspan');
        return colTemplate;
    };
    /*
     * Redistributes cell widths after column insertion.
     */
    TableCommand.prototype.redistributeCellWidths = function (cells, previousWidth, currentWidth, cols) {
        for (var i = 0; i < cells.length; i++) {
            if (cells[i].dataset.oldWidth) {
                var oldWidthValue = Number(cells[i].dataset.oldWidth.split('%')[0]);
                var colIndex = Array.prototype.slice.call(cells[i].
                    parentElement.querySelectorAll(':scope > td, :scope > th')).indexOf(cells[i]);
                cols[colIndex].style.width = (oldWidthValue * currentWidth / previousWidth).toFixed(4) + '%';
                delete cells[i].dataset.oldWidth;
            }
        }
    };
    /*
     * Finalizes column insertion by updating selection and executing callbacks.
     */
    TableCommand.prototype.finalizeColumnInsertion = function (e, selectedCell) {
        e.item.selection.setSelectionText(this.tableModel.getDocument(), selectedCell, selectedCell, 0, 0);
        this.executeCallback(e);
    };
    /*
     * Sets the background color for selected table cells.
     */
    TableCommand.prototype.setBGColor = function (args) {
        var range = this.parent.nodeSelection.getRange(this.tableModel.getDocument());
        var start = range.startContainer.nodeType === 3 ?
            range.startContainer.parentNode : range.startContainer;
        this.curTable = start.closest('table');
        var selectedCells = this.curTable.querySelectorAll('.e-cell-select');
        for (var i = 0; i < selectedCells.length; i++) {
            selectedCells[i].style.backgroundColor = args.value.toString();
        }
        this.parent.undoRedoManager.saveData();
        this.parent.observer.notify(EVENTS.hideTableQuickToolbar, {});
        this.executeBgColorCallback(args);
    };
    /*
     * Executes callback after setting background color.
     */
    TableCommand.prototype.executeBgColorCallback = function (args) {
        if (args.callBack) {
            args.callBack({
                requestType: args.subCommand,
                editorMode: 'HTML',
                event: args.event,
                range: this.parent.nodeSelection.getRange(this.tableModel.getDocument()),
                elements: this.parent.nodeSelection.getSelectedNodes(this.tableModel.getDocument())
            });
        }
    };
    /**
     * Applies table styles.
     * This method handles various table styling operations like adding dashed borders,
     * alternating borders, or custom CSS classes.
     *
     * @param {IHtmlItem} e - The click event arguments
     * @returns {void}
     * @private
     */
    TableCommand.prototype.tableStyles = function (e) {
        var args = e.event;
        var command = e.item.subCommand;
        var table = closest(args.selectParent[0], 'table');
        this.applyTableStyleCommand(command, table);
        this.applyCustomCssClasses(args, table);
        this.parent.undoRedoManager.saveData();
        this.parent.observer.notify(EVENTS.hideTableQuickToolbar, {});
        this.parent.nodeSelection.restore();
        if (e.callBack) {
            e.callBack({
                requestType: e.item.subCommand,
                editorMode: 'HTML',
                event: args.args,
                range: this.parent.nodeSelection.getRange(this.parent.currentDocument),
                elements: this.parent.nodeSelection.getSelectedNodes(this.parent.currentDocument)
            });
        }
    };
    /**
     * Applies a specific table style command.
     * This helper method handles the actual application of built-in table styles
     * such as dashed or alternating borders.
     *
     * @param {string} command - The style command to apply
     * @param {HTMLTableElement} table - The table element to style
     * @returns {void}
     * @private
     */
    TableCommand.prototype.applyTableStyleCommand = function (command, table) {
        if (command === 'Dashed') {
            var hasParentClass = this.parent.editableElement.classList.contains(EVENTS.CLS_TB_DASH_BOR);
            if (hasParentClass) {
                removeClassWithAttr([this.parent.editableElement], EVENTS.CLS_TB_DASH_BOR);
            }
            else {
                this.parent.editableElement.classList.add(EVENTS.CLS_TB_DASH_BOR);
            }
            var hasTableClass = table.classList.contains(EVENTS.CLS_TB_DASH_BOR);
            if (hasTableClass) {
                removeClassWithAttr([table], EVENTS.CLS_TB_DASH_BOR);
            }
            else {
                table.classList.add(EVENTS.CLS_TB_DASH_BOR);
            }
        }
        else if (command === 'Alternate') {
            var hasParentClass = this.parent.editableElement.classList.contains(EVENTS.CLS_TB_ALT_BOR);
            if (hasParentClass) {
                removeClassWithAttr([this.parent.editableElement], EVENTS.CLS_TB_ALT_BOR);
            }
            else {
                this.parent.editableElement.classList.add(EVENTS.CLS_TB_ALT_BOR);
            }
            var hasTableClass = table.classList.contains(EVENTS.CLS_TB_ALT_BOR);
            if (hasTableClass) {
                removeClassWithAttr([table], EVENTS.CLS_TB_ALT_BOR);
            }
            else {
                table.classList.add(EVENTS.CLS_TB_ALT_BOR);
            }
        }
    };
    /**
     * Applies custom CSS classes to a table.
     * This helper method processes any custom CSS classes specified in the
     * command arguments and toggles them on the table.
     *
     * @param {ITableNotifyArgs} args - The table notification arguments
     * @param {HTMLTableElement} table - The table element to style
     * @returns {void}
     * @private
     */
    TableCommand.prototype.applyCustomCssClasses = function (args, table) {
        var clickArgs = args.args;
        if (clickArgs && clickArgs.item.cssClass) {
            var classList = clickArgs.item.cssClass.split(' ');
            for (var i = 0; i < classList.length; i++) {
                var className = classList[i];
                if (table.classList.contains(className)) {
                    removeClassWithAttr([table], className);
                }
                else {
                    table.classList.add(className);
                }
            }
        }
    };
    /*
     * Deletes a column from the table.
     */
    TableCommand.prototype.deleteColumn = function (e) {
        var selectedCell = e.item.selection.range.startContainer;
        if (selectedCell.nodeType === 3) {
            selectedCell = closest(selectedCell.parentElement, 'td,th');
        }
        this.curTable = closest(selectedCell, 'table');
        // If only one column remains, remove the entire table
        var curRow = closest(selectedCell, 'tr');
        if (curRow.querySelectorAll('th,td').length === 1) {
            this.removeEntireTable(e);
        }
        else {
            insertColGroupWithSizes(this.curTable);
            var selectedMinMaxIndex = this.removeSelectedColumns(e);
            // Update colgroup structure after deletion
            this.updateColgroupAfterColumnDeletion(this.curTable, selectedMinMaxIndex.startColumn, selectedMinMaxIndex.endColumn);
        }
        this.executeDeleteColumnCallback(e);
    };
    /*
     * Updates colgroup structure after column deletion
     */
    TableCommand.prototype.updateColgroupAfterColumnDeletion = function (table, startColIndex, endColIndex) {
        var colGroup = getColGroup(table);
        var cols = colGroup.querySelectorAll('col');
        var deleteCount = endColIndex - startColIndex + 1;
        // Remove cols in the deleted range
        for (var i = 0; i < deleteCount; i++) {
            if (startColIndex < cols.length) {
                colGroup.removeChild(cols[startColIndex]);
                cols = colGroup.querySelectorAll('col');
            }
        }
        // Redistribute widths of remaining columns
        var remainingCount = cols.length;
        var tableWidth = table.offsetWidth;
        var colWidths = new Array(remainingCount);
        // Get all column offsetWidths in one pass to avoid reflow issues
        for (var i = 0; i < remainingCount; i++) {
            colWidths[i] = cols[i].offsetWidth;
        }
        // Now apply percentage widths all at once
        for (var i = 0; i < remainingCount; i++) {
            cols[i].style.width = convertPixelToPercentage(colWidths[i], tableWidth).toFixed(4) + '%';
        }
    };
    /*
     * Removes the entire table when the last column is being deleted.
     */
    TableCommand.prototype.removeEntireTable = function (e) {
        var selectedCell = e.item.selection.range.startContainer;
        detach(closest(selectedCell.parentElement, 'table'));
        e.item.selection.restore();
    };
    /*
     * Removes selected columns, handling colspan adjustments.
     */
    TableCommand.prototype.removeSelectedColumns = function (e) {
        var deleteIndex = -1;
        var allCells = getCorrespondingColumns(this.curTable);
        var selectedMinMaxIndex = this.getSelectedCellMinMaxIndex(allCells);
        var minCol = selectedMinMaxIndex.startColumn;
        var maxCol = selectedMinMaxIndex.endColumn;
        for (var i = 0; i < allCells.length; i++) {
            var currentRow = allCells[i];
            for (var j = 0; j < currentRow.length; j++) {
                var currentCell = currentRow[j];
                // Skip cells that have already been processed
                if (currentCell.dataset.processed === 'true') {
                    continue;
                }
                var currentCellIndex = getCorrespondingIndex(currentCell, allCells);
                var colSpanVal = parseInt(currentCell.getAttribute('colspan'), 10) || 1;
                if (this.isCellAffectedByDeletedColumns(currentCellIndex[1], colSpanVal, minCol, maxCol)) {
                    // Mark as processed
                    currentCell.dataset.processed = 'true';
                    if (colSpanVal > 1) {
                        this.adjustColspan(currentCell, colSpanVal);
                    }
                    else {
                        detach(currentCell);
                        deleteIndex = j;
                        this.handleIESpecificSelection(e, Browser.isIE);
                    }
                }
            }
        }
        this.cleanupProcessedCells();
        this.updateSelectionAfterColumnDelete(e, selectedMinMaxIndex.startRow, deleteIndex);
        return selectedMinMaxIndex;
    };
    /*
     * Cleanup method to remove processing flags from cells
     */
    TableCommand.prototype.cleanupProcessedCells = function () {
        var allTableCells = this.curTable.querySelectorAll('td, th');
        Array.from(allTableCells).forEach(function (cell) {
            delete cell.dataset.processed;
        });
    };
    /*
     * Checks if a cell is affected by the deleted columns.
     */
    TableCommand.prototype.isCellAffectedByDeletedColumns = function (cellColIndex, colSpanVal, minCol, maxCol) {
        return cellColIndex + (colSpanVal - 1) >= minCol && cellColIndex <= maxCol;
    };
    /*
     * Adjusts the colspan attribute of a cell during column deletion.
     */
    TableCommand.prototype.adjustColspan = function (cell, currentColspan) {
        cell.setAttribute('colspan', (currentColspan - 1).toString());
    };
    /*
     * Handles IE-specific selection issues during column deletion.
     */
    TableCommand.prototype.handleIESpecificSelection = function (e, isIE) {
        if (isIE) {
            var firstCell = this.curTable.querySelector('td');
            e.item.selection.setSelectionText(this.tableModel.getDocument(), firstCell, firstCell, 0, 0);
            firstCell.classList.add('e-cell-select');
        }
    };
    /*
     * Updates selection after column deletion.
     */
    TableCommand.prototype.updateSelectionAfterColumnDelete = function (e, rowIndex, deleteIndex) {
        if (deleteIndex > -1) {
            var rowHeadEle = this.curTable && this.curTable.rows[rowIndex];
            var cellIndex = deleteIndex <= (rowHeadEle && this.curTable.rows[rowIndex].cells.length - 1)
                ? deleteIndex
                : deleteIndex - 1;
            var nextFocusCell = this.curTable.rows[rowIndex].cells[cellIndex];
            if (nextFocusCell) {
                e.item.selection.setSelectionText(this.tableModel.getDocument(), nextFocusCell, nextFocusCell, 0, 0);
                nextFocusCell.classList.add('e-cell-select');
            }
        }
    };
    /*
     * Executes the callback after column deletion with additional cursor handling.
     */
    TableCommand.prototype.executeDeleteColumnCallback = function (e) {
        if (e.callBack) {
            var sContainer = this.parent.nodeSelection.getRange(this.tableModel.getDocument()).startContainer;
            // Handle selection if not directly in a TD element
            if (sContainer.nodeName !== 'TD') {
                var startChildLength = this.parent.nodeSelection.
                    getRange(this.tableModel.getDocument()).startOffset;
                var focusNode = sContainer.children[startChildLength];
                if (focusNode) {
                    this.parent.nodeSelection.setCursorPoint(this.tableModel.getDocument(), focusNode, 0);
                }
            }
            this.executeCallback(e);
        }
    };
    /*
     * Deletes selected rows from the table.
     */
    TableCommand.prototype.deleteRow = function (e) {
        var selectedCell = e.item.selection.range.startContainer;
        if (selectedCell.nodeType === 3) { // Text node
            selectedCell = closest(selectedCell.parentElement, 'td,th');
        }
        var colIndex = Array.prototype.indexOf.call(selectedCell.parentNode.childNodes, selectedCell);
        this.curTable = closest(selectedCell, 'table');
        var allCells = getCorrespondingColumns(this.curTable);
        var minMaxIndex = this.getSelectedCellMinMaxIndex(allCells);
        if (this.curTable.rows.length === 1) {
            this.removeEntireTable(e);
        }
        else {
            this.deleteSelectedRows(e, minMaxIndex, allCells, colIndex);
        }
        this.executeCallback(e);
    };
    /*
     * Deletes the selected rows and adjusts the table structure.
     */
    TableCommand.prototype.deleteSelectedRows = function (e, minMaxIndex, allCells, colIndex) {
        for (var rowIndex = minMaxIndex.endRow; rowIndex >= minMaxIndex.startRow; rowIndex--) {
            var currentRow = this.curTable.rows[rowIndex];
            this.adjustRowSpans(rowIndex, allCells);
            this.repositionSpannedCells(rowIndex, allCells);
            var deleteIndex = currentRow.rowIndex;
            this.curTable.deleteRow(deleteIndex);
            this.restoreFocusAfterRowDeletion(e, deleteIndex, colIndex);
        }
    };
    /*
     * Adjusts rowspan attributes of cells when a row is deleted.
     */
    TableCommand.prototype.adjustRowSpans = function (rowIndex, allCells) {
        for (var colIndex = 0; colIndex < allCells[rowIndex].length; colIndex++) {
            if (colIndex !== 0 && allCells[rowIndex][colIndex] === allCells[rowIndex][colIndex - 1]) {
                continue;
            }
            var currentCell = allCells[rowIndex][colIndex];
            var rowspanAttr = currentCell.getAttribute('rowspan');
            if (rowspanAttr && parseInt(rowspanAttr, 10) > 1) {
                var rowSpanVal = parseInt(rowspanAttr, 10) - 1;
                if (rowSpanVal === 1) {
                    currentCell.removeAttribute('rowspan');
                    this.createReplacementCellIfNeeded(colIndex);
                }
                else {
                    currentCell.setAttribute('rowspan', rowSpanVal.toString());
                }
            }
        }
    };
    /*
     * Creates a replacement cell if needed for a merged row.
     */
    TableCommand.prototype.createReplacementCellIfNeeded = function (colIndex) {
        var mergedRowCells = this.getMergedRow(getCorrespondingColumns(this.curTable));
        if (mergedRowCells && colIndex < mergedRowCells.length) {
            var cell = mergedRowCells[colIndex];
            if (cell) {
                var cloneNode = cell.cloneNode(true);
                cloneNode.innerHTML = '<br>';
                if (cell.parentElement) {
                    cell.parentElement.insertBefore(cloneNode, cell);
                }
            }
        }
    };
    /*
     * Repositions cells that span multiple rows when a row is deleted.
     */
    TableCommand.prototype.repositionSpannedCells = function (rowIndex, allCells) {
        for (var colIndex = 0; colIndex < allCells[rowIndex].length; colIndex++) {
            var currentCell = allCells[rowIndex][colIndex];
            var isSpanningToNextRow = rowIndex < allCells.length - 1 &&
                currentCell === allCells[rowIndex + 1][colIndex];
            var isBeginningOfSpan = rowIndex === 0 ||
                currentCell !== allCells[rowIndex - 1][colIndex];
            if (isSpanningToNextRow && isBeginningOfSpan) {
                var firstCellIndex = colIndex;
                while (firstCellIndex > 0 &&
                    currentCell === allCells[rowIndex][firstCellIndex - 1]) {
                    if (firstCellIndex === 0) {
                        this.curTable.rows[rowIndex + 1].prepend(currentCell);
                    }
                    else {
                        var previousCell = allCells[rowIndex + 1][firstCellIndex - 1];
                        previousCell.insertAdjacentElement('afterend', currentCell);
                    }
                    firstCellIndex--;
                }
            }
        }
    };
    /*
     * Restores focus to an appropriate cell after row deletion.
     */
    TableCommand.prototype.restoreFocusAfterRowDeletion = function (e, deleteIndex, colIndex) {
        // Find a suitable row element (either at same index or previous one)
        var focusTrEle = !isNOU(this.curTable.rows[deleteIndex])
            ? this.curTable.rows[deleteIndex]
            : this.curTable.rows[deleteIndex - 1];
        // Find a suitable cell in that row
        var nextFocusCell = focusTrEle &&
            focusTrEle.querySelectorAll('td')[colIndex];
        if (nextFocusCell) {
            e.item.selection.setSelectionText(this.tableModel.getDocument(), nextFocusCell, nextFocusCell, 0, 0);
            nextFocusCell.classList.add('e-cell-select');
        }
        else {
            var firstCell = this.curTable.querySelector('td');
            e.item.selection.setSelectionText(this.tableModel.getDocument(), firstCell, firstCell, 0, 0);
            firstCell.classList.add('e-cell-select');
        }
    };
    /*
     * Finds the first row in the table that has merged cells (different cell count than the first row).
     */
    TableCommand.prototype.getMergedRow = function (cells) {
        var mergedRow;
        var firstRowCellCount = this.curTable.rows[0].childNodes.length;
        for (var i = 0; i < cells.length; i++) {
            if (cells[i].length !== firstRowCellCount) {
                mergedRow = cells[i];
                break;
            }
        }
        return mergedRow;
    };
    /*
     * Removes the entire table from the document and restores selection.
     */
    TableCommand.prototype.removeTable = function (e) {
        var selectedCell = e.item.selection.range.startContainer;
        selectedCell = (selectedCell.nodeType === 3) ? selectedCell.parentNode : selectedCell;
        var selectedTable = closest(selectedCell.parentElement, 'table');
        if (selectedTable) {
            var elementNextSibling = selectedTable.nextSibling;
            detach(selectedTable);
            e.item.selection.restore();
            this.focusAfterTableDeletion(elementNextSibling);
        }
        this.executeCallback(e);
    };
    /*
     * Manages cursor positioning after a table has been deleted
     * Finds the first content node in the next sibling and positions the cursor appropriately
     */
    TableCommand.prototype.focusAfterTableDeletion = function (elementNextSibling) {
        if (!elementNextSibling) {
            return;
        }
        var firstPosition = this.parent.nodeSelection.findFirstContentNode(elementNextSibling);
        if (!firstPosition || !firstPosition.node) {
            return;
        }
        if (firstPosition.node.nodeName === 'BR') {
            var newRange = this.parent.editableElement.ownerDocument.createRange();
            newRange.setStartBefore(firstPosition.node);
            newRange.setEndBefore(firstPosition.node);
            this.parent.nodeSelection.setRange(this.parent.currentDocument, newRange);
        }
        else {
            this.parent.nodeSelection.setCursorPoint(this.parent.currentDocument, firstPosition.node, 0);
        }
    };
    /*
     * Toggles table header (THEAD) on or off in the selected table.
     * If the table doesn't have a header, one will be created.
     * If it already has a header, it will be removed.
     */
    TableCommand.prototype.tableHeader = function (e) {
        var tableElement = this.getTableFromSelection(e);
        var hasHeader = this.checkIfTableHasHeader(tableElement);
        if (tableElement && !hasHeader) {
            this.createTableHeader(tableElement);
        }
        else {
            tableElement.deleteTHead();
        }
        this.executeCallback(e);
    };
    /*
     * Gets the table element from the current selection.
     */
    TableCommand.prototype.getTableFromSelection = function (e) {
        var selectedCell = e.item.selection.range.startContainer;
        if (selectedCell.nodeName === 'TABLE') {
            return selectedCell;
        }
        if (selectedCell.nodeType === 3) {
            selectedCell = selectedCell.parentNode;
        }
        return closest(selectedCell.parentElement, 'table');
    };
    /*
     * Checks if the table already has a header element.
     */
    TableCommand.prototype.checkIfTableHasHeader = function (table) {
        var headerExists = false;
        Array.prototype.slice.call(table.childNodes).forEach(function (childNode) {
            if (childNode.nodeName === 'THEAD') {
                headerExists = true;
            }
        });
        return headerExists;
    };
    /*
     * Creates a header row for the table with appropriate number of cells.
     */
    TableCommand.prototype.createTableHeader = function (table) {
        var firstRow = table.querySelector('tr');
        var cellCount = firstRow.childElementCount;
        var totalCellCount = 0;
        for (var i = 0; i < cellCount; i++) {
            var colspanValue = parseInt(firstRow.children[i].getAttribute('colspan'), 10) || 1;
            totalCellCount += colspanValue;
        }
        var headerSection = table.createTHead();
        var headerRow = headerSection.insertRow(0);
        this.createHeaderCells(headerRow, totalCellCount);
    };
    /*
     * Creates the appropriate number of header cells in the header row.
     */
    TableCommand.prototype.createHeaderCells = function (headerRow, cellCount) {
        for (var j = 0; j < cellCount; j++) {
            var thElement = createElement('th');
            thElement.appendChild(createElement('br'));
            headerRow.appendChild(thElement);
        }
    };
    /*
     * Sets the vertical alignment for the selected table cell.
     */
    TableCommand.prototype.tableVerticalAlign = function (e) {
        var alignValue = this.getVerticalAlignmentValue(e.item.subCommand);
        this.applyVerticalAlignment(e.item.tableCell, alignValue);
        this.executeCallback(e);
    };
    /*
     * Determines the vertical alignment CSS value based on the subcommand.
     */
    TableCommand.prototype.getVerticalAlignmentValue = function (subCommand) {
        switch (subCommand) {
            case 'AlignTop':
                return 'top';
            case 'AlignMiddle':
                return 'middle';
            case 'AlignBottom':
                return 'bottom';
            default:
                return '';
        }
    };
    /*
     * Applies the vertical alignment to the table cell and removes any obsolete
     * valign attribute if necessary.
     */
    TableCommand.prototype.applyVerticalAlignment = function (cell, value) {
        var selectedCells = this.curTable && this.curTable.querySelectorAll('.e-cell-select');
        if (selectedCells && selectedCells.length > 0) {
            for (var i = 0; i < selectedCells.length; i++) {
                selectedCells[i].style.verticalAlign = value;
            }
        }
        else {
            cell.style.verticalAlign = value;
        }
        if (value && value !== '' && cell.getAttribute('valign')) {
            cell.removeAttribute('valign');
        }
    };
    /*
     * Merges selected table cells into a single cell, preserving content and handling
     * rowspan/colspan attributes appropriately.
     */
    TableCommand.prototype.cellMerge = function (e) {
        this.curTable = closest(this.parent.nodeSelection.range.startContainer.parentElement, 'table');
        var selectedCells = this.curTable.querySelectorAll('.e-cell-select');
        if (selectedCells.length < 2) {
            return;
        }
        insertColGroupWithSizes(this.curTable);
        var beforeMergeCellCount = getMaxCellCount(this.curTable);
        this.mergeCellContent();
        var minMaxIndexes = this.getSelectedMinMaxIndexes(getCorrespondingColumns(this.curTable));
        this.configureFirstCellForMerge(selectedCells, minMaxIndexes);
        this.cleanupAfterMerge(selectedCells);
        this.updateTableStructureAfterMerge(minMaxIndexes);
        // Update colgroup after merging cells
        this.updateColgroupAfterMerge(minMaxIndexes.startColumn, minMaxIndexes.endColumn, beforeMergeCellCount);
        this.updateSelectionAfterMerge(e, selectedCells[0]);
        this.executeCallback(e);
    };
    /*
     * Configures the first cell with proper width, height, rowspan and colspan attributes.
     */
    TableCommand.prototype.configureFirstCellForMerge = function (selectedCells, minMaxIndexes) {
        var firstCell = selectedCells[0];
        var rowSelectedCells = firstCell.parentElement.querySelectorAll('.e-cell-select');
        if (minMaxIndexes.startColumn < minMaxIndexes.endColumn) {
            firstCell.setAttribute('colspan', (minMaxIndexes.endColumn - minMaxIndexes.startColumn + 1).toString());
        }
        if (minMaxIndexes.startRow < minMaxIndexes.endRow) {
            firstCell.setAttribute('rowspan', (minMaxIndexes.endRow - minMaxIndexes.startRow + 1).toString());
        }
        var maxHeight = this.calculateMaxCellHeight(rowSelectedCells);
        firstCell.style.height = maxHeight + 'px';
    };
    /*
     * Updates colgroup structure after cells are merged
     */
    TableCommand.prototype.updateColgroupAfterMerge = function (startCol, endCol, beforeMergeCellCount) {
        var colGroup = getColGroup(this.curTable);
        var diffCount = this.isEntireColumnsMerged(beforeMergeCellCount);
        // Only proceed if multiple columns are merged
        if (startCol < endCol && diffCount > 0) {
            var cols = colGroup.querySelectorAll('col');
            var totalWidth = parseFloat(cols[startCol].style.width) || 0;
            if (startCol < cols.length) {
                for (var i = 0; i < diffCount; i++) {
                    var colIndex = startCol + 1; // Always remove the column after startCol
                    if (colIndex < cols.length) {
                        totalWidth += parseFloat(cols[colIndex].style.width);
                        colGroup.removeChild(cols[colIndex]);
                        cols = colGroup.querySelectorAll('col');
                    }
                }
            }
            cols[startCol].style.width = totalWidth + '%';
        }
    };
    /*
     * Checks if the entire columns have been merged across all rows
     */
    TableCommand.prototype.isEntireColumnsMerged = function (beforeMergeCellCount) {
        var afterMergeCellCount = getMaxCellCount(this.curTable);
        // Check if cell count decreased, indicating columns were merged
        var diffCount = beforeMergeCellCount - afterMergeCellCount;
        return diffCount;
    };
    /*
     * Calculates the maximum height among cells in the same row.
     */
    TableCommand.prototype.calculateMaxCellHeight = function (cells) {
        var maxHeight = 0;
        for (var j = 0; j < cells.length; j++) {
            var cellHeight = cells[j].offsetHeight;
            if (cellHeight > maxHeight) {
                maxHeight = cellHeight;
            }
        }
        return maxHeight;
    };
    /*
     * Removes the other cells after merge and cleans up empty rows.
     */
    TableCommand.prototype.cleanupAfterMerge = function (selectedCells) {
        var rowsToDelete = [];
        for (var i = 1; i < selectedCells.length; i++) {
            rowsToDelete.push(selectedCells[i]);
        }
        rowsToDelete.forEach(function (cell) { return detach(cell); });
        var rowsToRemove = [];
        for (var i = 0; i < this.curTable.rows.length; i++) {
            if (this.curTable.rows[i].innerHTML.trim() === '') {
                rowsToRemove.push(this.curTable.rows[i]);
            }
        }
        rowsToRemove.forEach(function (row) { return detach(row); });
        removeClassWithAttr(this.curTable.querySelectorAll('table td, table th'), 'e-multi-cells-select');
        removeClassWithAttr(this.curTable.querySelectorAll('table td, table th'), 'e-cell-select-end');
    };
    /*
     * Updates table structure after merge to maintain proper rowspan/colspan relationships.
     */
    TableCommand.prototype.updateTableStructureAfterMerge = function (minMaxIndexes) {
        this.updateRowSpanStyle(minMaxIndexes.startRow, minMaxIndexes.endRow, getCorrespondingColumns(this.curTable));
        this.updateColSpanStyle(minMaxIndexes.startColumn, minMaxIndexes.endColumn, getCorrespondingColumns(this.curTable));
    };
    /*
     * Updates selection after merge operation to focus on the first cell.
     */
    TableCommand.prototype.updateSelectionAfterMerge = function (e, firstCell) {
        e.item.selection.setSelectionText(this.tableModel.getDocument(), e.item.selection.range.startContainer, e.item.selection.range.startContainer, 0, 0);
        if (this.parent.nodeSelection && firstCell) {
            this.parent.nodeSelection.setCursorPoint(this.tableModel.getDocument(), firstCell, 0);
        }
    };
    /*
     * Updates the colspan attributes of cells in a specified range within the table.
     * This method handles the complex logic of adjusting colspan values when cells are merged or split.
     */
    TableCommand.prototype.updateColSpanStyle = function (min, max, elements) {
        var colIndex;
        var index = 0;
        var count = 0;
        var eleArray = elements;
        max = Math.min(max, eleArray[0].length - 1);
        if (min < max) {
            for (colIndex = min; colIndex <= max; colIndex++) {
                index = this.getEffectiveColspan(eleArray[0][colIndex], max - min + 1);
                if (this.isValidColspanStart(eleArray[0], min, colIndex, index)) {
                    count = this.processRowsForColspan(eleArray, colIndex, index);
                    if (!count) {
                        break;
                    }
                }
            }
            // Apply the calculated colspan adjustments if needed
            if (count) {
                this.updateCellAttribute(eleArray, count, 'colspan', 0, eleArray.length - 1, min, max);
            }
        }
    };
    /*
     * Gets the effective colspan value of a cell, capped by a maximum value.
     */
    TableCommand.prototype.getEffectiveColspan = function (cell, maxAllowed) {
        var colspanAttr = cell.getAttribute('colspan');
        var colspan = colspanAttr ? parseInt(colspanAttr, 10) : 1;
        return Math.min(colspan, maxAllowed);
    };
    /*
     * Determines if a cell is a valid starting point for colspan processing.
     * A valid starting cell is one that isn't part of a previous colspan
     * and has colspan > 1 and continues to next cell.
     */
    TableCommand.prototype.isValidColspanStart = function (row, min, colIndex, colspan) {
        var isPreviousCellContinuation = min < colIndex && row[colIndex] === row[colIndex - 1];
        var hasValidColspan = colspan > 1 && row[colIndex] === row[colIndex + 1];
        return !isPreviousCellContinuation && hasValidColspan;
    };
    /*
     * Processes all rows to ensure consistent colspan structure.
     */
    TableCommand.prototype.processRowsForColspan = function (eleArray, colIndex, index) {
        var count = index - 1;
        for (var rowIndex = 1; rowIndex < eleArray.length; rowIndex++) {
            if (eleArray[rowIndex][colIndex] !== eleArray[rowIndex - 1][colIndex]) {
                count = this.processRowCells(eleArray, rowIndex, colIndex, index, count);
                if (!count) {
                    break;
                }
            }
        }
        return count;
    };
    /*
     * Processes cells in a specific row to adjust colspan values.
     */
    TableCommand.prototype.processRowCells = function (eleArray, rowIndex, colIndex, index, count) {
        var updatedCount = count;
        for (var colMin = colIndex; colMin < colIndex + index; colMin++) {
            var attrValue = parseInt(eleArray[rowIndex][colMin].getAttribute('colspan'), 10) || 1;
            if (attrValue > 1 &&
                eleArray[rowIndex][colMin] === eleArray[rowIndex][colMin + 1]) {
                colMin += updatedCount = Math.min(updatedCount, attrValue - 1);
            }
            else {
                updatedCount = Math.max(0, updatedCount - 1);
                if (updatedCount === 0) {
                    break;
                }
            }
        }
        return updatedCount;
    };
    /*
     * Updates rowspan attributes of cells in the specified range within the table.
     * This complex method manages rowspans when merging or splitting cells.
     */
    TableCommand.prototype.updateRowSpanStyle = function (min, max, ele) {
        var eleArray = ele;
        var count = 0;
        max = Math.min(max, eleArray.length - 1);
        if (min < max) {
            for (var rowValue = min; rowValue <= max; rowValue++) {
                if (this.isValidRowspanStart(eleArray, min, rowValue, max)) {
                    var index = this.getEffectiveRowspan(eleArray[rowValue][0], max - min + 1);
                    count = this.processColumnsForRowspan(eleArray, rowValue, index);
                    if (!count) {
                        break;
                    }
                }
            }
            if (count) {
                this.updateCellAttribute(eleArray, count, 'rowspan', min, max, 0, eleArray[0].length - 1);
            }
        }
    };
    /*
     * Determines if a row is a valid starting point for rowspan processing.
     * Valid if it's not part of a previous row's rowspan and has rowspan > 1.
     */
    TableCommand.prototype.isValidRowspanStart = function (eleArray, min, rowValue, max) {
        var notContinuingPreviousSpan = !(min < rowValue &&
            eleArray[rowValue][0] === eleArray[rowValue - 1][0]);
        var cellExists = !!eleArray[rowValue][0];
        var rowspan = 0;
        if (cellExists) {
            var rowspanAttr = eleArray[rowValue][0].getAttribute('rowspan');
            rowspan = rowspanAttr ? parseInt(rowspanAttr, 10) : 1;
            rowspan = Math.min(rowspan, max - min + 1);
        }
        var spansToNextRow = rowspan > 1 &&
            rowValue + 1 <= max &&
            eleArray[rowValue][0] === eleArray[rowValue + 1][0];
        return notContinuingPreviousSpan && cellExists && rowspan > 1 && spansToNextRow;
    };
    /*
     * Gets the effective rowspan value for a cell, capped by the maximum allowed value.
     */
    TableCommand.prototype.getEffectiveRowspan = function (cell, maxAllowed) {
        var rowspanAttr = cell.getAttribute('rowspan');
        var rowspan = rowspanAttr ? parseInt(rowspanAttr, 10) : 1;
        return Math.min(rowspan, maxAllowed);
    };
    /*
     * Processes all columns to ensure consistent rowspan structure.
     */
    TableCommand.prototype.processColumnsForRowspan = function (eleArray, rowValue, index) {
        var count = index - 1;
        for (var colIndex = 1; colIndex < eleArray[0].length; colIndex++) {
            if (eleArray[rowValue][colIndex] !== eleArray[rowValue][colIndex - 1]) {
                count = this.processColumnCells(eleArray, rowValue, colIndex, index, count);
                if (!count) {
                    break;
                }
            }
        }
        return count;
    };
    /*
     * Processes cells in a specific column to adjust rowspan values.
     */
    TableCommand.prototype.processColumnCells = function (eleArray, rowValue, colIndex, index, count) {
        var updatedCount = count;
        for (var rowMin = rowValue; rowMin < rowValue + index; rowMin++) {
            var attrValue = parseInt(eleArray[rowMin][colIndex].getAttribute('rowspan'), 10) || 1;
            if (attrValue > 1 &&
                rowMin + 1 < eleArray.length &&
                eleArray[rowMin][colIndex] === eleArray[rowMin + 1][colIndex]) {
                rowMin += updatedCount = Math.min(updatedCount, attrValue - 1);
            }
            else {
                updatedCount = Math.max(0, updatedCount - 1);
                if (updatedCount === 0) {
                    break;
                }
            }
        }
        return updatedCount;
    };
    /*
     * Updates cell attributes for spans (colspan/rowspan) within a specified range of cells.
     * Decrements or removes span attributes based on the merging/splitting operation.
     */
    TableCommand.prototype.updateCellAttribute = function (elements, index, attr, min, max, firstIndex, length) {
        for (var rowIndex = min; rowIndex <= max; rowIndex++) {
            for (var colIndex = firstIndex; colIndex <= length; colIndex++) {
                if (elements[rowIndex][colIndex]) {
                    // Skip cells that have already been processed
                    if (elements[rowIndex][colIndex].dataset.processed === 'true') {
                        continue;
                    }
                    var spanCount = parseInt(elements[rowIndex][colIndex].getAttribute(attr), 10) || 1;
                    if (this.shouldUpdateCellAttribute(elements, rowIndex, colIndex, min, firstIndex, spanCount)) {
                        var newSpanValue = spanCount - index;
                        this.updateSpanAttribute(elements[rowIndex][colIndex], attr, newSpanValue);
                        elements[rowIndex][colIndex].dataset.processed = 'true';
                    }
                }
            }
        }
        this.cleanupProcessedCells();
    };
    /*
     * Determines if a cell's span attribute should be updated.
     */
    TableCommand.prototype.shouldUpdateCellAttribute = function (elements, rowIndex, colIndex, minRow, firstColIndex, spanCount) {
        var isPartOfVerticalSpan = minRow < rowIndex &&
            elements[rowIndex][colIndex] === elements[rowIndex - 1][colIndex];
        var isPartOfHorizontalSpan = firstColIndex < colIndex &&
            elements[rowIndex][colIndex] === elements[rowIndex][colIndex - 1];
        var hasSpanGreaterThanOne = spanCount > 1;
        return isPartOfVerticalSpan || isPartOfHorizontalSpan || hasSpanGreaterThanOne;
    };
    /*
     * Updates the span attribute of a cell or removes it if the new value is 1.
     */
    TableCommand.prototype.updateSpanAttribute = function (cell, attr, newValue) {
        if (newValue > 1) {
            cell.setAttribute(attr, newValue.toString());
        }
        else {
            cell.removeAttribute(attr);
        }
    };
    /*
     * Merges the content of all selected cells into the first cell.
     * Empty cells or cells with only a <br> tag are treated as empty.
     */
    TableCommand.prototype.mergeCellContent = function () {
        var selectedCells = this.curTable.querySelectorAll('.e-cell-select');
        var innerHtml = this.isCellEmpty(selectedCells[0]) ? '' : selectedCells[0].innerHTML;
        for (var i = 1; i < selectedCells.length; i++) {
            var currentCell = selectedCells[i];
            if (!this.isCellEmpty(currentCell)) {
                innerHtml = this.appendCellContent(innerHtml, currentCell.innerHTML);
            }
        }
        selectedCells[0].innerHTML = innerHtml;
    };
    /*
     * Checks if a cell is empty or contains only a <br> tag.
     */
    TableCommand.prototype.isCellEmpty = function (cell) {
        return cell.innerHTML === '<br>' || cell.innerHTML === '';
    };
    /*
     * Appends cell content with appropriate separator.
     */
    TableCommand.prototype.appendCellContent = function (existingContent, newContent) {
        return existingContent ? existingContent + '<br>' + newContent : newContent;
    };
    /*
     * Calculates the min and max row/column indexes of selected cells.
     * This is used to determine the boundaries of the area being merged.
     */
    TableCommand.prototype.getSelectedMinMaxIndexes = function (correspondingCells) {
        var selectedCells = this.curTable.querySelectorAll('.e-cell-select');
        if (selectedCells.length > 0) {
            var minMaxData = this.initializeMinMaxData(correspondingCells);
            for (var i = 0; i < selectedCells.length; i++) {
                minMaxData = this.updateMinMaxWithCell(minMaxData, selectedCells[i], correspondingCells);
            }
            return minMaxData;
        }
        return null;
    };
    /*
     * Initializes MinMax data structure with default boundary values.
     */
    TableCommand.prototype.initializeMinMaxData = function (cells) {
        return {
            startRow: cells.length,
            endRow: 0,
            startColumn: cells[0].length,
            endColumn: 0
        };
    };
    /*
     * Updates MinMax boundaries based on a specific cell.
     */
    TableCommand.prototype.updateMinMaxWithCell = function (currentMinMax, cell, cells) {
        var currentRowCol = getCorrespondingIndex(cell, cells);
        var targetRowCol = this.FindIndex(currentRowCol[0], currentRowCol[1], cells);
        return {
            startRow: Math.min(currentRowCol[0], currentMinMax.startRow),
            endRow: Math.max(targetRowCol[0], currentMinMax.endRow),
            startColumn: Math.min(currentRowCol[1], currentMinMax.startColumn),
            endColumn: Math.max(targetRowCol[1], currentMinMax.endColumn)
        };
    };
    /*
     * Splits a selected table cell horizontally into two cells.
     * The selected cell's rowspan will be divided between the original and new cell.
     */
    TableCommand.prototype.horizontalSplit = function (e) {
        var selectedCell = e.item.selection.range.startContainer;
        this.curTable = closest(selectedCell.parentElement, 'table');
        if (this.curTable.querySelectorAll('.e-cell-select').length > 1) {
            return;
        }
        this.activeCell = this.curTable.querySelector('.e-cell-select');
        var newCell = this.prepareNewCellForSplit();
        var activeCellIndex = getCorrespondingIndex(this.activeCell, getCorrespondingColumns(this.curTable));
        var correspondingCells = getCorrespondingColumns(this.curTable);
        var activeCellRowSpan = this.getRowSpanValue(this.activeCell);
        if (activeCellRowSpan > 1) {
            this.splitCellWithRowspan(activeCellRowSpan, activeCellIndex, correspondingCells, newCell);
        }
        else {
            this.splitCellWithoutRowspan(activeCellIndex, correspondingCells, newCell);
        }
        this.executeCallback(e);
    };
    /*
     * Prepares a new table cell by cloning the active cell and resetting its properties.
     */
    TableCommand.prototype.prepareNewCellForSplit = function () {
        var newCell = this.activeCell.cloneNode(true);
        newCell.removeAttribute('class');
        newCell.innerHTML = '<br>';
        return newCell;
    };
    /*
     * Gets the rowspan value of a cell, defaulting to 1 if not specified.
     */
    TableCommand.prototype.getRowSpanValue = function (cell) {
        return cell.getAttribute('rowspan') ? parseInt(cell.getAttribute('rowspan'), 10) : 1;
    };
    /*
     * Splits a cell that has rowspan > 1 by distributing the rowspan between the cells.
     */
    TableCommand.prototype.splitCellWithRowspan = function (currentRowspan, activeCellIndex, correspondingCells, newCell) {
        var topHalfRowspan = Math.ceil(currentRowspan / 2);
        var bottomHalfRowspan = currentRowspan - topHalfRowspan;
        if (this.activeCell.style && this.activeCell.style.height) {
            var originalCellHeight = this.activeCell.offsetHeight;
            var cellHeight = originalCellHeight / currentRowspan;
            this.activeCell.style.height = (cellHeight * topHalfRowspan) + 'px';
            newCell.style.height = (cellHeight * bottomHalfRowspan) + 'px';
        }
        this.updateRowspanAttributes(this.activeCell, newCell, topHalfRowspan, bottomHalfRowspan);
        var avgRowIndex = activeCellIndex[0] + topHalfRowspan;
        var insertionColIndex = this.findInsertionColumnIndex(correspondingCells, avgRowIndex, activeCellIndex[1]);
        this.insertNewCellIntoRow(correspondingCells, avgRowIndex, insertionColIndex, newCell);
    };
    /*
     * Updates rowspan attributes for both cells in the split operation.
     */
    TableCommand.prototype.updateRowspanAttributes = function (activeCell, newCell, topHalfRowspan, bottomHalfRowspan) {
        if (topHalfRowspan > 1) {
            activeCell.setAttribute('rowspan', topHalfRowspan.toString());
        }
        else {
            activeCell.removeAttribute('rowspan');
        }
        if (bottomHalfRowspan > 1) {
            newCell.setAttribute('rowspan', bottomHalfRowspan.toString());
        }
        else {
            newCell.removeAttribute('rowspan');
        }
    };
    /*
     * Finds the appropriate column index to insert the new cell.
     */
    TableCommand.prototype.findInsertionColumnIndex = function (correspondingCells, rowIndex, originalColIndex) {
        var colIndex = originalColIndex === 0 ? originalColIndex : originalColIndex - 1;
        while (colIndex >= 0) {
            var isPartOfHorizontalSpan = correspondingCells[rowIndex][colIndex] ===
                correspondingCells[rowIndex][colIndex - 1];
            var isPartOfVerticalSpan = rowIndex > 0 &&
                correspondingCells[rowIndex][colIndex] ===
                    correspondingCells[rowIndex - 1][colIndex];
            if (!(isPartOfHorizontalSpan || isPartOfVerticalSpan)) {
                break;
            }
            colIndex--;
        }
        return colIndex;
    };
    /*
     * Inserts the new cell into the appropriate row.
     */
    TableCommand.prototype.insertNewCellIntoRow = function (correspondingCells, rowIndex, colIndex, newCell) {
        if (colIndex === -1) {
            var targetRow = this.curTable.rows[rowIndex];
            if (targetRow.firstChild) {
                targetRow.prepend(newCell);
            }
            else {
                this.curTable.appendChild(newCell);
            }
        }
        else {
            correspondingCells[rowIndex][colIndex].insertAdjacentElement('afterend', newCell);
        }
    };
    /*
     * Splits a cell without rowspan by creating a new row with the new cell.
     */
    TableCommand.prototype.splitCellWithoutRowspan = function (activeCellIndex, correspondingCells, newCell) {
        if (this.activeCell.style && this.activeCell.style.height) {
            var cellHeight = this.activeCell.offsetHeight / 2;
            this.activeCell.style.height = cellHeight + 'px';
            newCell.style.height = cellHeight + 'px';
        }
        var newRow = createElement('tr');
        newRow.appendChild(newCell);
        var selectedRow = correspondingCells[activeCellIndex[0]];
        this.adjustRowspansInRow(selectedRow);
        if (this.activeCell.parentNode.style && this.activeCell.parentNode.style.height) {
            var rowHeight = parseFloat(this.activeCell.parentNode.style.height) / 2;
            newRow.style.height = rowHeight + '%';
            this.activeCell.parentNode.style.height = rowHeight + '%';
        }
        this.activeCell.parentNode.insertAdjacentElement('afterend', newRow);
    };
    /*
     * Adjusts rowspan attributes of other cells in the row being split.
     */
    TableCommand.prototype.adjustRowspansInRow = function (rowCells) {
        for (var j = 0; j <= rowCells.length - 1; j++) {
            if (rowCells[j] !== rowCells[j - 1] && rowCells[j] !== this.activeCell) {
                var currentRowspan = parseInt(rowCells[j].getAttribute('rowspan'), 10) || 1;
                rowCells[j].setAttribute('rowspan', (currentRowspan + 1).toString());
            }
        }
    };
    /*
     * Splits a selected table cell vertically into two cells.
     * The selected cell's colspan will be divided between the original and new cell.
     */
    TableCommand.prototype.verticalSplit = function (e) {
        var selectedCell = e.item.selection.range.startContainer;
        this.curTable = closest(selectedCell.parentElement, 'table');
        if (this.curTable.querySelectorAll('.e-cell-select').length > 1) {
            return;
        }
        insertColGroupWithSizes(this.curTable);
        var beforeSplitsCellCount = getMaxCellCount(this.curTable);
        this.activeCell = this.curTable.querySelector('.e-cell-select');
        var newCell = this.prepareNewCellForVerticalSplit();
        var activeCellIndex = getCorrespondingIndex(this.activeCell, getCorrespondingColumns(this.curTable));
        var correspondingColumns = getCorrespondingColumns(this.curTable);
        var activeCellColSpan = this.getColSpanValue(this.activeCell);
        var splitedCellsWidth = { leftCellWidth: 0, rightCellWidth: 0 };
        if (activeCellColSpan > 1) {
            splitedCellsWidth = this.splitCellWithColspan(activeCellColSpan, activeCellIndex, newCell);
        }
        else {
            splitedCellsWidth = this.splitCellWithoutColspan(activeCellIndex, correspondingColumns);
        }
        this.activeCell.parentNode.insertBefore(newCell, this.activeCell.nextSibling);
        this.updateColgroupAfterVerticalSplit(this.curTable, activeCellIndex[1], splitedCellsWidth, beforeSplitsCellCount);
        this.executeCallback(e);
    };
    /*
        * Updates colgroup structure after vertical split
    */
    TableCommand.prototype.updateColgroupAfterVerticalSplit = function (table, originalColIndex, splitedCellsWidth, beforeSplitsCellCount) {
        var colGroup = getColGroup(table);
        var afterSplitsCellCount = getMaxCellCount(table);
        var cols = colGroup.querySelectorAll('col');
        if (originalColIndex < cols.length && beforeSplitsCellCount < afterSplitsCellCount) {
            cols[originalColIndex].style.width = splitedCellsWidth.leftCellWidth + '%';
            var newCol = createElement('col');
            newCol.appendChild(createElement('br'));
            newCol.style.width = splitedCellsWidth.rightCellWidth + '%';
            cols[originalColIndex].parentNode.insertBefore(newCol, cols[originalColIndex].nextSibling);
        }
    };
    /*
     * Prepares a new table cell by cloning the active cell and resetting its properties.
     */
    TableCommand.prototype.prepareNewCellForVerticalSplit = function () {
        var newCell = this.activeCell.cloneNode(true);
        newCell.removeAttribute('class');
        newCell.innerHTML = '<br>';
        return newCell;
    };
    /*
     * Gets the colspan value of a cell, defaulting to 1 if not specified.
     */
    TableCommand.prototype.getColSpanValue = function (cell) {
        return parseInt(cell.getAttribute('colspan'), 10) || 1;
    };
    /*
     * Splits a cell that has colspan > 1 by distributing the colspan between the cells.
     */
    TableCommand.prototype.splitCellWithColspan = function (currentColspan, activeCellIndex, newCell) {
        var leftHalfColspan = Math.ceil(currentColspan / 2);
        var rightHalfColspan = currentColspan - leftHalfColspan;
        var colSizes = this.getColSizes(this.curTable);
        var leftCellWidth = this.calculateLeftCellWidth(activeCellIndex[1], leftHalfColspan, colSizes);
        var rightCellWidth = this.calculateRightCellWidth(activeCellIndex[1], leftHalfColspan, currentColspan, colSizes, leftCellWidth);
        this.updateColspanAttributes(this.activeCell, newCell, leftHalfColspan, rightHalfColspan);
        return { leftCellWidth: leftCellWidth, rightCellWidth: rightCellWidth };
    };
    /*
     * Calculates the width for the left cell after splitting.
     */
    TableCommand.prototype.calculateLeftCellWidth = function (startColIndex, leftHalfColspan, colSizes) {
        return this.getSplitColWidth(startColIndex, startColIndex + leftHalfColspan - 1, colSizes);
    };
    /*
     * Calculates the width for the right cell after splitting.
     */
    TableCommand.prototype.calculateRightCellWidth = function (startColIndex, leftHalfColspan, totalColspan, colSizes, leftCellWidth) {
        var calculatedWidth = this.getSplitColWidth(startColIndex + leftHalfColspan, startColIndex + totalColspan - 1, colSizes);
        var activeCellWidth = convertPixelToPercentage(this.activeCell.offsetWidth, this.curTable.offsetWidth);
        return (activeCellWidth - leftCellWidth) < calculatedWidth ?
            (activeCellWidth - leftCellWidth) : calculatedWidth;
    };
    /*
     * Updates colspan attributes for both cells in the split operation.
     */
    TableCommand.prototype.updateColspanAttributes = function (activeCell, newCell, leftHalfColspan, rightHalfColspan) {
        if (leftHalfColspan > 1) {
            activeCell.setAttribute('colspan', leftHalfColspan.toString());
        }
        else {
            activeCell.removeAttribute('colspan');
        }
        if (rightHalfColspan > 1) {
            newCell.setAttribute('colspan', rightHalfColspan.toString());
        }
        else {
            newCell.removeAttribute('colspan');
        }
    };
    /*
     * Splits a cell without colspan by creating two cells with equal width.
     */
    TableCommand.prototype.splitCellWithoutColspan = function (activeCellIndex, correspondingColumns) {
        var avgWidth = convertPixelToPercentage(this.activeCell.offsetWidth, this.curTable.offsetWidth) / 2;
        this.adjustColspansInColumn(correspondingColumns, activeCellIndex);
        return { leftCellWidth: avgWidth, rightCellWidth: avgWidth };
    };
    /*
     * Adjusts colspan attributes of other cells in the column being split.
     */
    TableCommand.prototype.adjustColspansInColumn = function (correspondingColumns, activeCellIndex) {
        var allRows = this.curTable.rows;
        for (var i = 0; i <= allRows.length - 1; i++) {
            if (this.shouldAdjustColspanForCell(i, correspondingColumns, activeCellIndex)) {
                var currentCell = correspondingColumns[i][activeCellIndex[1]];
                this.incrementColspan(currentCell);
            }
        }
    };
    /*
     * Determines if a cell's colspan should be adjusted during a vertical split.
     */
    TableCommand.prototype.shouldAdjustColspanForCell = function (rowIndex, correspondingColumns, activeCellIndex) {
        return (rowIndex === 0 ||
            correspondingColumns[rowIndex][activeCellIndex[1]] !== correspondingColumns[rowIndex - 1][activeCellIndex[1]]) &&
            correspondingColumns[rowIndex][activeCellIndex[1]] !== this.activeCell;
    };
    /*
     * Increments the colspan attribute of a cell.
     */
    TableCommand.prototype.incrementColspan = function (cell) {
        var currentColspan = parseInt(cell.getAttribute('colspan'), 10) || 1;
        cell.setAttribute('colspan', (currentColspan + 1).toString());
    };
    /*
     * Calculates the width of a specific column range for splitting.
     */
    TableCommand.prototype.getSplitColWidth = function (startIndex, endIndex, sizes) {
        var width = 0;
        for (var i = startIndex; i <= endIndex; i++) {
            width += sizes[i];
        }
        return convertPixelToPercentage(width, this.curTable.offsetWidth);
    };
    /*
     * Calculates column widths for table cells, handling complex layouts with rowspan and colspan.
     * Used during table operations such as split cell to maintain proper proportions.
     */
    TableCommand.prototype.getColSizes = function (curTable) {
        var cellColl = curTable.rows[0].cells;
        var cellCount = 0;
        for (var cell = 0; cell < cellColl.length; cell++) {
            cellCount = cellCount + cellColl[cell].colSpan;
        }
        var sizes = new Array(cellCount);
        var rowSpanCells = new Map();
        for (var i = 0; i < curTable.rows.length; i++) {
            var currentColIndex = 0;
            for (var k = 0; k < curTable.rows[i].cells.length; k++) {
                this.mapRowspanCells(curTable, rowSpanCells, i, k, currentColIndex);
                var cellIndex = getCellIndex(rowSpanCells, i, k);
                if (cellIndex > currentColIndex) {
                    currentColIndex = cellIndex;
                }
                this.storeCellWidth(curTable, sizes, currentColIndex, i, k);
                currentColIndex += 1 + curTable.rows[i].cells[k].colSpan - 1;
            }
        }
        return sizes;
    };
    /*
     * Maps cells with rowspan attributes for tracking complex table layouts.
     */
    TableCommand.prototype.mapRowspanCells = function (curTable, rowSpanCells, rowIndex, cellIndex, colIndex) {
        for (var l = 1; l < curTable.rows[rowIndex].cells[cellIndex].rowSpan; l++) {
            var key = "" + (rowIndex + l) + colIndex;
            rowSpanCells.set(key, curTable.rows[rowIndex].cells[cellIndex]);
        }
    };
    /*
     * Stores the width of a cell in the sizes array if it's smaller than existing width or not yet set.
     */
    TableCommand.prototype.storeCellWidth = function (curTable, sizes, colIndex, rowIndex, cellIndex) {
        var width = curTable.rows[rowIndex].cells[cellIndex].offsetWidth;
        if (!sizes[colIndex] || width < sizes[colIndex]) {
            sizes[colIndex] = width;
        }
    };
    /*
     * Finds the end indices of a cell in the table matrix, considering rowspan and colspan.
     */
    TableCommand.prototype.FindIndex = function (rowIndex, columnIndex, cells) {
        var endRowIndex = rowIndex + 1;
        var endColumnIndex = columnIndex + 1;
        while (endRowIndex < cells.length) {
            if (cells[endRowIndex][columnIndex] !== cells[rowIndex][columnIndex]) {
                endRowIndex--;
                break;
            }
            endRowIndex++;
        }
        if (endRowIndex === cells.length) {
            endRowIndex--;
        }
        while (endColumnIndex < cells[rowIndex].length) {
            if (cells[rowIndex][endColumnIndex] !== cells[rowIndex][columnIndex]) {
                endColumnIndex--;
                break;
            }
            endColumnIndex++;
        }
        if (endColumnIndex === cells[rowIndex].length) {
            endColumnIndex--;
        }
        return [endRowIndex, endColumnIndex];
    };
    /*
     * Checks if the cell has a rowspan or colspan greater than 1.
     */
    TableCommand.prototype.isMergedCell = function (cell) {
        return ((parseInt(cell.getAttribute('rowspan') || '1', 10) > 1) ||
            (parseInt(cell.getAttribute('colspan') || '1', 10) > 1));
    };
    /*
     * Adjusts the selection boundary based on merged cells (rowspan/colspan).
     */
    TableCommand.prototype.adjustBoundary = function (rowIndex, colIndex, eleArray, minRowIndex, maxRowIndex, minColIndex, maxColIndex) {
        var startCell = getCorrespondingIndex(eleArray[rowIndex][colIndex], eleArray);
        var endCell = this.FindIndex(startCell[0], startCell[1], eleArray);
        if (endCell) {
            minRowIndex = Math.min(startCell[0], minRowIndex);
            maxRowIndex = Math.max(endCell[0], maxRowIndex);
            minColIndex = Math.min(startCell[1], minColIndex);
            maxColIndex = Math.max(endCell[1], maxColIndex);
        }
        return [minRowIndex, maxRowIndex, minColIndex, maxColIndex];
    };
    /*
     * Highlights a range of cells in a table, accounting for merged cells (rowspan/colspan)
     * by expanding the selection to fully include any partially selected merged cells.
     */
    TableCommand.prototype.highlightCells = function (minRow, maxRow, minCol, maxCol, eleArray) {
        var _a, _b, _c, _d;
        var minRowIndex = minRow;
        var maxRowIndex = maxRow;
        var minColIndex = minCol;
        var maxColIndex = maxCol;
        // Loop through rows to adjust selection boundaries
        for (var j = minRowIndex; j <= maxRowIndex; j++) {
            if (this.isMergedCell(eleArray[j][minColIndex])) {
                _a = this.adjustBoundary(j, minColIndex, eleArray, minRowIndex, maxRowIndex, minColIndex, maxColIndex), minRowIndex = _a[0], maxRowIndex = _a[1], minColIndex = _a[2], maxColIndex = _a[3];
            }
            if (this.isMergedCell(eleArray[j][maxColIndex])) {
                _b = this.adjustBoundary(j, maxColIndex, eleArray, minRowIndex, maxRowIndex, minColIndex, maxColIndex), minRowIndex = _b[0], maxRowIndex = _b[1], minColIndex = _b[2], maxColIndex = _b[3];
            }
            // Loop through columns to adjust selection boundaries
            for (var k = minColIndex; k <= maxColIndex; k++) {
                if (this.isMergedCell(eleArray[minRowIndex][k])) {
                    _c = this.adjustBoundary(minRowIndex, k, eleArray, minRowIndex, maxRowIndex, minColIndex, maxColIndex), minRowIndex = _c[0], maxRowIndex = _c[1], minColIndex = _c[2], maxColIndex = _c[3];
                }
                if (this.isMergedCell(eleArray[maxRowIndex][k])) {
                    _d = this.adjustBoundary(maxRowIndex, k, eleArray, minRowIndex, maxRowIndex, minColIndex, maxColIndex), minRowIndex = _d[0], maxRowIndex = _d[1], minColIndex = _d[2], maxColIndex = _d[3];
                }
            }
        }
        // If the selection has expanded, recursively check for further expansions
        return (minRowIndex === minRow && maxRowIndex === maxRow && minColIndex === minCol && maxColIndex === maxCol)
            ? { startRow: minRow, endRow: maxRow, startColumn: minCol, endColumn: maxCol }
            : this.highlightCells(minRowIndex, maxRowIndex, minColIndex, maxColIndex, eleArray);
    };
    /*
     * Restores the selection range to a specific table cell
     */
    TableCommand.prototype.restoreRange = function (target) {
        // Special handling for Safari browser
        if (this.parent.userAgentData.isSafari()) {
            this.parent.nodeSelection.Clear(this.tableModel.getDocument());
            return;
        }
        // Only set cursor in table cells and when a valid selection exists
        var isTableCell = target.nodeName === 'TD' || target.nodeName === 'TH';
        var hasValidSelection = this.tableModel.getDocument().getSelection().rangeCount > 0;
        if (hasValidSelection && isTableCell) {
            this.parent.nodeSelection.setCursorPoint(this.tableModel.getDocument(), target, 0);
        }
    };
    /*
     * Applies table style and executes the associated callback
     */
    TableCommand.prototype.tableStyle = function (e) {
        this.executeCallback(e);
    };
    /*
     * Handles table cell selection and highlighting when moving across cells
     */
    TableCommand.prototype.tableMove = function (e) {
        this.activeCell = e.selectNode[0];
        if (!this.activeCell) {
            return;
        }
        var target = e.event.target;
        if (!this.isValidCellTarget(target)) {
            var closestCell = null;
            if (target.nodeType !== Node.ELEMENT_NODE) {
                closestCell = target.parentElement;
            }
            else {
                closestCell = target;
            }
            if (closestCell && closestCell.tagName !== 'TD' && closestCell.tagName !== 'TH') {
                closestCell = closest(closestCell, 'TD') || closest(closestCell, 'TH');
            }
            if (closestCell) {
                target = closestCell;
            }
            else {
                return;
            }
        }
        this.curTable = closest(target, 'table');
        var activeCellTable = closest(this.activeCell, 'table');
        if (activeCellTable.contains(this.curTable)) {
            var targetCell = this.findContainingCell(activeCellTable, this.curTable);
            if (targetCell) {
                this.curTable = activeCellTable;
                target = targetCell;
            }
        }
        if (!this.areCellsInSameTable(this.curTable, activeCellTable)) {
            return;
        }
        var correspondingCells = getCorrespondingColumns(this.curTable);
        var activeIndexes = getCorrespondingIndex(this.activeCell, correspondingCells);
        var targetIndexes = getCorrespondingIndex(target, correspondingCells);
        var activeCellList = this.clearPreviousSelection();
        if (this.isSameCellSelected(activeIndexes, targetIndexes, activeCellList)) {
            return;
        }
        this.selectCellRange(activeIndexes, targetIndexes, correspondingCells);
        target.classList.add('e-cell-select-end');
        if (e.event.type) {
            e.event.preventDefault();
        }
        this.restoreRange(target);
    };
    /*
    * Finds the table cell that contains the specified target element.
    * Iterates through all rows and cells in the table to locate the containing cell.
    */
    TableCommand.prototype.findContainingCell = function (table, targetElement) {
        var rows = table.rows;
        for (var i = 0; i < rows.length; i++) {
            var cells = rows[i].cells;
            for (var j = 0; j < cells.length; j++) {
                var cell = cells[j];
                if (cell.contains(targetElement)) {
                    return cell;
                }
            }
        }
        return null;
    };
    /*
     * Checks if the target element is a valid table cell
     */
    TableCommand.prototype.isValidCellTarget = function (target) {
        if (!this.activeCell || !target) {
            return false;
        }
        var activeCellTag = this.activeCell.tagName;
        var targetCellTag = target.tagName;
        var isTableCell = target.tagName === 'TD' || target.tagName === 'TH';
        return isTableCell || activeCellTag === targetCellTag;
    };
    /*
     * Checks if two cells are in the same table
     */
    TableCommand.prototype.areCellsInSameTable = function (table1, table2) {
        return !isNOU(table1) && !isNOU(table2) && table1 === table2;
    };
    /*
     * Clears all existing table cell selections
     */
    TableCommand.prototype.clearPreviousSelection = function () {
        var activeCellList = this.curTable.querySelectorAll('.e-cell-select, .e-multi-cells-select, .e-cell-select-end');
        for (var i = activeCellList.length - 1; i >= 0; i--) {
            var index = i; // Fix for Generic Object Injection Sink
            if (this.activeCell !== activeCellList[index]) {
                removeClassWithAttr([activeCellList[index]], ['e-cell-select']);
            }
            removeClassWithAttr([activeCellList[index]], ['e-multi-cells-select']);
            removeClassWithAttr([activeCellList[index]], ['e-cell-select-end']);
        }
        return activeCellList;
    };
    /*
     * Checks if the same cell is being selected
     */
    TableCommand.prototype.isSameCellSelected = function (activeIndexes, targetIndexes, activeCellList) {
        var isSameCell = activeIndexes[0] === targetIndexes[0] &&
            activeIndexes[1] === targetIndexes[1];
        if (isSameCell) {
            if (activeCellList.length > 1) {
                this.restoreRange(this.activeCell);
            }
            return true;
        }
        return false;
    };
    /*
     * Selects a range of cells between the active cell and target cell
     */
    TableCommand.prototype.selectCellRange = function (activeIndexes, targetIndexes, correspondingCells) {
        // Calculate selection boundaries, accounting for merged cells
        var minMaxIndexes = this.highlightCells(Math.min(activeIndexes[0], targetIndexes[0]), Math.max(activeIndexes[0], targetIndexes[0]), Math.min(activeIndexes[1], targetIndexes[1]), Math.max(activeIndexes[1], targetIndexes[1]), correspondingCells);
        for (var rowIndex = minMaxIndexes.startRow; rowIndex <= minMaxIndexes.endRow; rowIndex++) {
            var row = rowIndex;
            for (var colIndex = minMaxIndexes.startColumn; colIndex <= minMaxIndexes.endColumn; colIndex++) {
                var col = colIndex;
                correspondingCells[row][col].classList.add('e-cell-select');
                correspondingCells[row][col].classList.add('e-multi-cells-select');
            }
        }
    };
    /**
     * Cleans up resources by removing all event listeners
     *
     * @public
     * @returns {void}
     */
    TableCommand.prototype.destroy = function () {
        this.removeEventListener();
        if (this.resizeIconPositionTime) {
            clearTimeout(this.resizeIconPositionTime);
            this.resizeIconPositionTime = null;
        }
    };
    /*
     * Filters out specific CSS style properties from a style string
     * This method is used to clean up cell styles when copying/cloning cells
     */
    TableCommand.prototype.cellStyleCleanup = function (value) {
        var styles = value.split(';');
        var newStyles = [];
        var deniedFormats = [
            'vertical-align',
            'text-align'
        ];
        for (var i = 0; i < styles.length; i++) {
            var index = i;
            var style = styles[index];
            var isAllowed = true;
            for (var j = 0; j < deniedFormats.length; j++) {
                var formatIndex = j;
                var deniedStyle = deniedFormats[formatIndex];
                if (style.indexOf(deniedStyle) > -1) {
                    isAllowed = false;
                    break;
                }
            }
            if (isAllowed) {
                newStyles.push(style);
            }
        }
        return newStyles.join(';');
    };
    /**
     * Calculates the collection of the minimum width cells from each column in the table,
     * considering colSpan and rowSpan for proper cell indexing.
     *
     * @param {HTMLTableElement} curTable - The current table element to process.
     * @returns {HTMLTableDataCellElement[]} - Returns an array of HTMLTableDataCellElement representing each column's minimum width cell.
     * @public
     */
    TableCommand.prototype.calMaxCol = function (curTable) {
        if (!curTable || !curTable.rows || curTable.rows.length === 0 || !curTable.rows[0] || !curTable.rows[0].cells) {
            return [];
        }
        var cellColl = curTable.rows[0].cells;
        var cellCount = 0;
        for (var cell = 0; cell < cellColl.length; cell++) {
            cellCount = cellCount + cellColl[cell].colSpan;
        }
        var cells = new Array(cellCount);
        var rowSpanCells = new Map();
        for (var i = 0; i < curTable.rows.length; i++) {
            var currentColIndex = 0;
            for (var k = 0; k < curTable.rows[i].cells.length; k++) {
                for (var l = 1; l < curTable.rows[i].cells[k].rowSpan; l++) {
                    var key = "" + (i + l) + currentColIndex;
                    rowSpanCells.set(key, curTable.rows[i].cells[k]);
                }
                var cellIndex = getCellIndex(rowSpanCells, i, k);
                if (cellIndex > currentColIndex) {
                    currentColIndex = cellIndex;
                }
                var width = curTable.rows[i].cells[k].offsetWidth;
                if (!cells[currentColIndex] || width < cells[currentColIndex].offsetWidth) {
                    cells[currentColIndex] = curTable.rows[i].cells[k];
                }
                currentColIndex += 1 + curTable.rows[i].cells[k].colSpan - 1;
            }
        }
        return cells;
    };
    /**
     * Initializes the resize button state for columns, rows, and table box.
     *
     * @returns {Object} - An object representing the resize button state.
     * @public
     */
    TableCommand.prototype.resizeBtnInit = function () {
        return this.resizeBtnStat = { column: false, row: false, tableBox: false };
    };
    /**
     * Calculates the offset position of the given element relative to its offset parent.
     *
     * @param {HTMLElement} elem - The element for which to calculate the position.
     * @returns {OffsetPosition} - The top and left offset position of the element.
     * @public
     */
    TableCommand.prototype.calcPos = function (elem) {
        var parentOffset = { top: 0, left: 0 };
        if (!elem) {
            return parentOffset;
        }
        var offset = elem.getBoundingClientRect();
        var doc = elem.ownerDocument;
        var offsetParent = this.getOffsetParent(elem, doc);
        var isNestedTable = false;
        // Check for nested table inside TD
        if (offsetParent && offsetParent.nodeName === 'TD' && elem.nodeName === 'TABLE') {
            offsetParent = closest(offsetParent, '.e-rte-content');
            isNestedTable = true;
        }
        // Get parent offset if available
        if (offsetParent && offsetParent !== elem && offsetParent.nodeType === 1) {
            parentOffset = offsetParent.getBoundingClientRect();
        }
        // Adjust position if it's a nested table
        if (isNestedTable) {
            isNestedTable = false;
            var scrollElement = this.iframeSettings.enable ?
                doc.documentElement : this.tableModel.getEditPanel();
            var scrollTop = (scrollElement && scrollElement.scrollTop) || 0;
            var scrollLeft = (scrollElement && scrollElement.scrollLeft) || 0;
            var topValue = (scrollTop > 0 ? (scrollTop + offset.top) - parentOffset.top : offset.top - parentOffset.top);
            var leftValue = (scrollLeft > 0 ? (scrollLeft + offset.left) - parentOffset.left : offset.left - parentOffset.left);
            return { top: topValue, left: leftValue };
        }
        else if (offsetParent !== this.tableModel.getEditPanel() && elem.nodeName === 'TABLE') {
            var tableParent = elem;
            while (tableParent && tableParent.parentElement !== this.tableModel.getEditPanel()) {
                tableParent = tableParent.parentElement;
            }
            var tableParentOffset = tableParent.getBoundingClientRect();
            return {
                top: this.iframeSettings.enable ? offset.top : tableParent.offsetTop + offset.top - tableParentOffset.top,
                left: offset.left - tableParentOffset.left + 1
            };
        }
        else {
            return { top: elem.offsetTop, left: elem.offsetLeft };
        }
    };
    /*
     * Finds the appropriate offset parent for an element.
     * Traverses up the DOM tree to find a non-static positioned parent.
     */
    TableCommand.prototype.getOffsetParent = function (elem, doc) {
        var offsetParent = elem.offsetParent || doc.documentElement;
        // Traverse up to find non-static positioned parent
        while (offsetParent &&
            (offsetParent === doc.body || offsetParent === doc.documentElement) &&
            offsetParent.style.position === 'static') {
            offsetParent = offsetParent.parentNode;
        }
        return offsetParent;
    };
    /*
     * Gets the X coordinate from a PointerEvent or TouchEvent.
     */
    TableCommand.prototype.getPointX = function (e) {
        var touchEvent = e;
        var pointerEvent = e;
        if (touchEvent.touches && touchEvent.touches.length > 0) {
            return touchEvent.touches[0].pageX;
        }
        else {
            return pointerEvent.pageX;
        }
    };
    /*
     * Gets the Y coordinate from a PointerEvent or TouchEvent.
     */
    TableCommand.prototype.getPointY = function (e) {
        var touchEvent = e;
        var pointerEvent = e;
        if (touchEvent.touches && touchEvent.touches.length > 0) {
            return touchEvent.touches[0].pageY;
        }
        else {
            return pointerEvent.pageY;
        }
    };
    /*
     * Calculates the current column width as a percentage of the table width.
     */
    TableCommand.prototype.getCurrentColWidth = function (col, tableWidth) {
        var currentColWidth = 0;
        if (col && col.style && col.style.width !== '') {
            var widthValue = col.style.width;
            if (widthValue.indexOf('%') !== -1) {
                currentColWidth = parseFloat(widthValue.split('%')[0]);
            }
            else {
                currentColWidth = convertPixelToPercentage(col.offsetWidth, tableWidth);
            }
        }
        else {
            if (col && tableWidth > 0) {
                currentColWidth = convertPixelToPercentage(col.offsetWidth, tableWidth);
            }
        }
        return currentColWidth;
    };
    /*
     * Removes all resize helper elements and converts cell widths from pixels to percentages.
     */
    TableCommand.prototype.resetResizeHelper = function (curTable) {
        var colHelper = this.tableModel.rteElement.querySelectorAll('.e-table-rhelper.e-column-helper');
        Array.from(colHelper).forEach(function (element) {
            if (element.parentNode) {
                element.parentNode.removeChild(element);
            }
        });
        var rowHelper = this.tableModel.rteElement.querySelectorAll('.e-table-rhelper.e-row-helper');
        Array.from(rowHelper).forEach(function (element) {
            if (element.parentNode) {
                element.parentNode.removeChild(element);
            }
        });
        if (parseInt(curTable.style.width, 10) === 0) {
            curTable.style.width = curTable.offsetWidth + 'px';
        }
    };
    /*
     * Handles the start of a table resize operation when user interacts with the resizer.
     */
    TableCommand.prototype.resizeStart = function (e) {
        if (!this.parent || !this.tableModel || this.tableModel.readonly) {
            return;
        }
        if (Browser.isDevice) {
            this.resizeHelper(e);
        }
        var target = e.target;
        if (!target || !(target.classList.contains(EVENTS.CLS_TB_COL_RES) ||
            target.classList.contains(EVENTS.CLS_TB_ROW_RES) ||
            target.classList.contains(EVENTS.CLS_TB_BOX_RES))) {
            return;
        }
        this.resetResizeHelper(this.curTable);
        e.preventDefault();
        this.tableModel.preventDefaultResize(e);
        if (!target.classList.contains(EVENTS.CLS_TB_BOX_RES)) {
            var rzBox = this.tableModel.getEditPanel().querySelector('.e-table-box');
            if (!isNOU(rzBox) &&
                parseInt(target.getAttribute('data-col'), 10) !== this.calMaxCol(this.curTable).length) {
                rzBox.classList.add('e-hide');
            }
        }
        removeClassWithAttr(this.curTable.querySelectorAll('td,th'), CLS_TABLE_SEL);
        this.removeTableSelection();
        this.pageX = this.getPointX(e);
        this.pageY = this.getPointY(e);
        this.resizeBtnStat = this.resizeBtnInit();
        this.tableModel.hideTableQuickToolbar();
        if (target.classList.contains(EVENTS.CLS_TB_COL_RES)) {
            this.handleColumnResize(target);
        }
        else if (target.classList.contains(EVENTS.CLS_TB_ROW_RES)) {
            this.handleRowResize(target);
        }
        else if (target.classList.contains(EVENTS.CLS_TB_BOX_RES)) {
            this.resizeBtnStat.tableBox = true;
        }
        if (Browser.isDevice && this.helper && !this.helper.classList.contains('e-reicon')) {
            this.helper.classList.add('e-reicon');
            EventHandler.add(document, Browser.touchStartEvent, this.removeHelper, this);
            EventHandler.add(this.helper, Browser.touchStartEvent, this.resizeStart, this);
        }
        else {
            var args = { event: e, requestType: 'Table' };
            this.tableModel.resizeStart(args);
        }
        if (this.isResizeBind) {
            EventHandler.add(this.tableModel.getDocument(), Browser.touchMoveEvent, this.resizing, this);
            EventHandler.add(this.tableModel.getDocument(), Browser.touchEndEvent, this.resizeEnd, this);
            this.isResizeBind = false;
        }
        if (this.tableModel.tableSelectionFeature) {
            this.removeSelectionWrappers(true);
        }
    };
    /*
     * Handles column resize setup.
     */
    TableCommand.prototype.handleColumnResize = function (target) {
        this.resizeBtnStat.column = true;
        insertColGroupWithSizes(this.curTable);
        var dataColAttr = target.getAttribute('data-col') || '0';
        var dataCol = parseInt(dataColAttr, 10);
        if (dataCol === this.calMaxCol(this.curTable).length) {
            this.currentColumnResize = 'last';
            this.colIndex = dataCol - 1;
            this.columnEle = this.calMaxCol(this.curTable)[this.colIndex];
        }
        else {
            this.currentColumnResize = (dataCol === 0) ? 'first' : 'middle';
            this.colIndex = dataCol;
            this.columnEle = this.calMaxCol(this.curTable)[this.colIndex];
        }
        this.appendHelper(target);
    };
    /*
     * Appends a helper element to visualize the resize operation.
     */
    TableCommand.prototype.appendHelper = function (target) {
        var cssClass = 'e-table-rhelper' + this.tableModel.getCssClass(true);
        this.helper = createElement('div', { className: cssClass });
        if (Browser.isDevice) {
            this.helper.classList.add('e-reicon');
        }
        this.tableModel.getEditPanel().appendChild(this.helper);
        this.setHelperHeight(target);
        this.hideRowColumnAddIcons(target);
    };
    /*
     * Sets the position and size of the helper element based on the resize type (column or row).
     */
    TableCommand.prototype.setHelperHeight = function (target) {
        // Check if resize button state and helper are available
        if (this.resizeBtnStat && this.resizeBtnStat.column) {
            this.helper.classList.add('e-column-helper');
            var tableHeight = target.style.height;
            var columnLeft = parseFloat(target.style.left) + 0.5;
            var top_1 = target.style.top;
            this.helper.style.cssText =
                'height: ' + tableHeight + '; ' +
                    'top: ' + top_1 + '; ' +
                    'left: ' + columnLeft + 'px;';
        }
        else {
            this.helper.classList.add('e-row-helper');
            var tableWidth = target.style.width;
            var rowTop = parseFloat(target.style.top) - 0.5;
            var rowLeft = target.style.left;
            this.helper.style.cssText =
                'width: ' + tableWidth + '; ' +
                    'top: ' + rowTop + 'px; ' +
                    'left: ' + rowLeft + ';';
        }
    };
    /*
     * Updates the position of the helper element during the resize operation.
     */
    TableCommand.prototype.updateHelper = function () {
        if (!this.helper) {
            return;
        }
        var pos = this.calcPos(this.curTable);
        pos = this.adjustPositionForScrollbar(pos, this.curTable);
        // Check if the current operation is a column resize
        if (this.resizeBtnStat && this.resizeBtnStat.column) {
            var columnLeft = pos.left + this.calcPos(this.columnEle).left +
                (this.tableModel.enableRtl ? this.columnEle.getBoundingClientRect().width : 0);
            var offset = (this.currentColumnResize === 'last' && !this.tableModel.enableRtl) ? this.columnEle.offsetWidth : 0;
            var left = columnLeft + offset - 1;
            this.helper.style.left = left + 'px';
            this.helper.style.height = this.curTable.offsetHeight + 'px';
        }
        else {
            // Handle row resize
            var rowTop = this.calcPos(this.rowEle).top + pos.top + this.rowEle.offsetHeight - 1;
            this.helper.style.top = rowTop + 'px';
            this.helper.style.width = this.curTable.offsetWidth + 'px';
        }
    };
    /*
     * Handles row resize setup.
     */
    TableCommand.prototype.handleRowResize = function (target) {
        var dataRowAttr = target.getAttribute('data-row') || '0';
        var dataRow = parseInt(dataRowAttr, 10);
        this.rowEle = this.curTable.rows[dataRow];
        this.resizeBtnStat.row = true;
        this.appendHelper(target);
    };
    /**
     * Adds resize-related event handlers to the editor panel.
     * Registers touch events for all devices and mouseover for non-mobile devices.
     *
     * @returns {void} - This method does not return a value
     * @private
     */
    TableCommand.prototype.addResizeEventHandlers = function () {
        // Add touch event handlers for resizing on all devices
        if (this.tableModel.tableSettings.resize) {
            this.parent.observer.on(EVENTS.touchStart, this.resizeStart, this);
        }
        // Add mouseover handler for non-mobile devices only
        if (!Browser.isDevice) {
            EventHandler.add(this.tableModel.getEditPanel(), 'mouseover', this.resizeHelper, this);
        }
    };
    /**
     * removes resize-related event handlers to the editor panel.
     * Registers touch events for all devices and mouseover for non-mobile devices.
     *
     * @returns {void} - This method does not return a value
     * @private
     */
    TableCommand.prototype.removeResizeEventHandlers = function () {
        EventHandler.remove(this.tableModel.getEditPanel(), Browser.touchStartEvent, this.resizeStart);
        this.cancelResizeAction();
    };
    /*
     * Handles table resize helper logic when hovering or interacting with table elements.
     */
    TableCommand.prototype.resizeHelper = function (e) {
        if (!this.parent || !this.tableModel || this.tableModel.readonly) {
            return;
        }
        if (this.isTableMoveActive) {
            return;
        }
        if (e && e.buttons && e.buttons > 0) {
            return;
        }
        var target = null;
        if (e && e.targetTouches && e.targetTouches.length > 0) {
            target = e.targetTouches[0].target;
        }
        else if (e && e.target) {
            target = e.target;
        }
        if (!target) {
            return;
        }
        var closestTable = closest(target, 'table.e-rte-table, table.e-rte-paste-table, table.e-rte-custom-table');
        var editPanel = this.tableModel.getEditPanel();
        var isResizing = editPanel.querySelectorAll('.e-table-box.e-rbox-select, .e-table-rhelper.e-column-helper, .e-table-rhelper.e-row-helper').length > 0;
        if (!isResizing && !isNOU(this.curTable) && !isNOU(closestTable) &&
            closestTable !== this.curTable && editPanel.contains(closestTable)) {
            this.removeResizeElement();
            this.removeHelper(e);
            this.cancelResizeAction();
        }
        if (!this.isTableNode(target)) {
            var closestCell = closest(target, 'td, th');
            if (closestCell && editPanel.contains(closestCell)) {
                target = closestCell;
            }
        }
        if (!isResizing && this.isTableNode(target)) {
            if (closestTable && editPanel.contains(closestTable) &&
                (target.nodeName === 'TD' || target.nodeName === 'TH')) {
                this.curTable = closestTable;
            }
            else {
                this.curTable = target;
            }
            this.removeResizeElement();
            this.tableResizeEleCreation(this.curTable, e);
            this.handleRowColumnAddIcon(target, this.curTable);
        }
        else if (!this.tableModel.tableSelectionFeature || e.target === this.tableModel.getEditPanel()) {
            this.hideRowColumnAddIcons(target);
        }
        if (this.tableModel.tableSelectionFeature) {
            this.tableSelection(e);
        }
    };
    /*
     * Checks if the given DOM node is a table-related element.
     * Specifically, it returns true if the node is a TABLE, TD, or TH element
     */
    TableCommand.prototype.isTableNode = function (ele) {
        return ele.nodeName === 'TABLE' || ele.nodeName === 'TD' || ele.nodeName === 'TH';
    };
    /*
     * Handles the row/column add icon functionality.
     */
    TableCommand.prototype.handleRowColumnAddIcon = function (target, table) {
        if (!target || target.nodeName === 'TABLE') {
            return;
        }
        var rowSpan = this.getAttributeValue(target, 'rowspan', 1);
        var colSpan = this.getAttributeValue(target, 'colspan', 1);
        var allCells = getCorrespondingColumns(table);
        var selectedCellPosition = getCorrespondingIndex(target, allCells);
        if (selectedCellPosition.length < 2) {
            return;
        }
        var rowIndex = selectedCellPosition[0] + (rowSpan - 1);
        var cellIndex = selectedCellPosition[1];
        if (rowIndex !== 0 && cellIndex !== 0) {
            return;
        }
        this.updateRowInsertIcons(rowIndex, rowSpan);
        this.updateColumnInsertIcons(cellIndex, colSpan);
    };
    /*
     * Gets the numeric value of an attribute with a fallback default value.
     */
    TableCommand.prototype.getAttributeValue = function (element, attributeName, defaultValue) {
        if (!element.hasAttribute(attributeName)) {
            return defaultValue;
        }
        var attrValue = element.getAttribute(attributeName);
        return attrValue ? parseInt(attrValue, 10) : defaultValue;
    };
    /*
     * Creates a dot icon element with specified classes.
     */
    TableCommand.prototype.createIcon = function (className) {
        var insertDotIcon = createElement('span', {
            attrs: {
                'unselectable': 'on',
                'contenteditable': 'false'
            },
            className: 'e-icons ' + className
        });
        return insertDotIcon;
    };
    /*
     * Updates row insert icons for the selected cell.
     */
    TableCommand.prototype.updateRowInsertIcons = function (rowIndex, rowSpan) {
        var editPanel = this.tableModel.getEditPanel();
        for (var index = 0; index <= rowSpan; index++) {
            var selector = ".e-rte-table-resize.e-tb-row-insert[data-row=\"" + (rowIndex - index) + "\"]";
            var rowInsertIcon = editPanel.querySelector(selector);
            this.updateInsertIcon(rowInsertIcon);
        }
    };
    /*
     * Updates column insert icons for the selected cell.
     */
    TableCommand.prototype.updateColumnInsertIcons = function (cellIndex, colSpan) {
        var editPanel = this.tableModel.getEditPanel();
        for (var index = 0; index <= colSpan; index++) {
            var selector = ".e-rte-table-resize.e-tb-col-insert[data-col=\"" + (cellIndex + index) + "\"]";
            var colInsertIcon = editPanel.querySelector(selector);
            this.updateInsertIcon(colInsertIcon);
        }
    };
    /*
     * Updates an insert icon by adding a dot and setting opacity.
     */
    TableCommand.prototype.updateInsertIcon = function (insertIcon) {
        if (!insertIcon) {
            return;
        }
        var className = this.tableModel.enableRtl ? 'e-circle e-insert-cell-rtl' : 'e-circle';
        var insertDotIcon = this.createIcon(className);
        insertIcon.appendChild(insertDotIcon);
        insertIcon.style.opacity = '1';
    };
    /*
     * Hides the table resize icons by setting their opacity to 0.
     */
    TableCommand.prototype.hideRowColumnAddIcons = function (target) {
        if (!target || !target.classList || target.classList.contains('e-circle-add') || target.classList.contains('e-rte-table-resize') &&
            !this.tableModel.getEditPanel().contains(this.helper)) {
            return;
        }
        var editPanel = this.tableModel.getEditPanel();
        if (editPanel.querySelectorAll('.e-rte-table-resize .e-icons.e-circle-add').length > 0) {
            this.removeResizeElement();
        }
        else {
            var allIconElements = editPanel.querySelectorAll('.e-rte-table-resize .e-icons.e-circle');
            allIconElements.forEach(function (element) {
                var parentElement = element.parentElement;
                if (parentElement) {
                    parentElement.style.opacity = '0';
                    detach(element);
                }
            });
        }
    };
    /*
     * Finalizes the table resize operation, removes event handlers, and adjusts table row heights to percentages.
     */
    TableCommand.prototype.resizeEnd = function (e) {
        if (!this.parent) {
            return;
        }
        this.resizeBtnInit();
        this.isResizeBind = true;
        EventHandler.remove(this.tableModel.getDocument(), Browser.touchMoveEvent, this.resizing);
        EventHandler.remove(this.tableModel.getDocument(), Browser.touchEndEvent, this.resizeEnd);
        if (this.tableModel.getEditPanel().querySelector('.e-table-box') &&
            this.tableModel.getEditPanel().contains(this.tableModel.getEditPanel().querySelector('.e-table-box'))) {
            var rzBox = this.tableModel.getEditPanel().querySelector('.e-table-box');
            if (!isNOU(rzBox)) {
                rzBox.classList.remove('e-hide');
            }
            if (!Browser.isDevice) {
                EventHandler.remove(this.tableModel.getEditPanel(), 'mouseover', this.resizeHelper);
                EventHandler.add(this.tableModel.getEditPanel(), 'mouseover', this.resizeHelper, this);
            }
            this.removeResizeElement();
        }
        if (this.helper && this.tableModel.getEditPanel().contains(this.helper)) {
            detach(this.helper);
            this.helper = null;
        }
        this.resetResizeHelper(this.curTable);
        this.pageX = null;
        this.pageY = null;
        var currentTableTrElement = this.curTable.querySelectorAll('tr');
        var tableTrPercentage = [];
        for (var i = 0; i < currentTableTrElement.length; i++) {
            var percentage = (parseFloat(currentTableTrElement[i].clientHeight.toString())
                / parseFloat(this.curTable.clientHeight.toString())) * 100;
            tableTrPercentage[i] = percentage;
        }
        for (var i = 0; i < currentTableTrElement.length; i++) {
            if (currentTableTrElement[i].style.height) {
                if (currentTableTrElement[i].parentElement.nodeName === 'THEAD') {
                    currentTableTrElement[i].parentElement.style.height = tableTrPercentage[i] + '%';
                    currentTableTrElement[i].style.height = tableTrPercentage[i] + '%';
                }
                else {
                    currentTableTrElement[i].style.height = tableTrPercentage[i] + '%';
                }
            }
        }
        var args = { event: e, requestType: 'table' };
        this.tableModel.resizeEnd(args);
        this.resizeEndTime = new Date().getTime();
    };
    /**
     * Cancels the current table resize operation and cleans up event handlers.
     *
     * @public
     * @returns {void} - This method does not return a value.
     */
    TableCommand.prototype.cancelResizeAction = function () {
        this.isResizeBind = true;
        EventHandler.remove(this.tableModel.getEditPanel(), Browser.touchMoveEvent, this.resizing);
        EventHandler.remove(this.tableModel.getEditPanel(), Browser.touchEndEvent, this.resizeEnd);
        this.removeResizeElement();
    };
    /*
     * Removes all table resize elements from the editor panel.
     *
     * @returns {void} - Does not return anything.
     * @private
     */
    TableCommand.prototype.removeResizeElement = function (e) {
        var selector = '.e-column-resize, .e-row-resize, .e-table-box, .e-table-rhelper, .e-tb-col-insert, .e-tb-row-insert';
        var tableSelector = '.e-column-resize, .e-row-resize, .e-table-box, .e-table-rhelper';
        var editPanel = this.tableModel.getEditPanel();
        var items = e ? editPanel.querySelectorAll(tableSelector) : editPanel.querySelectorAll(selector);
        if (items && items.length > 0) {
            for (var i = 0; i < items.length; i++) {
                var item = items[i];
                if (item) {
                    if (item.classList && (item.classList.contains('e-tb-col-insert') || item.classList.contains('e-tb-row-insert'))) {
                        var circleAdd = item.querySelector('.e-circle-add');
                        if (circleAdd) {
                            EventHandler.remove(circleAdd, 'mousedown', this.handleIconMouseDown);
                        }
                        this.removeInsertIconEvents(item);
                    }
                    detach(items[i]);
                }
            }
        }
        var lastChild = this.parent.editableElement ? this.parent.editableElement.lastChild : null;
        if (lastChild && lastChild.nodeName === 'P') {
            removeClass([lastChild], ['e-rte-last-paragraph']);
            if (lastChild.classList.length === 0) {
                lastChild.removeAttribute('class');
            }
        }
    };
    /*
     * Removes the resize helper element when the user ends interaction outside the resize icon.
     */
    TableCommand.prototype.removeHelper = function (e) {
        var target = e ? e.target : null;
        var cls = target ? target.classList : null;
        if (cls && !cls.contains('e-reicon') && this.helper) {
            EventHandler.remove(document, Browser.touchStartEvent, this.removeHelper);
            EventHandler.remove(this.helper, Browser.touchStartEvent, this.resizeStart);
            if (this.tableModel.getEditPanel().contains(this.helper)) {
                detach(this.helper);
            }
            this.pageX = null;
            this.helper = null;
        }
    };
    /*
     * Creates and appends resize elements (column, row, and corner) to the given table for resizing.
     */
    TableCommand.prototype.tableResizeEleCreation = function (table, e) {
        if (!table || !e) {
            return;
        }
        this.tableModel.preventDefaultResize(e);
        var columns = this.calMaxCol(this.curTable);
        var rows = this.getTableRowsWithoutRowspan(table);
        var height = parseFloat(getComputedStyle(table).height) || 0;
        var width = parseFloat(getComputedStyle(table).width) || 0;
        var lastChild = this.parent.editableElement ? this.parent.editableElement.lastChild : null;
        // Applied a specific class to the last paragraph element in the editor to remove its bottom margin.
        if (lastChild && lastChild.nodeName === 'P') {
            addClass([lastChild], ['e-rte-last-paragraph']);
        }
        var pos = this.calcPos(table);
        pos = this.adjustPositionForScrollbar(pos, table);
        var allCells = getCorrespondingColumns(table);
        this.createColumnResizers(columns, height, pos, allCells);
        this.createRowResizers(rows, table, width, pos, allCells);
        if (this.tableModel.tableSettings.resize) {
            this.createResizeBox(columns.length, pos, width, height);
        }
    };
    /*
     * Adjusts position coordinates when scrollbars are present in the editor.
     */
    TableCommand.prototype.adjustPositionForScrollbar = function (pos, table) {
        if (!this.tableModel.enableRtl) {
            return pos;
        }
        if (this.iframeSettings.enable) {
            var doc = this.tableModel.getDocument();
            if (!doc || !doc.defaultView || !doc.body || !doc.body.id) {
                return pos;
            }
            var offsetParent = this.getOffsetParent(table, doc);
            var isNestedTable = offsetParent && offsetParent.nodeName === 'TD' && table.nodeName === 'TABLE';
            if (!isNestedTable) {
                return pos;
            }
            var parentWindow = doc.defaultView.parent && doc.defaultView.parent.window;
            var parentDoc = parentWindow && parentWindow.document;
            var editorId = doc.body.id.replace('-edit', '');
            if (parentDoc && editorId) {
                var editorElement = parentDoc.querySelector('#' + editorId);
                if (editorElement) {
                    var scrollbarWidth = editorElement.clientWidth - doc.body.clientWidth;
                    if (scrollbarWidth > 0) {
                        pos.left -= scrollbarWidth;
                    }
                }
            }
        }
        else {
            var editableElement = this.tableModel.getEditPanel();
            if (editableElement) {
                var scrollbarWidth = editableElement.offsetWidth - editableElement.clientWidth;
                if (scrollbarWidth > 0 && this.tableModel.enableRtl) {
                    pos.left -= scrollbarWidth;
                }
            }
        }
        return pos;
    };
    /*
     * Handles the resizing logic when a pointer or touch event occurs on the table.
     */
    TableCommand.prototype.resizing = function (e) {
        if (!this.parent || !this.tableModel) {
            return;
        }
        var args = { event: e, requestType: 'table' };
        this.tableModel.resizing(args);
    };
    /**
     * Handles the resizing logic when a pointer or touch event occurs on the table.
     *
     * @param {PointerEvent | TouchEvent} e - The pointer or touch event triggering the resize.
     * @returns {void} - This function does not return a value.
     * @public
     */
    TableCommand.prototype.perfomResizing = function (e) {
        var pageX = this.getPointX(e);
        var pageY = this.getPointY(e);
        var mouseX = (this.tableModel.enableRtl) ? -(pageX - this.pageX) : (pageX - this.pageX);
        var mouseY = (this.tableModel.enableRtl) ? -(pageY - this.pageY) : (pageY - this.pageY);
        this.pageX = pageX;
        this.pageY = pageY;
        var maxiumWidth;
        var currentTdElement = this.curTable.closest('td');
        var tableReBox = this.tableModel.getEditPanel().querySelector('.e-table-box');
        var tableWidth = parseInt(getComputedStyle(this.curTable).width, 10);
        var tableHeight = !isNaN(parseInt(this.curTable.style.height, 10)) ?
            parseInt(this.curTable.style.height, 10) : parseInt(getComputedStyle(this.curTable).height, 10);
        var paddingSize = +getComputedStyle(this.tableModel.getEditPanel()).paddingRight.match(/\d/g).join('');
        var rteWidth = this.tableModel.getEditPanel().offsetWidth -
            (this.tableModel.getEditPanel().offsetWidth -
                this.tableModel.getEditPanel().clientWidth) - paddingSize * 2;
        var widthCompare;
        var tableParentElement = this.curTable && this.curTable.parentElement;
        if (!isNOU(this.curTable.parentElement.closest('table')) && !isNOU(this.curTable.closest('td')) &&
            this.tableModel.getEditPanel().contains(this.curTable.closest('td'))) {
            var currentTd = this.curTable.closest('td');
            var currentTDPad = +getComputedStyle(currentTd).paddingRight.match(/\d/g).join('');
            // Padding of the current table with the parent element multiply with 2.
            widthCompare = currentTd.offsetWidth - (currentTd.offsetWidth - currentTd.clientWidth) - currentTDPad * 2;
        }
        else if (tableParentElement && tableParentElement !== this.tableModel.getEditPanel() &&
            tableParentElement.clientWidth !== rteWidth) {
            widthCompare = tableParentElement.clientWidth - +getComputedStyle(tableParentElement).paddingRight.match(/\d/g).join('') * 2;
        }
        else {
            widthCompare = rteWidth;
        }
        if (this.resizeBtnStat.column) {
            if (this.curTable.closest('li')) {
                widthCompare = this.curTable.closest('li').offsetWidth;
            }
            var colGroup = this.curTable.querySelectorAll('colgroup > col');
            var currentTableWidth = void 0;
            if (this.curTable.style.width !== '' && this.curTable.style.width.includes('%')) {
                currentTableWidth = parseFloat(this.curTable.style.width.split('%')[0]);
            }
            else {
                currentTableWidth = this.getCurrentTableWidth(this.curTable.offsetWidth, this.tableModel.getEditPanel().offsetWidth);
            }
            var currentCol = colGroup[this.colIndex];
            var currentColResizableWidth = this.getCurrentColWidth(currentCol, tableWidth);
            if (this.currentColumnResize === 'first') {
                mouseX = mouseX - 0.75; //This was done for to make the gripper and the table first/last column will be close.
                this.removeResizeElement();
                if (currentTdElement) {
                    maxiumWidth = this.curTable.getBoundingClientRect().right - this.calcPos(currentTdElement).left;
                    this.curTable.style.maxWidth = maxiumWidth + 'px';
                }
                // Below the value '100' is the 100% width of the parent element.
                if (((mouseX !== 0 && 5 < currentColResizableWidth) || mouseX < 0) && currentTableWidth <= 100 &&
                    convertPixelToPercentage(tableWidth - mouseX, widthCompare) <= 100) {
                    var firstColumnsCell = colGroup[this.colIndex];
                    this.curTable.style.width = convertPixelToPercentage(tableWidth - mouseX, widthCompare) > 100 ? (100 + '%') :
                        (convertPixelToPercentage(tableWidth - mouseX, widthCompare) + '%');
                    var differenceWidth = currentTableWidth - convertPixelToPercentage(tableWidth - mouseX, widthCompare);
                    var preMarginLeft = 0;
                    var widthType = this.curTable.style.width.indexOf('%') > -1;
                    if (!widthType && this.curTable.offsetWidth >
                        this.tableModel.getEditPanel().offsetWidth) {
                        this.curTable.style.width = rteWidth + 'px';
                        return;
                    }
                    if (widthType && parseFloat(this.curTable.style.width.split('%')[0]) > 100) {
                        this.curTable.style.width = '100%';
                        return;
                    }
                    if (!isNOU(this.curTable.style.marginLeft) && this.curTable.style.marginLeft !== '') {
                        var regex = /[-+]?\d*\.\d+|\d+/;
                        var value = this.curTable.style.marginLeft.match(regex);
                        if (!isNOU(value)) {
                            preMarginLeft = parseFloat(value[0]);
                        }
                    }
                    var currentMarginLeft = preMarginLeft + differenceWidth;
                    if (currentMarginLeft && currentMarginLeft > 100) {
                        var width = parseFloat(this.curTable.style.width);
                        currentMarginLeft = 100 - width;
                    }
                    // For table pasted from word, Margin left can be anything so we are avoiding the below process.
                    if (!this.curTable.classList.contains('e-rte-paste-table') && currentMarginLeft && currentMarginLeft < 1) {
                        this.curTable.style.marginLeft = null;
                        this.curTable.style.width = '100%';
                        return;
                    }
                    this.curTable.style.marginLeft = 'calc(' + (this.curTable.style.width === '100%' ? 0 : currentMarginLeft) + '%)';
                    var currentColumnCellWidth = this.getCurrentColWidth(firstColumnsCell, tableWidth);
                    firstColumnsCell.style.width = (currentColumnCellWidth - differenceWidth) + '%';
                }
            }
            else if (this.currentColumnResize === 'last') {
                mouseX = mouseX + 0.75; //This was done for to make the gripper and the table first/last column will be close.
                this.removeResizeElement();
                if (currentTdElement) {
                    maxiumWidth = currentTdElement.getBoundingClientRect().right - this.curTable.getBoundingClientRect().left;
                    this.curTable.style.maxWidth = maxiumWidth + 'px';
                }
                // Below the value '100' is the 100% width of the parent element.
                if (((mouseX !== 0 && 5 < currentColResizableWidth) || mouseX > 0) &&
                    currentTableWidth <= 100 && convertPixelToPercentage(tableWidth + mouseX, widthCompare) <= 100) {
                    var lastColumnsCell = colGroup[this.colIndex];
                    this.curTable.style.width = convertPixelToPercentage(tableWidth + mouseX, widthCompare) > 100 ? (100 + '%') : (convertPixelToPercentage(tableWidth + mouseX, widthCompare) + '%');
                    var differenceWidth = currentTableWidth - convertPixelToPercentage(tableWidth + mouseX, widthCompare);
                    var currentColumnCellWidth = this.getCurrentColWidth(lastColumnsCell, tableWidth);
                    lastColumnsCell.style.width = (currentColumnCellWidth - differenceWidth) + '%';
                }
            }
            else {
                var actualwid = colGroup[this.colIndex].offsetWidth - mouseX;
                // eslint-disable-next-line
                var totalwid = colGroup[this.colIndex].offsetWidth + colGroup[this.colIndex - 1].offsetWidth;
                if ((totalwid - actualwid) > 20 && actualwid > 20) {
                    var leftColumnWidth = totalwid - actualwid;
                    var rightColWidth = actualwid;
                    colGroup[this.colIndex - 1].style.width = convertPixelToPercentage(leftColumnWidth, tableWidth) + '%';
                    colGroup[this.colIndex].style.width = convertPixelToPercentage(rightColWidth, tableWidth) + '%';
                }
            }
            this.updateHelper();
        }
        else if (this.resizeBtnStat.row) {
            this.tableModel.preventDefaultResize(e);
            var tableTrElementPixel = [];
            var currentTableTrElement = this.curTable.querySelectorAll('tr');
            for (var i = 0; i < currentTableTrElement.length; i++) {
                if (this.rowEle !== currentTableTrElement[i]) {
                    tableTrElementPixel[i] = (parseFloat(currentTableTrElement[i].clientHeight.toString()));
                }
            }
            this.curTable.style.height = (parseFloat(this.curTable.clientHeight.toString()) + ((mouseY > 0) ? 0 : mouseY)) + 'px';
            for (var i = 0; i < currentTableTrElement.length; i++) {
                if (this.rowEle === currentTableTrElement[i]) {
                    currentTableTrElement[i].style.height = (parseFloat(currentTableTrElement[i].clientHeight.toString()) + mouseY) + 'px';
                }
                else {
                    currentTableTrElement[i].style.height = tableTrElementPixel[i] + 'px';
                }
            }
            if (!isNOU(tableReBox)) {
                tableReBox.style.cssText = 'top: ' + (this.calcPos(this.curTable).top + tableHeight - 4) +
                    'px; left:' + (this.calcPos(this.curTable).left + (this.tableModel.enableRtl ? 0 : tableWidth) - 4) + 'px;';
            }
            this.updateHelper();
        }
        else if (this.resizeBtnStat.tableBox) {
            if (currentTdElement) {
                var tableBoxPosition = this.curTable.getBoundingClientRect().left
                    - currentTdElement.getBoundingClientRect().left;
                maxiumWidth = Math.abs(tableBoxPosition - currentTdElement.getBoundingClientRect().width) - 5;
                this.curTable.style.maxWidth = maxiumWidth + 'px';
            }
            this.curTable.style.height = tableHeight + mouseY + 'px';
            if (!isNOU(tableReBox)) {
                tableReBox.classList.add('e-rbox-select');
                tableReBox.style.cssText = 'top: ' + (this.calcPos(this.curTable).top + parseInt(getComputedStyle(this.curTable).height, 10) - 4) +
                    'px; left:' + (this.calcPos(this.curTable).left + (this.tableModel.enableRtl ? 0 : tableWidth) - 4) + 'px;';
            }
            if (this.curTable.closest('li')) {
                widthCompare = this.curTable.closest('li').offsetWidth;
            }
            var widthType = this.curTable.style.width.indexOf('%') > -1;
            if (widthType && parseFloat(this.curTable.style.width.split('%')[0]) > 100) {
                this.curTable.style.width = '100%';
                return;
            }
            if (!widthType && this.curTable.offsetWidth > this.tableModel.getEditPanel().offsetWidth) {
                this.curTable.style.width = rteWidth + 'px';
                return;
            }
            this.curTable.style.width = widthType ? convertPixelToPercentage(tableWidth + mouseX, widthCompare) + '%'
                : tableWidth + mouseX + 'px';
        }
    };
    /*
     * Calculates the current table width as a percentage of the parent width.
     */
    TableCommand.prototype.getCurrentTableWidth = function (tableWidth, parentWidth) {
        // Avoid division by zero
        if (parentWidth === 0) {
            return 0;
        }
        var currentTableWidth = (tableWidth / parentWidth) * 100;
        return currentTableWidth;
    };
    /*
     * Extracts the first cell from each row that doesn't have rowspan.
     */
    TableCommand.prototype.getTableRowsWithoutRowspan = function (table) {
        var rows = [];
        for (var i = 0; i < table.rows.length; i++) {
            for (var j = 0; j < table.rows[i].cells.length; j++) {
                if (!table.rows[i].cells[j].hasAttribute('rowspan')) {
                    rows.push(table.rows[i].cells[j]);
                    break;
                }
            }
        }
        return rows;
    };
    /*
     * Creates column resizer handles.
     */
    TableCommand.prototype.createColumnResizers = function (columns, height, pos, allCells) {
        var nonEmptyColumns = columns.filter(function (col) { return col != null; });
        var isRTL = this.tableModel.enableRtl;
        var leftOffset = pos.left;
        if (isRTL) {
            for (var i = 0; i < nonEmptyColumns.length; i++) {
                leftOffset = leftOffset + nonEmptyColumns[i].getBoundingClientRect().width;
            }
        }
        for (var i = 0; i <= nonEmptyColumns.length; i++) {
            if (i !== 0) {
                var curCol = nonEmptyColumns[i - 1];
                leftOffset = leftOffset + (isRTL ? -curCol.getBoundingClientRect().width : curCol.getBoundingClientRect().width);
            }
            if (this.tableModel.tableSettings.resize) {
                var colReEle = createElement('span', {
                    attrs: { 'data-col': i.toString(), 'unselectable': 'on', 'contenteditable': 'false' }
                });
                colReEle.classList.add(EVENTS.CLS_RTE_TABLE_RESIZE, EVENTS.CLS_TB_COL_RES);
                colReEle.style.cssText = 'height:' + height + 'px;width:4px;top:' + pos.top + 'px;left:' + (leftOffset - 1.5) + 'px;';
                this.tableModel.getEditPanel().appendChild(colReEle);
            }
            if (i !== 0 && (i === nonEmptyColumns.length || allCells[0][i] !== allCells[0][i - 1])) {
                // Create insertion icon
                this.createTableInsertIcon({
                    index: i,
                    top: pos.top,
                    left: leftOffset,
                    cellType: 'column',
                    allCells: allCells,
                    isRTL: isRTL
                });
            }
        }
    };
    /*
     * Creates insertion icons for table columns or rows.
     */
    TableCommand.prototype.createTableInsertIcon = function (config) {
        var _a;
        var isColumn = config.cellType === 'column';
        var dataAttr = isColumn ? 'data-col' : 'data-row';
        var cssClass = isColumn ? EVENTS.CLS_TB_COL_INSERT : EVENTS.CLS_TB_ROW_INSERT;
        var resizeClass = isColumn ? 'e-column-resize' : 'e-row-resize';
        var helperClass = isColumn ? 'e-column-helper' : 'e-row-helper';
        // Create the icon element
        var insertIcon = createElement('span', {
            attrs: (_a = {},
                _a[dataAttr] = config.index.toString(),
                _a['unselectable'] = 'on',
                _a['contenteditable'] = 'false',
                _a)
        });
        insertIcon.classList.add(EVENTS.CLS_RTE_TABLE_RESIZE, cssClass);
        if (config.isRTL) {
            insertIcon.classList.add('e-insert-cell-rtl');
        }
        // Set positioning based on type
        var posLeft = isColumn ? config.left - 12.5 : config.left - 14.3;
        var posTop = isColumn ? config.top - 14.3 : config.top - 11.5;
        var posStyles = "left:" + posLeft + "px;top:" + posTop + "px;";
        insertIcon.style.cssText = insertIcon.style.cssText + posStyles;
        this.attachInsertIconEvents(insertIcon, dataAttr, resizeClass, helperClass, config.allCells, config.isRTL);
    };
    /*
     * Attaches event handlers to the insert icon.
     */
    TableCommand.prototype.attachInsertIconEvents = function (insertIcon, dataAttr, resizeClass, helperClass, allCells, isRTL) {
        EventHandler.add(insertIcon, 'mouseover', this.handleIconMouseOver.bind(this, insertIcon, dataAttr, resizeClass, helperClass, isRTL, allCells), this);
        EventHandler.add(insertIcon, 'mouseout', this.handleIconMouseOut.bind(this, insertIcon), this);
        var editPanel = this.tableModel.getEditPanel();
        if (editPanel) {
            editPanel.appendChild(insertIcon);
        }
    };
    /*
    * Handles the mouseover event for insert icons
    */
    TableCommand.prototype.handleIconMouseOver = function (insertIcon, dataAttr, resizeClass, helperClass, isRTL, allCells) {
        var dotIconElement = insertIcon.querySelector('.e-circle');
        if (dotIconElement && dotIconElement.classList && dotIconElement.classList.contains('e-circle')) {
            detach(dotIconElement);
            insertIcon.style.cursor = this.isResizeBind ? 'pointer' : '';
            if (this.isResizeBind) {
                this.handleInsertIconHover(insertIcon, dataAttr, resizeClass, helperClass, isRTL, allCells);
            }
        }
    };
    /*
     * Handles the mouseout event for insert icons
     */
    TableCommand.prototype.handleIconMouseOut = function (insertIcon, e) {
        if (!this.tableModel.tableSelectionFeature) {
            var relatedTarget = e.relatedTarget;
            if (relatedTarget &&
                !insertIcon.contains(relatedTarget) &&
                insertIcon.style.opacity === '1') {
                this.removeResizeElement();
            }
        }
    };
    /*
     * Handles the mousedown event for insert icons
     */
    TableCommand.prototype.handleIconMouseDown = function (insertIcon, dataAttr, allCells, e) {
        if (!e || !this.parent || !this.parent.undoRedoManager) {
            return;
        }
        this.setCurrentRowAndColIndexValue();
        var isColumn = dataAttr === 'data-col';
        var iconAttrValue = insertIcon.getAttribute(dataAttr) ? parseInt(insertIcon.getAttribute(dataAttr), 10) : -1;
        var indexValue = isColumn ? this.currentColIndex : this.currentRowIndex;
        if (indexValue < 0 || !allCells || iconAttrValue < 0) {
            return;
        }
        e.preventDefault();
        e.stopPropagation();
        this.removeResizeElement();
        this.parent.undoRedoManager.saveData();
        var cellType = isColumn ? 'column' : 'row';
        var subCommand;
        if (cellType === 'column') {
            subCommand = 'InsertColumnLeft';
            if (iconAttrValue > this.currentColIndex) {
                subCommand = 'InsertColumnRight';
            }
        }
        else { // row
            subCommand = 'InsertRowBefore';
            if (iconAttrValue === this.currentRowIndex) {
                subCommand = 'InsertRowAfter';
            }
        }
        this.insertTableElement(cellType, indexValue, allCells, subCommand);
        if (this.tableModel.tableSelectionFeature) {
            this.removeSelectionWrappers(true);
        }
    };
    /*
     * It sets the current target row or column index value
     */
    TableCommand.prototype.setCurrentRowAndColIndexValue = function () {
        var allRowInsertIcons = this.tableModel.getEditPanel().querySelectorAll('.e-tb-row-insert');
        var allColInsertIcons = this.tableModel.getEditPanel().querySelectorAll('.e-tb-col-insert');
        var resizeGripperElementsRow = Array.from(allRowInsertIcons).filter(function (el) { return el.querySelector('.e-circle, .e-circle-add'); });
        var resizeGripperElementsCol = Array.from(allColInsertIcons).filter(function (el) { return el.querySelector('.e-circle, .e-circle-add'); });
        this.currentRowIndex = resizeGripperElementsRow.length === 1 ? Number(resizeGripperElementsRow[0].getAttribute('data-row')) :
            Number(resizeGripperElementsRow[1].getAttribute('data-row'));
        this.currentColIndex = resizeGripperElementsCol.length === 1 ? Number(resizeGripperElementsCol[0].getAttribute('data-col')) - 1 :
            Number(resizeGripperElementsCol[1].getAttribute('data-col')) - 1;
    };
    TableCommand.prototype.resetInsertIconState = function (insertIcon, dataAttr) {
        var circleAddIcon = insertIcon.querySelector('.e-circle-add');
        if (circleAddIcon) {
            EventHandler.remove(circleAddIcon, 'mousedown', this.handleIconMouseDown);
            detach(circleAddIcon);
        }
        // Add regular circle icon
        var isRTL = this.tableModel.enableRtl;
        var className = isRTL ? 'e-circle e-insert-cell-rtl' : 'e-circle';
        var circleIcon = this.createIcon(className);
        insertIcon.appendChild(circleIcon);
        insertIcon.style.opacity = '1';
        // Get the helper element and convert back to resize element
        var attrValue = insertIcon.getAttribute(dataAttr);
        var isColumn = dataAttr === 'data-col';
        var helperClass = isColumn ? 'e-column-helper' : 'e-row-helper';
        var resizeClass = isColumn ? 'e-column-resize' : 'e-row-resize';
        var selector = "span[" + dataAttr + "=\"" + attrValue + "\"].e-table-rhelper." + helperClass;
        var editPanel = this.tableModel.getEditPanel();
        var helperElement = editPanel.querySelector(selector);
        if (!helperElement) {
            return;
        }
        // Convert helper back to resize element
        helperElement.classList.remove('e-table-rhelper', helperClass);
        helperElement.classList.add('e-rte-table-resize', resizeClass);
        // Reset styles
        if (isColumn) {
            this.resetColumnResizeStyles(helperElement);
        }
        else {
            this.resetRowResizeStyles(helperElement, isRTL);
        }
    };
    /*
     * Resets styles for column resize elements
     */
    TableCommand.prototype.resetColumnResizeStyles = function (element) {
        element.style.width = '4px';
        var currentHeight = this.parseNumericStyle(element.style.height);
        element.style.height = (currentHeight + 0.5) + 'px';
        var currentTop = this.parseNumericStyle(element.style.top);
        element.style.top = (currentTop - 0.5) + 'px';
        var currentLeft = this.parseNumericStyle(element.style.left);
        element.style.left = (currentLeft - 1) + 'px';
    };
    /*
     * Resets styles for row resize elements
     */
    TableCommand.prototype.resetRowResizeStyles = function (element, isRTL) {
        element.style.height = '4px';
        if (!isRTL) {
            var currentWidth = this.parseNumericStyle(element.style.width);
            element.style.width = (currentWidth + 0.5) + 'px';
            var currentLeft = this.parseNumericStyle(element.style.left);
            element.style.left = (currentLeft - 0.5) + 'px';
        }
        var currentTop = this.parseNumericStyle(element.style.top);
        element.style.top = (currentTop - 1) + 'px';
    };
    /*
     * Removes all event handlers from an insert icon
     */
    TableCommand.prototype.removeInsertIconEvents = function (insertIcon) {
        if (insertIcon) {
            EventHandler.remove(insertIcon, 'mouseover', this.handleIconMouseOver);
            EventHandler.remove(insertIcon, 'mouseout', this.handleIconMouseOut);
        }
    };
    /*
     * Handles hover state changes for insertion icons
     */
    TableCommand.prototype.handleInsertIconHover = function (insertIcon, dataAttr, resizeClass, helperClass, isRTL, allCells) {
        var className = isRTL ? 'e-circle-add e-insert-cell-rtl' : 'e-circle-add';
        var plusIcon = this.createIcon(className);
        EventHandler.add(plusIcon, 'mousedown', this.handleIconMouseDown.bind(this, insertIcon, dataAttr, allCells), this);
        insertIcon.appendChild(plusIcon);
        // Update icon styles
        insertIcon.style.opacity = '1';
        // Get the resize element
        var attrValue = insertIcon.getAttribute(dataAttr);
        var selectorClasses = "e-rte-table-resize." + resizeClass;
        var selector = "span[" + dataAttr + "=\"" + attrValue + "\"]." + selectorClasses;
        var editPanel = this.tableModel.getEditPanel();
        var resizeElement = editPanel.querySelector(selector);
        if (!resizeElement) {
            return;
        }
        resizeElement.classList.add('e-table-rhelper', helperClass);
        resizeElement.classList.remove('e-rte-table-resize', resizeClass);
        resizeElement.style.backgroundColor = '';
        if (dataAttr === 'data-row') {
            this.applyRowResizeStyles(resizeElement, isRTL);
        }
        else {
            this.applyColumnResizeStyles(resizeElement);
        }
    };
    /*
     * Applies styles for row resize elements.
     */
    TableCommand.prototype.applyRowResizeStyles = function (element, isRTL) {
        element.style.height = '2px';
        if (!isRTL) {
            var currentWidth = this.parseNumericStyle(element.style.width);
            element.style.width = (currentWidth - 0.5) + 'px';
            var currentLeft = this.parseNumericStyle(element.style.left);
            element.style.left = (currentLeft + 0.5) + 'px';
        }
        var currentTop = this.parseNumericStyle(element.style.top);
        element.style.top = (currentTop + 1) + 'px';
    };
    /**
     * Applies styles for column resize elements.
     *
     * @param {HTMLElement} element - The element to style.
     * @returns {void}
     */
    TableCommand.prototype.applyColumnResizeStyles = function (element) {
        element.style.width = '2px';
        var currentHeight = this.parseNumericStyle(element.style.height);
        element.style.height = (currentHeight - 0.5) + 'px';
        var currentTop = this.parseNumericStyle(element.style.top);
        element.style.top = (currentTop + 0.5) + 'px';
        var currentLeft = this.parseNumericStyle(element.style.left);
        element.style.left = (currentLeft + 1) + 'px';
    };
    /*
     * Safely parses a CSS numeric value.
     */
    TableCommand.prototype.parseNumericStyle = function (value) {
        if (!value) {
            return 0;
        }
        var parsed = parseFloat(value);
        return isNaN(parsed) ? 0 : parsed;
    };
    /*
     * Generic method to insert a row or column at a specified index.
     */
    TableCommand.prototype.insertTableElement = function (cellType, index, allCells, subCommand) {
        if (!this.curTable || index < 0) {
            return;
        }
        var docElement = this.tableModel.getDocument();
        var targetCell = cellType === 'column' ? allCells[0][index] : allCells[index][0];
        this.parent.nodeSelection.setCursorPoint(docElement, targetCell, 0);
        if (cellType === 'row') {
            this.clearTableSelections();
            addClass([targetCell], CLS_TABLE_SEL);
        }
        var range = this.parent.nodeSelection.getRange(docElement);
        var selection = this.parent.nodeSelection.save(range, docElement);
        var event = {
            item: {
                selection: selection,
                subCommand: subCommand,
                width: this.tableModel.tableSettings.width
            },
            callBack: null,
            subCommand: '',
            value: '',
            selector: ''
        };
        if (cellType === 'column') {
            this.insertColumn(event);
        }
        else {
            this.insertRow(event);
        }
        this.parent.undoRedoManager.saveData();
        this.tableModel.enableUndo();
    };
    TableCommand.prototype.createSpan = function (classNames) {
        if (classNames === void 0) { classNames = []; }
        var _a;
        var el = createElement('span');
        el.contentEditable = 'false';
        el.setAttribute('unselectable', 'on');
        if (classNames.length) {
            (_a = el.classList).add.apply(_a, classNames);
        }
        return el;
    };
    TableCommand.prototype.tableSelection = function (e) {
        // creating rowIcon and column gripper icon along with the wrapper
        var rowIcon = this.createSpan(['e-icons', 'e-drag-and-drop']);
        var colIcon = this.createSpan(['e-icons', 'e-drag-and-drop']);
        var tableIcon = this.createSpan(['e-icons', 'e-move']);
        var wrapperRowSpan = this.createSpan(['e-row-wrapper']);
        var wrapperColSpan = this.createSpan(['e-col-wrapper']);
        var tableIconWrapper = this.createSpan(['e-table-wrapper']);
        var isRTL = this.tableModel.enableRtl;
        var isTableElement = this.isTableNode(e.target);
        // since method is bounded with mouseover only allows futher onl;y the the cursor is in the table elements
        if (isTableElement) {
            var allRowInsertIcons = this.tableModel.getEditPanel().querySelectorAll('.e-tb-row-insert');
            var allColInsertIcons = this.tableModel.getEditPanel().querySelectorAll('.e-tb-col-insert');
            // icon is removed when the moved of one table element to another
            this.removeSelectionWrappers(true);
            var trElement = e.target.parentElement;
            var children = Array.from(trElement.children);
            var index = children.indexOf(e.target);
            var table = (e.target).closest('table');
            // Filter out elements that have the e-circle class
            var resizeGripperElementsRow = Array.from(allRowInsertIcons).filter(function (el) { return el.querySelector('.e-circle'); });
            // renders the row icon gripppers if ther are only the circle to add the icon
            if (resizeGripperElementsRow.length === 1 || resizeGripperElementsRow.length === 2) {
                rowIcon.style.height = e.target.offsetHeight + 'px';
                var cellRect_1 = trElement.children[0].getBoundingClientRect();
                var panelRect_1 = this.tableModel.getEditPanel().getBoundingClientRect();
                // Account for scroll position
                var scrollElement_1 = this.tableModel.getEditPanel();
                wrapperRowSpan.style.left = cellRect_1.left - panelRect_1.left - 14 + "px";
                wrapperRowSpan.style.top = cellRect_1.top - panelRect_1.top + scrollElement_1.scrollTop + "px";
                wrapperRowSpan.appendChild(rowIcon);
                this.tableModel.getEditPanel().append(wrapperRowSpan);
                if (isRTL) {
                    wrapperRowSpan.style.left = cellRect_1.left - panelRect_1.left + cellRect_1.width + "px";
                    var rowSelectIcon = this.tableModel.getEditPanel().querySelector('.e-drag-and-drop');
                    if (rowSelectIcon) {
                        addClass([rowSelectIcon], 'e-rtl');
                    }
                }
                this.currentRowTarget = trElement;
                rowIcon.addEventListener('click', this.rowIconClickHandler);
            }
            var resizeGripperElementsCol = Array.from(allColInsertIcons).filter(function (el) { return el.querySelector('.e-circle'); });
            // renders the col icon gripppers if ther are only the circle to add the icon
            if (resizeGripperElementsCol.length === 1 || resizeGripperElementsCol.length === 2) {
                this.currentColIndex = index;
                this.currentColTable = table;
                colIcon.addEventListener('click', this.colIconClickHandler);
                colIcon.style.width = e.target.offsetWidth + 'px';
                var trElement_1 = table.querySelector('tr');
                var cellRect_2 = trElement_1.children[index].getBoundingClientRect();
                var panelRect_2 = this.tableModel.getEditPanel().getBoundingClientRect();
                var scrollElement_2 = this.tableModel.getEditPanel();
                wrapperColSpan.style.left = cellRect_2.left - panelRect_2.left + "px";
                wrapperColSpan.style.top = scrollElement_2.scrollTop + cellRect_2.top - panelRect_2.top - 14.1 + "px";
                wrapperColSpan.appendChild(colIcon);
                this.tableModel.getEditPanel().append(wrapperColSpan);
            }
            // code for the entire table slect icon appending
            this.currentEntireTable = table;
            tableIcon.addEventListener('click', this.tableIconClickHandler);
            var cellRect = table.querySelector('tr').getBoundingClientRect();
            var panelRect = this.tableModel.getEditPanel().getBoundingClientRect();
            var scrollElement = this.tableModel.getEditPanel();
            tableIconWrapper.style.left = cellRect.left - panelRect.left - 14 + "px";
            tableIconWrapper.style.top = scrollElement.scrollTop + cellRect.top - panelRect.top - 14 + "px";
            if (isRTL) {
                tableIconWrapper.style.left = cellRect.left - panelRect.left + cellRect.width + "px";
            }
            tableIconWrapper.appendChild(tableIcon);
            this.tableModel.getEditPanel().append(tableIconWrapper);
        }
        // handles for the case of removing the gipper pnly if the cursor moved out of table via bottom
        if (e.target === this.tableModel.getEditPanel()) {
            this.removeSelectionWrappers(true);
        }
        var circleAddIcon = this.tableModel.getEditPanel().querySelector('.e-circle-add');
        if ((e.target.matches('.e-icons.e-move') || e.target.matches('.e-icons.e-drag-and-drop') || e.target.tagName === 'TD' || e.target.tagName === 'TH') && circleAddIcon) {
            // Find the parent insert icon element
            var insertIcon = circleAddIcon.closest('.e-rte-table-resize');
            if (insertIcon) {
                var dataAttr = insertIcon.hasAttribute('data-col') ? 'data-col' : 'data-row';
                // Use the method to reset the icon state
                this.resetInsertIconState(insertIcon, dataAttr);
            }
        }
    };
    TableCommand.prototype.removeSelectionWrappers = function (removetableIcon) {
        var rowselectIcon = this.tableModel.getEditPanel().querySelector('.e-row-wrapper');
        var colselectIcon = this.tableModel.getEditPanel().querySelector('.e-col-wrapper');
        var tableselectIcon = this.tableModel.getEditPanel().querySelector('.e-table-wrapper');
        if (rowselectIcon) {
            rowselectIcon.children[0].removeEventListener('click', this.rowIconClickHandler);
            rowselectIcon.remove();
        }
        if (colselectIcon) {
            colselectIcon.children[0].removeEventListener('click', this.colIconClickHandler);
            colselectIcon.remove();
        }
        if (tableselectIcon && removetableIcon) {
            tableselectIcon.children[0].removeEventListener('click', this.tableIconClickHandler);
            tableselectIcon.remove();
        }
    };
    /**
     * For internal use only - keydown the event handler;
     *
     * @param {HTMLElement} trElement - specifies the event.
     * @returns {void}
     * @hidden
     */
    TableCommand.prototype.selectTableRow = function (trElement) {
        var rowselectIcon = this.tableModel.getEditPanel().querySelector('.e-drag-and-drop');
        var children = Array.from(trElement.children);
        var isActive = false;
        if (rowselectIcon) {
            isActive = rowselectIcon.classList.contains('e-active');
        }
        if (isActive) {
            rowselectIcon.classList.remove('e-active');
            children.forEach(function (element) {
                element.classList.remove('e-cell-select', 'e-multi-cells-select', 'e-cell-select-end');
            });
        }
        else {
            if (rowselectIcon) {
                addClass([rowselectIcon], 'e-active');
            }
            children.forEach(function (element, index) {
                addClass([element], ['e-cell-select', 'e-multi-cells-select']);
                if (index === children.length - 1) {
                    element.classList.add('e-cell-select-end');
                }
            });
        }
    };
    TableCommand.prototype.selectTableColumn = function (expectedIndex, tableElement) {
        var colselectIcon = (this.tableModel.getEditPanel().querySelectorAll('.e-drag-and-drop')[1]);
        var colgroupTag = tableElement.querySelectorAll('tr');
        var isActive = false;
        if (colselectIcon) {
            isActive = colselectIcon.classList.contains('e-active');
        }
        if (isActive) {
            colselectIcon.classList.remove('e-active');
            colgroupTag.forEach(function (tr) {
                var children = Array.from(tr.children);
                children[expectedIndex].classList.remove('e-cell-select', 'e-multi-cells-select', 'e-cell-select-end');
            });
        }
        else {
            if (colselectIcon) {
                addClass([colselectIcon], 'e-active');
            }
            colgroupTag.forEach(function (tr, index) {
                var children = Array.from(tr.children);
                if (children[expectedIndex]) {
                    addClass([children[expectedIndex]], ['e-cell-select', 'e-multi-cells-select']);
                }
                // If this is the LAST ROW → add one more class
                if (index === colgroupTag.length - 1) {
                    children[expectedIndex].classList.add('e-cell-select-end');
                }
            });
        }
    };
    /**
     * For internal use only - keydown the event handler;
     *
     * @param {HTMLElement} tableElement - specifies the event.
     * @returns {void}
     * @hidden
     */
    TableCommand.prototype.selectEntireTable = function (tableElement) {
        this.removeSelectionWrappers(false);
        var entireTableSelection = this.tableModel.getEditPanel().querySelector('.e-move');
        var cellElements = tableElement.querySelectorAll('td, th');
        var isActive = false;
        if (entireTableSelection) {
            isActive = entireTableSelection.classList.contains('e-active');
        }
        if (isActive) {
            // Remove active state
            entireTableSelection.classList.remove('e-active');
            cellElements.forEach(function (td) {
                td.classList.remove('e-cell-select', 'e-multi-cells-select', 'e-cell-select-end');
            });
        }
        else {
            if (entireTableSelection) {
                addClass([entireTableSelection], 'e-active');
            }
            cellElements.forEach(function (td, index) {
                addClass([td], ['e-cell-select', 'e-multi-cells-select']);
                if (index === cellElements.length - 1) {
                    td.classList.add('e-cell-select-end');
                }
            });
        }
    };
    /*
     * Creates row resizer handles.
     */
    TableCommand.prototype.createRowResizers = function (rows, table, width, pos, allCells) {
        for (var i = 0; i < rows.length; i++) {
            var row = rows[i];
            var hasCellSpacing = !isNOU(table.getAttribute('cellspacing')) || table.getAttribute('cellspacing') !== '';
            var rowPosLeft = hasCellSpacing ? 0 : this.calcPos(row).left;
            var isMultiCell = (row.classList && row.classList.contains('e-multi-cells-select')) ? true : false;
            var topPos = this.calcPos(row).top + (isMultiCell ? 0 :
                pos.top) + row.getBoundingClientRect().height - 1.5;
            if (this.tableModel.tableSettings.resize) {
                var rowReEle = createElement('span', {
                    attrs: { 'data-row': i.toString(), 'unselectable': 'on', 'contenteditable': 'false' }
                });
                rowReEle.classList.add(EVENTS.CLS_RTE_TABLE_RESIZE, EVENTS.CLS_TB_ROW_RES);
                rowReEle.style.cssText = 'width:' + width + 'px;height:4px;top:' + topPos + 'px;left:' + (rowPosLeft + pos.left) + 'px; z-index: 2';
                rowReEle.style.cssText = 'width:' + width + 'px;height:4px;top:' + topPos + 'px;left:' + (rowPosLeft + pos.left) + 'px;';
                this.tableModel.getEditPanel().appendChild(rowReEle);
            }
            if ((i + 1) === rows.length || allCells[i][0] !== allCells[i + 1][0]) {
                // Create insertion icon
                this.createTableInsertIcon({
                    index: i,
                    top: topPos,
                    left: this.tableModel.enableRtl ? (width + rowPosLeft + pos.left) : (rowPosLeft + pos.left),
                    cellType: 'row',
                    allCells: allCells,
                    isRTL: this.tableModel.enableRtl
                });
            }
        }
    };
    /*
     * Creates the table resize corner box.
     */
    TableCommand.prototype.createResizeBox = function (colCount, pos, width, height) {
        var tableReBox = createElement('span', {
            className: EVENTS.CLS_TB_BOX_RES + this.tableModel.getCssClass(true),
            attrs: { 'data-col': colCount.toString(), 'unselectable': 'on', 'contenteditable': 'false' }
        });
        width = this.tableModel.enableRtl ? 0 : width;
        tableReBox.style.cssText = 'top:' + (pos.top + height - 4) + 'px;left:' + (pos.left + width - 4) + 'px;';
        if (Browser.isDevice) {
            tableReBox.classList.add('e-rmob');
        }
        this.tableModel.getEditPanel().appendChild(tableReBox);
    };
    /**
     * Removes table selection styling and fake selection elements.
     * This cleanup method removes the selection class from tables and
     * cleans up any fake selection elements that may have been created
     * during the table selection process.
     *
     * @returns {void}
     * @public
     */
    TableCommand.prototype.removeTableSelection = function () {
        var table = this.tableModel.getEditPanel().querySelector('table.e-cell-select');
        if (table) {
            removeClassWithAttr([table], CLS_TABLE_SEL);
        }
        // Remove all fake selection elements used for deletion operations
        this.removeAllFakeSelectionEles();
    };
    /*
     * Removes all fake selection elements from the editor.
     * This cleanup method ensures that all temporary selection elements
     * are removed from the DOM after they are no longer needed.
     */
    TableCommand.prototype.removeAllFakeSelectionEles = function () {
        var fakeSelectionEles = this.tableModel.getEditPanel().querySelectorAll('.e-table-fake-selection');
        if (fakeSelectionEles && fakeSelectionEles.length > 0) {
            fakeSelectionEles.forEach(function (element) {
                detach(element);
            });
        }
    };
    /**
     * Handles arrow key navigation between table cells
     *
     * @param {KeyboardEvent} event - The keyboard event
     * @param {NodeSelection} selection - The current selection
     * @param {HTMLElement} ele - The current table cell element
     * @returns {void}
     * @public
     */
    TableCommand.prototype.tableArrowNavigation = function (event, selection, ele) {
        this.previousTableElement = ele;
        if (this.shouldSkipArrowNavigation(event, selection)) {
            return;
        }
        event.preventDefault();
        this.clearSelectionState(ele);
        var targetElement = this.getTargetCellForArrowNavigation(event, ele);
        if (targetElement) {
            selection.setSelectionText(this.tableModel.getDocument(), targetElement, targetElement, 0, 0);
        }
    };
    /*
     * Determines if arrow key navigation should be skipped
     */
    TableCommand.prototype.shouldSkipArrowNavigation = function (event, selection) {
        var selText = selection.range.startContainer;
        // Skip for down arrow with text node that has BR sibling or non-TD parent
        if (event.keyCode === 40 && selText.nodeType === 3 &&
            ((selText.nextSibling && selText.nextSibling.nodeName === 'BR') ||
                (selText.parentNode && !selText.parentNode.closest('td')))) {
            return true;
        }
        // Skip for up arrow with text node that has BR sibling or non-TD parent
        if (event.keyCode === 38 && selText.nodeType === 3 &&
            ((selText.previousSibling && selText.previousSibling.nodeName === 'BR') ||
                (selText.parentNode && !selText.parentNode.closest('td')))) {
            return true;
        }
        return false;
    };
    /*
     * Clears selection state before navigation
     */
    TableCommand.prototype.clearSelectionState = function (element) {
        removeClassWithAttr([element], CLS_TABLE_SEL);
        this.removeCellSelectClasses();
        this.removeTableSelection();
    };
    /*
     * Gets the target cell for arrow key navigation
     */
    TableCommand.prototype.getTargetCellForArrowNavigation = function (event, element) {
        // Handle down arrow navigation
        if (event.keyCode === 40) {
            return this.getNextRowCell(element);
        }
        // Handle up arrow navigation
        else {
            return this.getPreviousRowCell(element);
        }
    };
    /*
     * Gets the cell below the current cell (next row)
     */
    TableCommand.prototype.getNextRowCell = function (element) {
        var parentRow = closest(element, 'tr');
        var parentTable = closest(element, 'table');
        // Check if we have a next row within the same table
        if (parentRow && parentRow.nextElementSibling) {
            var cellIndex = element.cellIndex;
            return parentRow.nextElementSibling.children[cellIndex];
        }
        // If we're in a header row, move to the first body row
        if (parentTable.tHead && element.nodeName === 'TH') {
            if (parentTable.rows.length > 1) {
                return parentTable.rows[1].cells[element.cellIndex];
            }
        }
        if (parentTable.nextSibling) {
            return parentTable.nextSibling;
        }
        return element;
    };
    /*
     * Gets the cell above the current cell (previous row)
     */
    TableCommand.prototype.getPreviousRowCell = function (element) {
        var parentRow = closest(element, 'tr');
        var parentTable = closest(element, 'table');
        if (parentRow && parentRow.previousElementSibling) {
            var cellIndex = element.cellIndex;
            return parentRow.previousElementSibling.children[cellIndex];
        }
        if (parentTable.tHead && element.nodeName !== 'TH') {
            return parentTable.tHead.rows[0].cells[element.cellIndex];
        }
        if (parentTable.previousSibling) {
            return parentTable.previousSibling;
        }
        return element;
    };
    /**
     * Handles tab key navigation within table cells
     *
     * @param {KeyboardEvent} event - The keyboard event
     * @param {NodeSelection} selection - The current selection
     * @param {HTMLElement} ele - The current table cell element
     * @returns {void}
     * @public
     */
    TableCommand.prototype.tabSelection = function (event, selection, ele) {
        this.cleanTableRows(ele);
        this.previousTableElement = ele;
        var hasInsideList = this.insideList(selection.range);
        if (!hasInsideList) {
            this.clearSelectionState(ele);
        }
        if (this.shouldSkipTabNavigation(event) || hasInsideList) {
            return;
        }
        event.preventDefault();
        // Forward navigation (Tab)
        if (!event.shiftKey && event.keyCode !== 37) {
            this.handleForwardTabNavigation(ele, selection, event);
        }
        // Backward navigation (Shift+Tab)
        else {
            this.handleBackwardTabNavigation(ele, selection, event);
        }
    };
    /*
     * Removes empty text nodes from table rows for cleaner structure
     */
    TableCommand.prototype.cleanTableRows = function (element) {
        var table = element.closest('table');
        if (!table) {
            return;
        }
        var allHeadBodyTRElements = table.querySelectorAll('thead, tbody, tr');
        for (var i = 0; i < allHeadBodyTRElements.length; i++) {
            this.removeEmptyTextNodes(allHeadBodyTRElements[i]);
        }
    };
    /*
     * Removes empty text nodes from a table row element
     */
    TableCommand.prototype.removeEmptyTextNodes = function (element) {
        var children = element.childNodes;
        for (var i = children.length - 1; i >= 0; i--) {
            var node = children[i];
            if (node.nodeType === Node.TEXT_NODE && node.nodeValue.trim() === '') {
                element.removeChild(node);
            }
        }
    };
    /*
     * Determines if tab navigation should be skipped
     */
    TableCommand.prototype.shouldSkipTabNavigation = function (event) {
        return (event.keyCode === 37 || event.keyCode === 39);
    };
    /*
     * Checks if the current selection is inside a list element
     */
    TableCommand.prototype.insideList = function (range) {
        var blockNodes = this.getBlockNodesInSelection(range);
        var listNodes = this.getListNodesFromBlocks(blockNodes);
        if (listNodes.length > 1 || (listNodes.length && (range.startOffset === 0 && range.endOffset === 0))) {
            this.ensureInsideTableList = true;
            return true;
        }
        else {
            this.ensureInsideTableList = false;
            return false;
        }
    };
    /*
     * Filters list-related nodes from block elements
     */
    TableCommand.prototype.getListNodesFromBlocks = function (blockNodes) {
        var nodes = [];
        for (var i = 0; i < blockNodes.length; i++) {
            var currentNode = blockNodes[i];
            var parentNode = currentNode.parentNode;
            if (parentNode.tagName === 'LI') {
                nodes.push(parentNode);
            }
            else if (currentNode.tagName === 'LI' &&
                this.isSimpleListItem(currentNode)) {
                nodes.push(currentNode);
            }
        }
        return nodes;
    };
    /*
     * Checks if a list item is a simple list item (not containing nested lists)
     */
    TableCommand.prototype.isSimpleListItem = function (listItem) {
        if (!listItem.childNodes.length) {
            return false;
        }
        var firstChild = listItem.childNodes[0];
        return firstChild.tagName !== 'P' &&
            firstChild.tagName !== 'OL' &&
            firstChild.tagName !== 'UL';
    };
    /*
     * Gets all block-level elements within the current selection range
     */
    TableCommand.prototype.getBlockNodesInSelection = function (range) {
        var blockTags = [
            'DIV', 'SECTION', 'HEADER', 'FOOTER', 'ARTICLE', 'NAV',
            'P', 'H1', 'H2', 'H3', 'BLOCKQUOTE', 'LI', 'PRE',
            'TD', 'TH', 'FORM', 'FIELDSET', 'LEGEND', 'LABEL', 'TEXTAREA'
        ];
        var blockNodes = new Set();
        if (range.collapsed) {
            this.handleCollapsedRangeBlockNodes(range, blockTags, blockNodes);
        }
        else {
            this.handleExpandedRangeBlockNodes(range, blockTags, blockNodes);
        }
        return Array.from(blockNodes);
    };
    /*
     * Handles finding block nodes when the selection range is collapsed
     */
    TableCommand.prototype.handleCollapsedRangeBlockNodes = function (range, blockTags, blockNodes) {
        var blockNode = this.getImmediateBlockNode(range.startContainer, blockTags);
        if (blockNode) {
            blockNodes.add(blockNode);
        }
    };
    /*
     * Handles finding block nodes when the selection range is expanded
     */
    TableCommand.prototype.handleExpandedRangeBlockNodes = function (range, blockTags, blockNodes) {
        var treeWalker = this.tableModel.getDocument().createTreeWalker(range.commonAncestorContainer, NodeFilter.SHOW_TEXT, {
            acceptNode: function (node) { return (range.intersectsNode(node) ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT); }
        });
        while (treeWalker.nextNode()) {
            var blockNode = this.getImmediateBlockNode(treeWalker.currentNode, blockTags);
            if (blockNode) {
                blockNodes.add(blockNode);
            }
        }
    };
    /*
     * Finds the closest block-level parent element of a node
     */
    TableCommand.prototype.getImmediateBlockNode = function (node, blockTags) {
        var parentNode = node.nodeType === Node.TEXT_NODE ? node.parentNode : node;
        while (parentNode && parentNode.nodeType === Node.ELEMENT_NODE) {
            var element = parentNode;
            if (blockTags.indexOf(element.tagName) > -1) {
                return element;
            }
            parentNode = parentNode.parentNode;
        }
        return null;
    };
    /*
     * Handles forward tab navigation (Tab key)
     */
    TableCommand.prototype.handleForwardTabNavigation = function (element, selection, event) {
        var nextElement = this.findNextElementForward(element);
        if (element === nextElement && element.nodeName === 'TH') {
            nextElement = closest(element, 'table').rows[1].cells[0];
        }
        if (event.keyCode === 39 && element === nextElement) {
            nextElement = closest(element, 'table').nextSibling;
        }
        if (nextElement) {
            this.setSelectionForElement(nextElement, selection);
        }
        if (element === nextElement && event.keyCode !== 39 && nextElement) {
            this.addNewRowAndNavigate(element, nextElement, selection, event);
        }
    };
    /*
     * Finds the next element when navigating forward with Tab
     */
    TableCommand.prototype.findNextElementForward = function (element) {
        if (!isNOU(element.nextSibling)) {
            return element.nextSibling;
        }
        var nextRow = closest(element, 'tr').nextSibling;
        if (!isNOU(nextRow)) {
            return nextRow.childNodes[0];
        }
        var nextSibling = closest(element, 'table').nextSibling;
        if (!isNOU(nextSibling)) {
            return (nextSibling.nodeName.toLowerCase() === 'td') ? nextSibling : element;
        }
        return element;
    };
    /*
     * Adds a new row when tabbing from the last cell and navigates to it
     */
    TableCommand.prototype.addNewRowAndNavigate = function (element, nextElement, selection, event) {
        element.classList.add(CLS_TABLE_SEL);
        this.tableModel.addRow(selection, event, true);
        this.clearSelectionState(element);
        var parentElement = nextElement.parentElement;
        nextElement = parentElement.nextSibling ?
            parentElement.nextSibling.firstChild :
            parentElement.firstChild;
        this.setSelectionForElement(nextElement, selection);
    };
    /**
     * Removes all cell selection-related CSS classes from table cells.
     *
     * @returns {void} - Does not return a value.
     * @public
     */
    TableCommand.prototype.removeCellSelectClasses = function () {
        removeClassWithAttr(this.tableModel.getEditPanel().querySelectorAll('table td, table th'), CLS_TABLE_SEL_END);
        removeClassWithAttr(this.tableModel.getEditPanel().querySelectorAll('table td, table th'), CLS_TABLE_MULTI_CELL);
        removeClassWithAttr(this.tableModel.getEditPanel().querySelectorAll('table td, table th'), CLS_TABLE_SEL);
    };
    /*
     * Handles backward tab navigation (Shift+Tab)
     */
    TableCommand.prototype.handleBackwardTabNavigation = function (element, selection, event) {
        var prevElement = this.findPreviousElementBackward(element);
        if (this.shouldNavigateToTableHeader(element, prevElement)) {
            var clsTable = closest(element, 'table');
            prevElement = clsTable.rows[0].cells[clsTable.rows[0].cells.length - 1];
        }
        if (element === prevElement && event.keyCode === 37) {
            prevElement = closest(element, 'table').previousSibling;
        }
        prevElement = this.handleNestedTableNavigation(prevElement);
        if (prevElement) {
            this.setSelectionForElement(prevElement, selection);
        }
    };
    /*
     * Finds the previous element when navigating backward with Shift+Tab
     */
    TableCommand.prototype.findPreviousElementBackward = function (element) {
        if (!isNOU(element.previousSibling)) {
            return element.previousSibling;
        }
        var prevRow = closest(element, 'tr').previousSibling;
        if (!isNOU(prevRow)) {
            return prevRow.childNodes[prevRow.childNodes.length - 1];
        }
        var prevSibling = closest(element, 'table').previousSibling;
        if (!isNOU(prevSibling)) {
            return (prevSibling.nodeName.toLowerCase() === 'td') ? prevSibling : element;
        }
        return element;
    };
    /*
     * Checks if navigation should move from first body cell to last header cell
     */
    TableCommand.prototype.shouldNavigateToTableHeader = function (element, prevElement) {
        return element === prevElement &&
            element.cellIndex === 0 &&
            closest(element, 'table').tHead &&
            element.nodeName !== 'TH';
    };
    /*
     * Finds the innermost cell when navigating through nested tables
     */
    TableCommand.prototype.handleNestedTableNavigation = function (element) {
        if (!isNOU(element) && element.firstChild &&
            element.firstChild.nodeName === 'TABLE') {
            var tableChild = element;
            while (!isNOU(tableChild.firstChild) &&
                tableChild.firstChild.nodeName === 'TABLE' &&
                tableChild.firstChild.rows.length > 0 &&
                tableChild.firstChild.rows[0].cells.length > 0) {
                tableChild = tableChild.firstChild.rows[0].cells[0];
            }
            return tableChild;
        }
        return element;
    };
    /*
     * Sets selection to the target element during navigation
     */
    TableCommand.prototype.setSelectionForElement = function (element, selection) {
        if ((element.textContent.trim() !== '' && closest(element, 'td'))) {
            selection.setSelectionNode(this.tableModel.getDocument(), element);
        }
        else {
            selection.setSelectionText(this.tableModel.getDocument(), element, element, 0, 0);
        }
    };
    /**
     * Resets all table selection states and visual indicators
     *
     * This method clears all selection-related CSS classes from table cells,
     * resets the active cell reference, and ensures proper selection is applied
     * to the current table when needed.
     *
     * @public
     * @returns {void}
     */
    TableCommand.prototype.resetTableSelection = function () {
        var selectedEndCell = this.tableModel.getEditPanel()
            .querySelectorAll('.e-cell-select-end');
        if (!isNOU(selectedEndCell) && selectedEndCell.length > 0) {
            this.parent.nodeSelection.setSelectionNode(this.tableModel.getDocument(), this.curTable);
        }
        this.removeCellSelectClasses();
        this.removeTableSelection();
    };
    /**
     * Sets up event handler for shift key table selection
     *
     * @param {KeyboardEventArgs} event - The keyboard event arguments
     * @returns {void}
     * @public
     */
    TableCommand.prototype.handleShiftKeyTableSelection = function (event) {
        var isArrowKey = event.keyCode === 39 || event.keyCode === 37 ||
            event.keyCode === 38 || event.keyCode === 40;
        if (event.shiftKey && isArrowKey) {
            this.keyDownEventInstance = event;
            EventHandler.add(this.tableModel.getDocument(), 'selectionchange', this.tableCellsKeyboardSelection, this);
        }
    };
    /**
     * Handles keyboard-based selection of table cells
     *
     * This method processes the selection changes when using arrow keys with shift key
     * for selecting multiple cells in a table.
     *
     * @param {Event} e - The selection change event
     * @returns {void}
     * @public
     */
    TableCommand.prototype.tableCellsKeyboardSelection = function (e) {
        EventHandler.remove(this.tableModel.getDocument(), 'selectionchange', this.tableCellsKeyboardSelection);
        this.setupSelectionState();
        var selectedEndCell = this.tableModel.getEditPanel().querySelectorAll('.e-cell-select-end');
        var isMultiSelect = this.isTableMultiSelectActive();
        if (isMultiSelect || (!isNOU(selectedEndCell) && selectedEndCell.length > 0)) {
            this.handleTableCellArrowNavigation(selectedEndCell);
        }
        else {
            if (!this.curTable || !this.parent || !this.tableModel) {
                return;
            }
            var selectedCells = this.curTable.querySelectorAll('.e-cell-select');
            if (!selectedCells || selectedCells.length < 1) {
                var range = this.parent.nodeSelection.getRange(this.tableModel.getDocument());
                if (!range) {
                    return;
                }
                var elem = null;
                if (range.endContainer.nodeType === Node.ELEMENT_NODE) {
                    elem = range.endContainer;
                }
                else {
                    elem = range.endContainer.parentElement || null;
                }
                if (elem && elem.tagName !== 'TD' && elem.tagName !== 'TH') {
                    elem = closest(elem, 'TD') || closest(elem, 'TH') || null;
                }
                if (elem && this.curTable.contains(elem)) {
                    this.moveToTargetCell(elem);
                }
            }
        }
        if (selectedEndCell.length > 0) {
            this.keyDownEventInstance.preventDefault();
            e.preventDefault();
        }
    };
    /*
     * Handles arrow key navigation between table cells during selection
     */
    TableCommand.prototype.handleTableCellArrowNavigation = function (selectedEndCell) {
        var cells = getCorrespondingColumns(this.curTable);
        var cell = !isNOU(selectedEndCell) &&
            selectedEndCell.length > 0 ?
            selectedEndCell[0] :
            this.activeCell;
        var activeIndexes = getCorrespondingIndex(cell, cells);
        var rowIndex = activeIndexes[0];
        var colIndex = activeIndexes[1];
        var target;
        switch (this.keyDownEventInstance.keyCode) {
            case 39: // Right arrow
                target = this.handleRightArrowNavigation(cells, rowIndex, colIndex, selectedEndCell);
                break;
            case 37: // Left arrow
                target = this.handleLeftArrowNavigation(cells, rowIndex, colIndex, selectedEndCell);
                break;
            case 38: // Up arrow
                target = this.handleUpArrowNavigation(cells, rowIndex, colIndex);
                break;
            case 40: // Down arrow
                target = this.handleDownArrowNavigation(cells, rowIndex, colIndex);
                break;
        }
        if (target) {
            this.moveToTargetCell(target);
        }
        if (this.parent.userAgentData.isSafari()) {
            this.setupSelectionState();
        }
    };
    /*
     * Moves selection to the target cell and updates UI
     */
    TableCommand.prototype.moveToTargetCell = function (target) {
        this.parent.observer.notify('TABLE_MOVE', {
            event: { target: target },
            selectNode: [this.activeCell]
        });
    };
    /*
     * Sets up the selection state by clearing any existing selection and positioning cursor
     */
    TableCommand.prototype.setupSelectionState = function () {
        var selectedEndCell = this.tableModel.getEditPanel().querySelectorAll('.e-cell-select-end');
        if (!isNOU(selectedEndCell) && selectedEndCell.length > 0) {
            this.parent.nodeSelection.Clear(this.tableModel.getDocument());
            this.parent.nodeSelection.setSelectionText(this.tableModel.getDocument(), selectedEndCell[0], selectedEndCell[0], 0, 0);
            this.parent.nodeSelection.setCursorPoint(this.tableModel.getDocument(), selectedEndCell[0], 0);
        }
    };
    /*
     * Checks if table multi-select mode is active based on the current selection
     */
    TableCommand.prototype.isTableMultiSelectActive = function () {
        var range = this.parent.nodeSelection.getRange(this.tableModel.getDocument());
        if (isNOU(range) || isNOU(range.commonAncestorContainer) || isNOU(this.activeCell)) {
            return false;
        }
        var commonAncestor = range.commonAncestorContainer;
        if (commonAncestor.nodeType !== Node.ELEMENT_NODE) {
            return false;
        }
        var ancestorElement = commonAncestor;
        var ancestorTagName = ancestorElement.tagName;
        var isTableRelatedAncestor = ancestorTagName === 'TR' ||
            ancestorTagName === 'TBODY' ||
            ancestorTagName === 'THEAD' ||
            ancestorTagName === 'TABLE';
        if (ancestorTagName === 'TABLE') {
            var selectedCells = ancestorElement
                .querySelectorAll('.e-cell-select, .e-multi-cells-select');
            if (selectedCells.length > 1) {
                return true;
            }
            var activeCell = this.activeCell;
            var startContainer = range.startContainer;
            var startCell = null;
            if (startContainer.nodeType === Node.ELEMENT_NODE) {
                startCell = startContainer;
            }
            else {
                startCell = startContainer.parentElement;
            }
            if (startCell && startCell.tagName !== 'TD' && startCell.tagName !== 'TH') {
                startCell = closest(startCell, 'td,th');
            }
            if (startCell && startCell !== activeCell) {
                return true;
            }
            var selectionEndContainer = range.endContainer;
            var endCell = null;
            if (selectionEndContainer.nodeType === Node.ELEMENT_NODE) {
                endCell = selectionEndContainer;
            }
            else {
                endCell = selectionEndContainer.parentElement;
            }
            if (endCell && endCell.tagName !== 'TD' && endCell.tagName !== 'TH') {
                endCell = closest(endCell, 'td,th');
            }
            return endCell !== null && endCell !== activeCell;
        }
        return isTableRelatedAncestor;
    };
    /*
     * Handles right arrow key navigation logic
     */
    TableCommand.prototype.handleRightArrowNavigation = function (cells, rowIndex, colIndex, selectedEndCell) {
        if (colIndex < cells[0].length - 1) {
            // Move to next cell in same row
            return cells[rowIndex][(colIndex + 1)];
        }
        else if (rowIndex < cells.length - 1) {
            // Move to first cell of next row
            if (selectedEndCell.length === 0 && rowIndex < cells.length - 1) {
                this.activeCell = cells[rowIndex][0];
            }
            return cells[(rowIndex + 1)][colIndex];
        }
        else {
            // At last cell, reset selection
            this.resetTableSelection();
            return null;
        }
    };
    /*
     * Handles left arrow key navigation logic
     */
    TableCommand.prototype.handleLeftArrowNavigation = function (cells, rowIndex, colIndex, selectedEndCell) {
        if (0 < colIndex) {
            // Move to previous cell in same row
            return cells[rowIndex][(colIndex - 1)];
        }
        else if (0 < rowIndex) {
            // Move to last cell of previous row
            if (selectedEndCell.length === 0 && 0 < rowIndex) {
                this.activeCell = cells[rowIndex][(cells[rowIndex].length - 1)];
            }
            return cells[(rowIndex - 1)][colIndex];
        }
        else {
            // At first cell, reset selection
            this.resetTableSelection();
            return null;
        }
    };
    /*
     * Handles up arrow key navigation logic
     */
    TableCommand.prototype.handleUpArrowNavigation = function (cells, rowIndex, colIndex) {
        if (0 < rowIndex) {
            // Move to cell above in previous row
            return cells[(rowIndex - 1)][colIndex];
        }
        else {
            // At first row, reset selection
            this.resetTableSelection();
            return null;
        }
    };
    /*
     * Handles down arrow key navigation logic
     */
    TableCommand.prototype.handleDownArrowNavigation = function (cells, rowIndex, colIndex) {
        if (rowIndex < cells.length - 1) {
            // Move to cell below in next row
            return cells[(rowIndex + 1)][colIndex];
        }
        else {
            // At last row, reset selection
            this.resetTableSelection();
            return null;
        }
    };
    /**
     * Checks if table interaction is possible based on current selection and editor state
     *
     * @param {KeyboardEventArgs} event - The keyboard event arguments
     * @returns {boolean} True if table interaction is possible
     * @public
     */
    TableCommand.prototype.isTableInteractionPossible = function (event) {
        return !isNOU(this.parent.nodeSelection) &&
            this.tableModel.getEditPanel() &&
            event.code !== 'KeyK';
    };
    /**
     * Handles keyboard interactions within table elements
     *
     * @param {KeyboardEventArgs} event - The keyboard event arguments
     * @returns {void}
     * @public
     */
    TableCommand.prototype.handleTableKeyboardInteractions = function (event) {
        var range = this.parent.nodeSelection.getRange(this.tableModel.getDocument());
        var ele = this.parent.nodeSelection.getParentNodeCollection(range)[0];
        ele = (ele && ele.tagName !== 'TD' && ele.tagName !== 'TH') ? ele.parentElement : ele;
        this.handleTableDeleteOperations(event, range, ele);
        ele = this.findClosestTableCell(ele);
        this.handleTableCellNavigation(event, range, ele);
    };
    /*
     * Handles Delete/Backspace/Cut operations on tables
     */
    TableCommand.prototype.handleTableDeleteOperations = function (event, range, ele) {
        var isDeleteKey = event.keyCode === 8 || event.keyCode === 46;
        if (isDeleteKey) {
            if (ele && ele.tagName === 'TBODY') {
                if (!isNOU(this.parent) && this.tableModel.getDocument() &&
                    this.tableModel.getDocument()) {
                    var selection = this.parent.nodeSelection.save(range, this.tableModel.getDocument());
                    event.preventDefault();
                    this.tableModel.removeTable(selection, event, true);
                }
            }
            else if (ele && ele.querySelectorAll('table').length > 0) {
                this.removeResizeElement();
                this.tableModel.hideTableQuickToolbar();
            }
        }
    };
    /*
     * Finds the closest table cell element from the current element
     */
    TableCommand.prototype.findClosestTableCell = function (ele) {
        if (ele && ele.tagName !== 'TD' && ele.tagName !== 'TH') {
            var closestTd = closest(ele, 'td');
            return !isNOU(closestTd) && this.tableModel.getEditPanel().contains(closestTd) ? closestTd : ele;
        }
        return ele;
    };
    /*
     * Handles keyboard navigation within table cells
     */
    TableCommand.prototype.handleTableCellNavigation = function (event, range, ele) {
        if (ele && (ele.tagName === 'TD' || ele.tagName === 'TH')) {
            var selectedEndCell = this.tableModel.getEditPanel().querySelectorAll('.e-cell-select-end');
            // Update active cell if needed
            if ((isNOU(this.activeCell) || this.activeCell !== ele) && !isNOU(selectedEndCell) && selectedEndCell.length === 0
                && (range.collapsed || event.keyCode === 9)) {
                this.activeCell = ele;
            }
            // Save selection for navigation operations
            var selection = void 0;
            if (!isNOU(this.parent.nodeSelection)) {
                selection = this.parent.nodeSelection.save(range, this.tableModel.getDocument());
            }
            // Process navigation keys without shift (or with shift only for Tab)
            if (!(event.shiftKey) || (event.shiftKey && event.keyCode === 9)) {
                switch (event.keyCode) {
                    case 9: // Tab
                    case 37: // Left arrow
                    case 39: // Right arrow
                        this.tabSelection(event, selection, ele);
                        break;
                    case 40: // Down arrow
                    case 38: // Up arrow
                        this.tableArrowNavigation(event, selection, ele);
                        break;
                }
            }
        }
    };
    /**
     * Handles global keyboard shortcuts like Ctrl+A
     *
     * @param {KeyboardEventArgs} event - The keyboard event arguments
     * @returns {void}
     * @public
     */
    TableCommand.prototype.handleGlobalKeyboardShortcuts = function (event) {
        if (event.ctrlKey && event.key === 'a') {
            this.handleSelectAll();
        }
    };
    /*
     * Handles Ctrl+A (Select All) action in the context of tables.
     * This method ensures proper cleanup of table selection indicators
     * when the user performs a select all operation.
     */
    TableCommand.prototype.handleSelectAll = function () {
        this.cancelResizeAction();
        var selectedCells = this.tableModel.getEditPanel().querySelectorAll('.' + CLS_TABLE_SEL);
        var multiSelectedCells = this.tableModel.getEditPanel().querySelectorAll('.' + CLS_TABLE_SEL);
        var selectedCellsEnd = this.tableModel.getEditPanel().querySelectorAll('.' + CLS_TABLE_SEL);
        removeClassWithAttr(selectedCells, CLS_TABLE_SEL);
        removeClassWithAttr(multiSelectedCells, CLS_TABLE_MULTI_CELL);
        removeClassWithAttr(selectedCellsEnd, CLS_TABLE_SEL_END);
        this.removeTableSelection();
    };
    /**
     * Handles table deletion with Delete/Backspace keys
     *
     * @param {KeyboardEventArgs} event - The keyboard event arguments
     * @returns {void}
     * @public
     */
    TableCommand.prototype.handleTableDeletion = function (event) {
        var isDeleteKey = event.code === 'Delete' && event.which === 46;
        var isBackspaceKey = event.code === 'Backspace' && event.which === 8;
        if ((isDeleteKey || isBackspaceKey) && this.tableModel.editorMode === 'HTML') {
            var range = this.parent.nodeSelection.getRange(this.tableModel.getDocument());
            // Handle fake selection deletion
            if (this.isFakeTableSelectionElement(range.startContainer)) {
                this.deleteTable();
                event.preventDefault();
                return;
            }
            // Handle adjacent table deletion
            var table = this.getAdjacentTableElement(range, isDeleteKey);
            if (table) {
                this.updateTableSelection(table);
                event.preventDefault();
            }
        }
    };
    /*
     * Applies selection styling to a table element.
     * This method adds the appropriate CSS class to visually indicate
     * that a table has been selected.
     */
    TableCommand.prototype.updateTableSelection = function (table) {
        addClass([table], 'e-cell-select');
    };
    /*
     * Finds an adjacent table element relative to the current selection
     * This method identifies table elements that are next to the current cursor position
     * when the user presses Delete or Backspace keys at content boundaries.
     */
    TableCommand.prototype.getAdjacentTableElement = function (range, isdelKey) {
        if (!range.collapsed || (!isdelKey && this.tableModel.isTableQuickToolbarVisible())) {
            return null;
        }
        var nodeCollection = this.getNodeCollection(range);
        var startContainer = (range.collapsed && this.tableModel.getEditPanel() === range.startContainer
            && nodeCollection && nodeCollection.length > 0 && nodeCollection[0] ?
            nodeCollection[0] : range.startContainer);
        var adjacentElement = this.getSelectedTableEle(nodeCollection);
        var isBrEle = this.getBrElement(range, nodeCollection);
        if (this.shouldSkipForMediaElement(startContainer, range, isdelKey)) {
            return null;
        }
        if (this.shouldSkipForTextNode(startContainer, range, isdelKey)) {
            return null;
        }
        if (startContainer && startContainer.nodeType === Node.ELEMENT_NODE && startContainer.tagName === 'TABLE') {
            adjacentElement = startContainer;
        }
        if (adjacentElement) {
            var currentEleIndex = this.parent.nodeSelection.getIndex(adjacentElement);
            if (!((range.startOffset === currentEleIndex && isdelKey) ||
                (range.startOffset !== currentEleIndex && !isdelKey))) {
                adjacentElement = null;
            }
        }
        if (!adjacentElement && startContainer) {
            adjacentElement = this.getAdjacentElementFromDom(startContainer, isBrEle, isdelKey);
        }
        if (adjacentElement && adjacentElement.nodeType === Node.ELEMENT_NODE &&
            adjacentElement.tagName === 'TABLE') {
            this.setSelection(adjacentElement, isBrEle);
            return adjacentElement;
        }
        return null;
    };
    /*
     * Checks if the operation should be skipped because of media elements
     */
    TableCommand.prototype.shouldSkipForMediaElement = function (element, range, isdelKey) {
        if (element && element.nodeType === Node.ELEMENT_NODE) {
            var isMediaElement = element.tagName === 'IMG' ||
                !!element.querySelector('img') ||
                element.tagName === 'AUDIO' ||
                !!element.querySelector('audio') ||
                element.tagName === 'VIDEO' ||
                !!element.querySelector('video') ||
                !!element.querySelector('.e-video-clickelem');
            if (isMediaElement) {
                var compareRange = this.tableModel.getDocument().createRange();
                compareRange.collapse(true);
                compareRange.selectNodeContents(element);
                var nodeIndex = this.parent.nodeSelection.getIndex(element);
                return (isdelKey && compareRange.startOffset >= range.startOffset) ||
                    (!isdelKey && (element.tagName !== 'IMG' && compareRange.startOffset !== range.startOffset
                        || element.tagName === 'IMG' && nodeIndex !== range.startOffset));
            }
        }
        return false;
    };
    /*
     * Checks if the operation should be skipped for text nodes
     */
    TableCommand.prototype.shouldSkipForTextNode = function (startContainer, range, isdelKey) {
        if (startContainer && startContainer.nodeType === Node.TEXT_NODE) {
            if (isdelKey) {
                if (range.endOffset !== range.endContainer.textContent.length) {
                    if (range.endOffset !== range.endContainer.textContent.trim().length) {
                        return true;
                    }
                }
            }
            else if (range.startOffset !== 0) {
                return true;
            }
        }
        return false;
    };
    /*
     * Finds adjacent elements by traversing through the DOM hierarchy.
     * This method recursively searches for adjacent elements by traversing up the DOM tree
     * and checking siblings at each level until it finds a suitable element.
     */
    TableCommand.prototype.getAdjacentElementFromDom = function (startContainer, isBrEle, isdelKey) {
        var adjacentElement;
        var parentElement = (isBrEle ? isBrEle : startContainer.parentNode);
        var currentElement = startContainer;
        while (parentElement && !adjacentElement && parentElement.parentNode) {
            var childNodes = Array.from(parentElement.childNodes);
            var startContainerIndex = childNodes.indexOf(currentElement);
            // Check if we can find an adjacent sibling within the parent
            if (startContainerIndex !== -1 && ((isdelKey && startContainerIndex < childNodes.length - 1)
                || (!isdelKey && startContainerIndex > 0))) {
                adjacentElement = (childNodes[isdelKey ?
                    startContainerIndex + 1 :
                    startContainerIndex - 1]);
            }
            else {
                // Otherwise, look at parent's siblings
                adjacentElement = (isdelKey ? parentElement.nextSibling : parentElement.previousSibling);
                currentElement = parentElement;
            }
            // Handle special case for BR elements
            if (this.isBrElement(isBrEle, startContainer, adjacentElement)) {
                isBrEle = currentElement = parentElement = adjacentElement;
                adjacentElement = null;
                continue;
            }
            // Skip empty text nodes
            if (this.isEmptyTextNode(isBrEle, adjacentElement)) {
                currentElement = parentElement = adjacentElement.parentNode;
                adjacentElement = null;
                continue;
            }
            // Handle list elements specially
            if (this.isListElement(adjacentElement)) {
                adjacentElement = this.getAdjacentElementFromList(adjacentElement, isdelKey);
                if (!adjacentElement) {
                    return null;
                }
            }
            // Special handling for list items
            if (this.isLiElement(parentElement, isdelKey)) {
                adjacentElement = parentElement;
            }
            parentElement = parentElement.parentNode;
        }
        return adjacentElement;
    };
    /*
     * Checks if the given element is a BR element that needs special handling
     */
    TableCommand.prototype.isBrElement = function (isBrEle, startContainer, adjacentElement) {
        return !isBrEle &&
            startContainer.nodeType === Node.TEXT_NODE &&
            adjacentElement &&
            adjacentElement.tagName &&
            adjacentElement.tagName.toUpperCase() === 'BR';
    };
    /*
     * Checks if the given element is an empty text node
     */
    TableCommand.prototype.isEmptyTextNode = function (isBrEle, adjacentElement) {
        return !isBrEle &&
            adjacentElement &&
            !(adjacentElement.nodeType === Node.ELEMENT_NODE && adjacentElement.tagName === 'TABLE') &&
            !isNOU(adjacentElement.textContent) &&
            !adjacentElement.textContent.trim();
    };
    /*
     * Checks if the given element is a list element
     */
    TableCommand.prototype.isListElement = function (element) {
        return element &&
            element.tagName &&
            ['UL', 'OL', 'LI'].indexOf(element.tagName.toUpperCase()) !== -1;
    };
    /*
     * Checks if the given element is a list item element in a special case
     */
    TableCommand.prototype.isLiElement = function (element, isdelKey) {
        return element &&
            element.tagName &&
            element.tagName.toUpperCase() === 'LI' &&
            !isdelKey;
    };
    /*
     * Recursively finds the appropriate adjacent element within list structures.
     * This method handles the special case of navigating within nested lists
     * by finding the correct target element.
     */
    TableCommand.prototype.getAdjacentElementFromList = function (adjacentElement, isdelKey) {
        while (adjacentElement) {
            if (adjacentElement.tagName &&
                ['UL', 'OL', 'LI'].indexOf(adjacentElement.tagName.toUpperCase()) === -1) {
                if (!(adjacentElement.nodeType === Node.ELEMENT_NODE && adjacentElement.tagName === 'TABLE')) {
                    adjacentElement = (isdelKey ?
                        adjacentElement.firstChild :
                        adjacentElement.lastChild);
                }
                break;
            }
            adjacentElement = (isdelKey ?
                adjacentElement.firstChild :
                adjacentElement.lastChild);
        }
        return adjacentElement;
    };
    /*
     * Retrieves a collection of DOM nodes from the current selection range.
     * This method extracts relevant nodes based on whether the range is collapsed
     * or expanded, handling the special case of a collapsed range at the edit panel.
     */
    TableCommand.prototype.getNodeCollection = function (range) {
        var nodes = [];
        if (range.collapsed && this.tableModel.getEditPanel() === range.startContainer
            && range.startContainer.childNodes.length > 0) {
            var index = Math.max(0, Math.min(range.startContainer.childNodes.length - 1, range.endOffset - 1));
            nodes.push(range.startContainer.childNodes[index]);
        }
        else {
            nodes = this.parent.nodeSelection.getNodeCollection(range);
        }
        return nodes;
    };
    /*
     * Finds the first table element within a collection of nodes.
     * This method scans the provided node collection and returns the first
     * node that is a TABLE element.
     */
    TableCommand.prototype.getSelectedTableEle = function (nodeCollection) {
        if (nodeCollection && nodeCollection.length > 0) {
            for (var _i = 0, _a = Array.from(nodeCollection); _i < _a.length; _i++) {
                var element = _a[_i];
                if (element && element.tagName === 'TABLE') {
                    return element;
                }
            }
        }
        return null;
    };
    /*
     * Finds a BR element within the range or node collection.
     * This method checks whether the range's end container is a BR element
     * or if the node collection contains exactly one BR element.
     */
    TableCommand.prototype.getBrElement = function (range, nodeCollection) {
        if (range.endContainer.tagName === 'BR') {
            return range.endContainer;
        }
        // Check if the node collection contains exactly one BR element
        if (nodeCollection.length === 1 && nodeCollection[0] &&
            nodeCollection[0].tagName === 'BR') {
            return nodeCollection[0];
        }
        return null;
    };
    /*
     * Sets up selection for a table element about to be deleted.
     * This method prepares the editor for table deletion by creating a fake selection
     * element and removing any BR elements that might interfere with the process.
     */
    TableCommand.prototype.setSelection = function (nextElement, isBrEle) {
        if (!nextElement.classList.contains('e-cell-select')) {
            this.parent.nodeSelection.Clear(this.tableModel.getDocument());
            if (isBrEle) {
                if (isBrEle.parentNode &&
                    isBrEle.parentNode.childNodes.length === 1 &&
                    isBrEle.parentNode.firstChild.nodeName === 'BR') {
                    detach(isBrEle.parentNode);
                }
                else {
                    detach(isBrEle);
                }
            }
            // Create and add a fake selection element
            var fakeSelectionEle = createElement('div', {
                className: 'e-table-fake-selection'
            });
            fakeSelectionEle.setAttribute('contenteditable', 'false');
            this.tableModel.getEditPanel().appendChild(fakeSelectionEle);
            this.parent.nodeSelection.setSelectionNode(this.tableModel.getDocument(), fakeSelectionEle);
        }
    };
    /*
     * Removes a table from the document and replaces it with an appropriate container element.
     * This method deletes the selected table and inserts a proper container element (p, div, or br)
     * based on the editor's configuration. It then positions the cursor at the new container.
     */
    TableCommand.prototype.deleteTable = function () {
        var table = this.tableModel.getEditPanel().querySelector('table.e-cell-select');
        this.removeResizeElement();
        if (table) {
            var brElement = document.createElement('br');
            var containerEle = brElement;
            if (this.tableModel.enterKey === 'DIV') {
                containerEle = document.createElement('div');
                containerEle.appendChild(brElement);
            }
            else if (this.tableModel.enterKey === 'P') {
                containerEle = document.createElement('p');
                containerEle.appendChild(brElement);
            }
            table.parentNode.replaceChild(containerEle, table);
            this.parent.nodeSelection.setSelectionText(this.tableModel.getDocument(), containerEle, containerEle, 0, 0);
            this.removeTableSelection();
        }
    };
    /*
     * Checks if the element is a fake table selection div
     */
    TableCommand.prototype.isFakeTableSelectionElement = function (element) {
        return element.nodeType === Node.ELEMENT_NODE &&
            element.nodeName === 'DIV' &&
            element.classList.contains('e-table-fake-selection');
    };
    /**
     * Handles deselection when typing or using action keys
     *
     * @param {KeyboardEventArgs} event - The keyboard event arguments
     * @returns {void}
     * @public
     */
    TableCommand.prototype.handleDeselectionOnTyping = function (event) {
        var isShiftEnter = event.shiftKey && event.key === 'Enter';
        var isActionKey = TABLE_SELECTION_STATE_ALLOWED_ACTIONKEYS.indexOf(event.key) !== -1;
        var isSingleCharKey = event.key && event.key.length === 1;
        if (isShiftEnter || isActionKey || isSingleCharKey) {
            var table = this.tableModel.getEditPanel().querySelector('table.e-cell-select');
            if (table) {
                if (event.keyCode === 39 || event.keyCode === 37) {
                    this.parent.nodeSelection.setCursorPoint(this.tableModel.getDocument(), table, 0);
                }
                else {
                    var firstTd = table.querySelector('tr').cells[0];
                    this.parent.nodeSelection.setSelectionText(this.tableModel.getDocument(), firstTd, firstTd, 0, 0);
                }
                this.removeTableSelection();
            }
        }
    };
    /**
     * Sets appropriate default content when the editor is empty based on the configured enter key behavior.
     *
     * @returns {void} - This method does not return a value
     * @public
     */
    TableCommand.prototype.setDefaultEmptyContent = function () {
        if (this.tableModel.getEditPanel().innerHTML === null || this.tableModel.getEditPanel().innerHTML === '') {
            var editPanel = this.tableModel.getEditPanel();
            if (this.tableModel.enterKey === 'DIV') {
                editPanel.innerHTML = '<div><br/></div>';
            }
            else if (this.tableModel.enterKey === 'BR') {
                editPanel.innerHTML = '<br/>';
            }
            else {
                editPanel.innerHTML = '<p><br/></p>';
            }
        }
    };
    /**
     * Handles keyboard events after key up in tables.
     * This method identifies the current table cell element based on selection,
     * applies appropriate CSS classes, and manages selection state transitions
     * when navigating between cells.
     *
     * @param {NotifyArgs} e - The notification arguments containing event data
     * @returns {void}
     * @private
     */
    TableCommand.prototype.tableModulekeyUp = function (e) {
        if (!isNOU(this.parent.nodeSelection) && this.tableModel.getEditPanel()) {
            var range = this.parent.nodeSelection.getRange(this.tableModel.getDocument());
            var ele = this.getSelectedElementFromRange(range);
            if ((ele && (ele.tagName === 'TD' || ele.tagName === 'TH')) &&
                !ele.classList.contains(CLS_TABLE_SEL) && (range.collapsed || e.args.keyCode === 9)) {
                ele.classList.add(CLS_TABLE_SEL);
            }
            this.handleTableElementTransition(ele, e.args);
        }
    };
    /*
     * Gets the selected element from the current range.
     * This method extracts the parent element of the selection and ensures
     * it's the actual table cell (TD or TH) by traversing up if needed.
     */
    TableCommand.prototype.getSelectedElementFromRange = function (range) {
        var ele = this.parent.nodeSelection
            .getParentNodeCollection(range)[0];
        ele = (ele && ele.tagName !== 'TD' && ele.tagName !== 'TH') ? ele.parentElement : ele;
        if (ele && ele.tagName !== 'TD' && ele.tagName !== 'TH') {
            var closestTd = closest(ele, 'td');
            ele = !isNOU(closestTd) && this.tableModel.getEditPanel().contains(closestTd) ?
                closestTd : ele;
        }
        return ele;
    };
    /*
     * Handles transitions between table elements during navigation.
     * This method cleans up selection states when moving between different
     * table cells using arrow keys.
     */
    TableCommand.prototype.handleTableElementTransition = function (currentElement, eventArgs) {
        var isNewElement = this.previousTableElement !== currentElement;
        var isPreviousElementValid = !isNOU(this.previousTableElement);
        var isArrowNavigation = !eventArgs.shiftKey &&
            (eventArgs.keyCode === 39 || eventArgs.keyCode === 37 ||
                eventArgs.keyCode === 38 || eventArgs.keyCode === 40);
        // If moving from one cell to another with arrow keys, clean up previous cell
        if (isNewElement && isPreviousElementValid && isArrowNavigation) {
            removeClassWithAttr([this.previousTableElement], CLS_TABLE_SEL);
            this.removeTableSelection();
        }
        if ((eventArgs.which === 8 && eventArgs.code === 'Backspace') || (eventArgs.which === 46 && eventArgs.code === 'Delete')) {
            this.tableModel.hideTableQuickToolbar();
        }
    };
    /**
     * Handles cell selection in a table when a cell is clicked.
     *
     * @param {ITableNotifyArgs} e - The event arguments containing information about the cell selection event.
     * @returns {void} - This method does not return a value.
     * @public
     */
    TableCommand.prototype.cellSelect = function (e) {
        if (!e || !e.args) {
            return;
        }
        var target = this.getTargetCell(e);
        if (this.isShiftKeyTableMove(e, target)) {
            this.handleShiftKeyTableMove(e);
            return;
        }
        this.resetTableSelectionState(e, target);
        if (this.isValidTableCell(target)) {
            this.setActiveCell(target);
        }
    };
    /*
     * Resets the table selection state.
     */
    TableCommand.prototype.resetTableSelectionState = function (e, target) {
        var mouseEvent = e.args;
        var isRightClickOnSelectedCell = this.tableModel.quickToolbarSettings.showOnRightClick &&
            mouseEvent.which === 3 &&
            target.classList.contains(CLS_TABLE_SEL);
        if (!isRightClickOnSelectedCell) {
            if (this && this.isTableMoveActive) {
                this.unwireTableSelectionEvents();
                this.isTableMoveActive = false;
                this.activeCell = null;
            }
            this.heightcheck();
            if (this) {
                this.removeCellSelectClasses();
                this.removeTableSelection();
            }
        }
    };
    /*
     * Unwires (detaches) mouse events related to table selection functionality.
     */
    TableCommand.prototype.unwireTableSelectionEvents = function () {
        if (!this.curTable) {
            return;
        }
        EventHandler.remove(this.curTable, 'mousemove', this.tableMouseMove);
        EventHandler.remove(this.tableModel.getDocument(), 'mouseup', this.tableMouseUp);
        EventHandler.remove(this.tableModel.getDocument(), 'dragend', this.tableMouseUp);
        EventHandler.remove(this.curTable, 'mouseleave', this.tableMouseLeave);
    };
    /*
     * Handles the mousemove event during table selection.
     */
    TableCommand.prototype.tableMouseMove = function (event) {
        this.parent.observer.notify('TABLE_MOVE', { event: event, selectNode: [this.activeCell] });
    };
    /*
     * Handles mouse up event during table selection.
     */
    TableCommand.prototype.tableMouseUp = function () {
        this.unwireTableSelectionEvents();
        this.handleTableSelectionEnd();
        this.isTableMoveActive = false;
    };
    /*
     * Clears active table selection state if the selection was not finalized.
     */
    TableCommand.prototype.handleTableSelectionEnd = function () {
        if (this.activeCell &&
            !this.activeCell.classList.contains(CLS_TABLE_SEL) &&
            this.isTableMoveActive) {
            this.activeCell = null;
        }
    };
    /*
     * Handles mouse leave event when selecting a table.
     */
    TableCommand.prototype.tableMouseLeave = function () {
        if (!Browser.isDevice) {
            this.resetTableSelection();
        }
    };
    /*
     * Gets the target table cell element from the event.
     */
    TableCommand.prototype.getTargetCell = function (e) {
        var mouseEvent = e.args;
        var target = mouseEvent.target;
        var tdNode = closest(target, 'td,th');
        var isTargetNotCell = target.nodeName !== 'TD';
        var isTdNodeValid = tdNode !== null && tdNode !== undefined;
        var isInEditPanel = isTdNodeValid &&
            this.tableModel.getEditPanel().contains(tdNode);
        return (isTargetNotCell && isTdNodeValid && isInEditPanel) ?
            tdNode : target;
    };
    /*
     * Checks if the event is a shift key press for table movement.
     */
    TableCommand.prototype.isShiftKeyTableMove = function (e, target) {
        var mouseEvent = e.args;
        return this && !isNOU(this.activeCell) &&
            mouseEvent.shiftKey &&
            !isNOU(target) &&
            !isNOU(target.tagName) &&
            (target.tagName === 'TD' || target.tagName === 'TH') &&
            this.activeCell !== target;
    };
    /*
     * Handles table movement with shift key pressed.
     */
    TableCommand.prototype.handleShiftKeyTableMove = function (e) {
        this.parent.observer.notify('TABLE_MOVE', {
            event: e.args,
            selectNode: [this.activeCell]
        });
        e.args.preventDefault();
    };
    /*
     * Checks if the target is a valid table cell (TD or TH).
     */
    TableCommand.prototype.isValidTableCell = function (target) {
        return target &&
            target.tagName &&
            (target.tagName === 'TD' || target.tagName === 'TH');
    };
    /*
     * Sets the active cell and initializes table selection.
     */
    TableCommand.prototype.setActiveCell = function (target) {
        addClass([target], CLS_TABLE_SEL);
        this.activeCell = target;
        if (!this.curTable) {
            this.curTable = closest(target, 'table');
        }
        this.wireTableSelectionEvents();
        if (!Browser.isDevice) {
            this.isTableMoveActive = true;
        }
        if (!this.tableModel.tableSelectionFeature) {
            this.removeResizeElement();
        }
        if (this.helper && this.tableModel.getEditPanel().contains(this.helper)) {
            detach(this.helper);
        }
    };
    /*
     * Checks and corrects the height of a table cell if it contains an image with percentage-based height.
     */
    TableCommand.prototype.heightcheck = function () {
        var editPanel = this.tableModel.getEditPanel();
        var tableCell = editPanel.querySelector('td.e-cell-select');
        if (!tableCell) {
            return;
        }
        var image = tableCell.querySelector('img');
        if (!image || !image.style || typeof image.style.height !== 'string') {
            return;
        }
        if (image.style.height.indexOf('%') !== -1) {
            tableCell.style.height = 'inherit';
        }
    };
    /*
     * Wires (attaches) mouse events for table selection functionality.
     */
    TableCommand.prototype.wireTableSelectionEvents = function () {
        if (!this.curTable) {
            return;
        }
        EventHandler.add(this.curTable, 'mousemove', this.tableMouseMove, this);
        EventHandler.add(this.tableModel.getDocument(), 'mouseup', this.tableMouseUp, this);
        EventHandler.add(this.tableModel.getDocument(), 'dragend', this.tableMouseUp, this);
        EventHandler.add(this.curTable, 'mouseleave', this.tableMouseLeave, this);
    };
    /**
     * Handles table cell selection based on mouse position.
     *
     * @param {MouseEvent} [e] - The mouse event triggering the selection.
     * @returns {void} - Does not return a value.
     * @public
     */
    TableCommand.prototype.tableCellSelect = function (e) {
        if (!e) {
            return;
        }
        var target = e.target;
        if (!target) {
            return;
        }
        var parentRow = target.parentElement;
        var tableRow = parentRow ? parentRow.parentElement : null;
        if (!parentRow || !tableRow) {
            return;
        }
        var row = Array.prototype.slice.call(tableRow.children).indexOf(parentRow);
        var col = Array.prototype.slice.call(parentRow.children).indexOf(target);
        var list = this.dlgDiv.querySelectorAll('.e-rte-tablecell');
        Array.prototype.forEach.call(list, function (item) {
            var itemParentRow = item.parentElement;
            var itemTableRow = itemParentRow ? itemParentRow.parentElement : null;
            if (!itemParentRow || !itemTableRow) {
                return;
            }
            var parentIndex = Array.prototype.slice.call(itemTableRow.children).indexOf(itemParentRow);
            var cellIndex = Array.prototype.slice.call(itemParentRow.children).indexOf(item);
            removeClassWithAttr([item], 'e-active');
            if (parentIndex <= row && cellIndex <= col) {
                addClass([item], 'e-active');
            }
        });
        this.tblHeader.innerHTML = (col + 1) + 'x' + (row + 1);
    };
    /**
     * Handles mouse leave event on table cell to reset selection.
     *
     * @returns {void} - Does not return a value.
     * @public
     */
    TableCommand.prototype.tableCellLeave = function () {
        removeClassWithAttr(this.dlgDiv.querySelectorAll('.e-rte-tablecell'), 'e-active');
        var firstCell = this.dlgDiv.querySelector('.e-rte-tablecell');
        if (firstCell) {
            addClass([firstCell], 'e-active');
        }
        this.tblHeader.innerHTML = '1x1';
    };
    /**
     * Updates the table resize handles after a key is pressed.
     *
     * @returns {void} - This method does not return a value
     * @public
     */
    TableCommand.prototype.afterKeyDown = function () {
        var _this = this;
        if (this.curTable) {
            this.resizeIconPositionTime = setTimeout(function () {
                _this.updateResizeIconPosition();
                if (_this.tableModel.tableSelectionFeature) {
                    _this.updateSelectionWrappers();
                    _this.updateLastInsertIconPositions();
                }
            }, 1);
        }
    };
    /*
     * Updates the position of resize icons based on the current table dimensions.
     */
    TableCommand.prototype.updateResizeIconPosition = function () {
        if (this.curTable) {
            var tableReBox = this.tableModel.getEditPanel().querySelector('.e-table-box');
            if (!isNOU(tableReBox)) {
                var tablePosition = this.calcPos(this.curTable);
                tableReBox.style.cssText = 'top: ' + (tablePosition.top + parseInt(getComputedStyle(this.curTable).height, 10) - 4) +
                    'px; left:' + (tablePosition.left + (this.tableModel.enableRtl ? 0 : parseInt(getComputedStyle(this.curTable).width, 10)) - 4) + 'px;';
            }
        }
    };
    TableCommand.prototype.setWrapperPosition = function (wrapper, icon, setWidth, setHeight) {
        var cellRect = wrapper.getBoundingClientRect();
        if (setHeight) {
            icon.firstChild.style.height = cellRect.height + "px";
        }
        if (setWidth) {
            icon.firstChild.style.width = cellRect.width + "px";
        }
    };
    TableCommand.prototype.updateSelectionWrappers = function () {
        if (this.curTable) {
            var rowWrapper = this.tableModel.getEditPanel().querySelector('.e-row-wrapper');
            var colWrapper = this.tableModel.getEditPanel().querySelector('.e-col-wrapper');
            var activeCell = this.tableModel.getEditPanel().querySelector('.e-cell-select');
            if (activeCell && rowWrapper) {
                this.setWrapperPosition(activeCell, rowWrapper, false, true);
            }
            if (activeCell && rowWrapper) {
                this.setWrapperPosition(activeCell, colWrapper, true, false);
            }
        }
    };
    TableCommand.prototype.updateLastInsertIconPositions = function () {
        var allRowInsertIconCircles = this.tableModel.getEditPanel().querySelectorAll('.e-rte-table-resize.e-tb-row-insert .e-icons.e-circle');
        var allColInsertIconCircles = this.tableModel.getEditPanel().querySelectorAll('.e-rte-table-resize.e-tb-col-insert .e-icons.e-circle');
        var activeCell = this.tableModel.getEditPanel().querySelector('.e-cell-select');
        var rowWrapperEle = this.tableModel.getEditPanel().querySelector('.e-row-wrapper');
        var colWrapperEle = this.tableModel.getEditPanel().querySelector('.e-col-wrapper');
        // Update the last row insert icon top position based on row wrapper
        if (allRowInsertIconCircles.length > 0 && activeCell && rowWrapperEle) {
            var lastRowInsertIcon = allRowInsertIconCircles[allRowInsertIconCircles.length - 1];
            var rowWrapperTop = parseFloat(rowWrapperEle.style.top);
            var rowIconHeight = parseFloat(rowWrapperEle.childNodes[0].style.height);
            lastRowInsertIcon.parentElement.style.top = (rowWrapperTop + rowIconHeight - 11) + 'px';
        }
        // Update the last column insert icon left position based on column wrapper
        if (allColInsertIconCircles.length > 0 && activeCell && colWrapperEle) {
            var lastColInsertIcon = allColInsertIconCircles[allColInsertIconCircles.length - 1];
            var colWrapperLeft = parseFloat(colWrapperEle.style.left);
            var colIconWidth = parseFloat(colWrapperEle.childNodes[0].style.width);
            lastColInsertIcon.parentElement.style.left = (colWrapperLeft + colIconWidth - 11) + 'px';
        }
    };
    return TableCommand;
}());
export { TableCommand };
/*
 * Class representing table cell selection boundaries
 * Used to track the start and end positions of selected cells in a table
 */
var MinMax = /** @class */ (function () {
    function MinMax() {
    }
    return MinMax;
}());
